import axios from 'axios';
import JSZip from 'jszip';
import { loamsEngineerApi } from './loamsEngineerApi';



// Create base axios instance for main API
const api = axios.create({
    baseURL: 'https://accountss-3.onrender.com',
    headers: {
        'Content-Type': 'application/json'
    },
    withCredentials: true,
    timeout: 300000 // 5 minutes
});

// Create Python service instance with improved configuration
// Update Python service configuration
const pythonApi = axios.create({
    baseURL: 'https://accountss-loams.onrender.com',
    timeout: 600000,
    withCredentials: true,
    headers: {
        'Accept': '*/*',
        'Content-Type': 'application/json'
    },
    maxContentLength: 50 * 1024 * 1024,
    maxBodyLength: 50 * 1024 * 1024
});

// Add request interceptor for authentication and logging
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = token;
        }
        console.log('[API] Request:', {
            url: config.baseURL + config.url,
            method: config.method,
            hasToken: !!token
        });
        return config;
    },
    (error) => {
        console.error('[API] Request error:', error);
        return Promise.reject(error);
    }
);

// Enhanced request interceptor for LOAMS service
pythonApi.interceptors.request.use(
    config => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = token;
        }

        // Add retry attempt tracking
        config.retryAttempt = config.retryAttempt || 0;

        console.log('[LOAMS] Making request:', {
            url: config.url,
            method: config.method,
            contentType: config.headers['Content-Type'],
            retryAttempt: config.retryAttempt
        });
        return config;
    },
    error => {
        console.error('[LOAMS] Request error:', error);
        return Promise.reject(error);
    }
);




// Enhanced response interceptor for LOAMS service
pythonApi.interceptors.response.use(
    response => {
        console.log('[LOAMS] Response received:', {
            url: response.config.url,
            status: response.status,
            contentType: response.headers['content-type']
        });
        return response;
    },
    async error => {
        console.error('[LOAMS] Response error:', {
            url: error.config?.url,
            status: error.response?.status,
            message: error.message,
            retryAttempt: error.config?.retryAttempt
        });

        // Implement retry logic
        const config = error.config;
        if (!config || !config.retry || config.retryAttempt >= config.retry) {
            return Promise.reject(error);
        }

        config.retryAttempt = config.retryAttempt + 1;
        const delay = config.retryDelay * config.retryAttempt;

        // Handle specific error cases
        if (error.response?.status === 413) {
            return Promise.reject(new Error('File size too large. Please reduce the file size and try again.'));
        }

        if (error.code === 'ECONNABORTED') {
            console.log(`[LOAMS] Retrying request after timeout. Attempt ${config.retryAttempt} of ${config.retry}`);
            await new Promise(resolve => setTimeout(resolve, delay));
            return pythonApi(config);
        }

        // Handle blob error responses
        if (error.response?.data instanceof Blob) {
            try {
                const text = await error.response.data.text();
                const errorData = JSON.parse(text);
                error.details = errorData;
                console.error('[LOAMS] Blob error details:', errorData);
            } catch (e) {
                console.error('[LOAMS] Failed to parse blob error:', e);
                error.details = { error: 'Failed to parse error response' };
            }
        }

        if (error.response?.status === 401) {
            localStorage.clear();
            window.location.href = '/login?session=expired';
        }

        return Promise.reject(error);
    }
);






function parseAdvancedCertificateInfo(s3Key, filename) {
    try {
        console.log('[PARSE] Parsing certificate info from:', { s3Key, filename });
        
        // Try parsing from filename first (more reliable for new format)
        if (filename) {
            const parsed = parseFilenameAdvanced(filename);
            if (parsed && parsed.cluster !== 'Unknown') {
                console.log('[PARSE] Successfully parsed from filename:', parsed);
                return parsed;
            }
        }
        
        // Try parsing from S3 key
        if (s3Key) {
            const parsed = parseS3KeyAdvanced(s3Key);
            if (parsed && parsed.cluster !== 'Unknown') {
                console.log('[PARSE] Successfully parsed from S3 key:', parsed);
                return parsed;
            }
        }
        
        console.log('[PARSE] Could not parse certificate info');
        return null;
    } catch (error) {
        console.error('[PARSE] Error parsing certificate info:', error);
        return null;
    }
}




function parseFilenameAdvanced(filename) {
    try {
        if (!filename || !filename.endsWith('.pdf')) {
            return null;
        }
        
        const baseName = filename.replace('.pdf', '');
        
        // Pattern 1: cluster-agreement-YYYY-MM
        const pattern1 = /^(.+)-(PO_\w+)-(\d{4}-\d{2})$/;
        const match1 = baseName.match(pattern1);
        
        if (match1) {
            return {
                cluster: match1[1].replace(/_/g, ' '),
                agreement: match1[2],
                month: match1[3]
            };
        }
        
        // Pattern 2: more flexible parsing
        const parts = baseName.split('-');
        if (parts.length >= 3) {
            // Look for month pattern (YYYY-MM) at the end
            let monthFound = false;
            let monthIndex = -1;
            
            for (let i = 0; i < parts.length - 1; i++) {
                const combined = `${parts[i]}-${parts[i + 1]}`;
                if (/^\d{4}-\d{2}$/.test(combined)) {
                    monthFound = true;
                    monthIndex = i;
                    break;
                }
            }
            
            if (monthFound && monthIndex > 0) {
                const month = `${parts[monthIndex]}-${parts[monthIndex + 1]}`;
                const beforeMonth = parts.slice(0, monthIndex);
                
                // Find agreement (look for PO pattern)
                let cluster = '';
                let agreement = '';
                
                let foundPO = false;
                for (let i = 0; i < beforeMonth.length; i++) {
                    if (beforeMonth[i].includes('PO')) {
                        cluster = beforeMonth.slice(0, i).join('-').replace(/_/g, ' ');
                        agreement = beforeMonth.slice(i).join('-');
                        foundPO = true;
                        break;
                    }
                }
                
                if (!foundPO && beforeMonth.length >= 2) {
                    // Assume last part is agreement
                    cluster = beforeMonth.slice(0, -1).join('-').replace(/_/g, ' ');
                    agreement = beforeMonth[beforeMonth.length - 1];
                }
                
                if (cluster && agreement) {
                    return { cluster, agreement, month };
                }
            }
        }
        
        return null;
    } catch (error) {
        console.error('[PARSE] Error parsing filename:', error);
        return null;
    }
}







function parseS3KeyAdvanced(s3Key) {
    try {
        // Handle new format: COMPLETITION C/filename.pdf
        if (s3Key.includes('COMPLETITION C/')) {
            const filename = s3Key.replace('COMPLETITION C/', '');
            return parseFilenameAdvanced(filename);
        }
        
        // Handle old format: COMPLETITION C/cluster/agreement/month.pdf
        const parts = s3Key.split('/');
        if (parts.length >= 4 && parts[0] === 'COMPLETITION C') {
            return {
                cluster: parts[1].replace(/_/g, ' '),
                agreement: parts[2],
                month: parts[3].replace('.pdf', '')
            };
        }
        
        return null;
    } catch (error) {
        console.error('[PARSE] Error parsing S3 key:', error);
        return null;
    }
}


// Enhanced LOAMS API endpoints
export const loamsApi = {
    // HTTP methods for direct API calls
    get: (url, config) => pythonApi.get(url, config),
    post: (url, data, config) => pythonApi.post(url, data, config),
    put: (url, data, config) => pythonApi.put(url, data, config),
    delete: (url, config) => pythonApi.delete(url, config),

    previewAndSave: async (formData, saveToDb = false, onProgress = null) => {
        console.log('[LOAMS] Starting preview and save request', { saveToDb });
        
        try {
            // Get file info for logging and timeout calculation
            const file = formData.get('file');
            const fileSizeMB = file ? (file.size / (1024 * 1024)).toFixed(2) : 'unknown';
            
            console.log('[LOAMS] File info:', {
                name: file?.name,
                size: `${fileSizeMB} MB`,
                type: file?.type
            });
    
            // Create a new FormData instance to ensure clean data
            const newFormData = new FormData();
            newFormData.append('file', file);
            newFormData.append('save_to_db', saveToDb.toString());
    
            // Calculate dynamic timeout based on file size
            // Base: 60 seconds + 15 seconds per MB (for large files)
            const baseTimeout = 60000; // 1 minute base
            const timeoutPerMB = 15000; // 15 seconds per MB
            const fileSizeNum = parseFloat(fileSizeMB) || 0;
            const calculatedTimeout = Math.max(
                baseTimeout + (fileSizeNum * timeoutPerMB),
                300000 // Minimum 5 minutes for any file
            );
    
            console.log('[LOAMS] Using timeout:', `${calculatedTimeout / 1000} seconds`);
    
            const response = await pythonApi.post('/preview_and_save', newFormData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                timeout: calculatedTimeout,
                maxContentLength: 50 * 1024 * 1024, // 50MB
                maxBodyLength: 50 * 1024 * 1024,    // 50MB
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        console.log(`[LOAMS] Upload progress: ${percentCompleted}%`);
                        if (onProgress) {
                            onProgress({
                                type: 'upload',
                                percent: percentCompleted,
                                loaded: progressEvent.loaded,
                                total: progressEvent.total
                            });
                        }
                    }
                },
                onDownloadProgress: (progressEvent) => {
                    if (progressEvent.total && onProgress) {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        onProgress({
                            type: 'download',
                            percent: percentCompleted,
                            loaded: progressEvent.loaded,
                            total: progressEvent.total
                        });
                    }
                }
            });
    
            console.log('[LOAMS] Preview and save successful:', {
                totalRows: response.data?.totalRows,
                isLargeFile: response.data?.is_large_file,
                fileSizeMB: response.data?.file_size_mb
            });
            
            return response;
            
        } catch (error) {
            console.error('[LOAMS] Preview and save error:', {
                message: error.message,
                code: error.code,
                status: error.response?.status
            });
            
            // Enhanced error handling with user-friendly messages
            if (error.code === 'ERR_NETWORK') {
                error.userMessage = 'Network error. The file may be too large or the server timed out. Try splitting the file into smaller chunks (max 5MB each).';
            } else if (error.code === 'ECONNABORTED') {
                error.userMessage = 'Request timed out. The file may be too large to process. Try splitting the file into smaller chunks.';
            } else if (error.response?.status === 413) {
                error.userMessage = 'File too large. Maximum file size is 50MB.';
            } else if (error.response?.status === 500) {
                error.userMessage = error.response?.data?.details || 'Server error while processing file. The file may be too large.';
            } else if (error.response?.status === 502 || error.response?.status === 504) {
                error.userMessage = 'Server gateway timeout. The file is too large to process. Please split it into smaller files (under 5MB each).';
            }
            
            throw error;
        }
    },

    // Add these methods to your existing loamsApi object in api.js

// GPT-4 Certificate Validation methods - ADD THIS TO YOUR EXISTING loamsApi OBJECT
gpt4Validation: {
    // Check if GPT-4 service is available and healthy
    checkHealth: async () => {
        console.log('[LOAMS] Checking GPT-4 service health');
        try {
            const response = await pythonApi.get('/api/certificate/gpt4-health', {
                timeout: 10000 // 10 second timeout
            });
            
            console.log('[LOAMS] GPT-4 health check result:', response.data);
            return response.data;
        } catch (error) {
            console.error('[LOAMS] GPT-4 health check failed:', error);
            
            // Return consistent error structure
            return {
                status: 'error',
                message: error.message || 'Health check failed',
                api_key_configured: false,
                api_working: false,
                model: 'gpt-4o'
            };
        }
    },

    // Validate certificate using GPT-4 Vision
    validateCertificate: async (file, expectedPO, expectedMonth) => {
        console.log('[LOAMS] Starting GPT-4 certificate validation:', {
            fileName: file.name,
            fileSize: file.size,
            expectedPO,
            expectedMonth
        });
        
        try {
            // Prepare form data
            const formData = new FormData();
            formData.append('file', file);
            formData.append('expected_po', expectedPO);
            formData.append('expected_month', expectedMonth);
            
            // Make validation request with extended timeout
            const response = await pythonApi.post('/api/certificate/validate-gpt4', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                timeout: 60000, // 60 second timeout for GPT-4 processing
                retry: 2, // Allow 2 retries
                retryDelay: 3000 // 3 second delay between retries
            });
            
            console.log('[LOAMS] GPT-4 validation completed:', response.data);
            return response.data;
            
        } catch (error) {
            console.error('[LOAMS] GPT-4 validation error:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data
            });
            
            // Enhanced error handling
            if (error.code === 'ECONNABORTED') {
                throw new Error('Certificate validation timed out. Please try again or contact support.');
            }
            
            if (error.response?.status === 422) {
                // Validation completed but failed - return the validation result
                return error.response.data;
            }
            
            if (error.response?.status === 500) {
                throw new Error('Server error during validation. The AI service may be temporarily unavailable.');
            }
            
            if (error.response?.status === 400) {
                throw new Error(error.response.data?.error || 'Invalid request parameters');
            }
            
            throw new Error(error.message || 'Certificate validation failed');
        }
    },

    // Process and format validation results for UI
    formatValidationResult: (validationData) => {
        if (!validationData || !validationData.validation) {
            return {
                isValid: false,
                summary: 'Invalid validation data',
                details: 'No validation information available'
            };
        }
        
        const validation = validationData.validation;
        const result = validation.validation_result || {};
        const confidence = validation.confidence || {};
        
        // Create summary message
        let summary = '';
        if (result.overall_valid) {
            summary = `Certificate validated successfully`;
            if (confidence.overall_confidence) {
                summary += ` (${(confidence.overall_confidence * 100).toFixed(1)}% confidence)`;
            }
        } else {
            const issues = [];
            if (!result.po_matches) issues.push('PO number mismatch');
            if (!result.month_matches) issues.push('Month mismatch');
            summary = `Validation failed: ${issues.join(', ')}`;
        }
        
        return {
            isValid: result.overall_valid || false,
            summary,
            confidence: confidence.overall_confidence || 0,
            rawData: validation
        };
    }
},

// ADD this helper function for GPT-4 validation to your existing code
validateCertificateWithGPT4: async (file, selectedPO, selectedMonth) => {
    try {
        console.log('[VALIDATION] Starting GPT-4 certificate validation');
        
        // Check if GPT-4 is available
        const healthCheck = await loamsApi.gpt4Validation.checkHealth();
        
        if (!healthCheck.api_working) {
            console.warn('[VALIDATION] GPT-4 unavailable, using fallback');
            return {
                isValid: true,
                fallback: true,
                errors: {},
                warning: 'AI validation temporarily unavailable. File accepted pending manual review.',
                method: 'fallback'
            };
        }
        
        // Use GPT-4 validation
        const response = await loamsApi.gpt4Validation.validateCertificate(file, selectedPO, selectedMonth);
        
        if (!response.success) {
            throw new Error(response.error || 'Validation failed');
        }
        
        const validation = response.validation;
        const isValid = validation.validation_result?.overall_valid || false;

        // Create detailed error messages if validation failed
        const errors = {};
        
        if (!validation.validation_result?.po_matches) {
            const foundPOs = validation.extracted_data?.po_numbers_found || [];
            const bestMatch = validation.extracted_data?.best_po_match || 'None';
            
            errors.po = `PO number "${selectedPO}" not found in certificate. ` +
                      `Found: ${foundPOs.length > 0 ? foundPOs.join(', ') : 'No PO numbers detected'}. ` +
                      `Best match: ${bestMatch}`;
        }

        if (!validation.validation_result?.month_matches) {
            const foundMonths = validation.extracted_data?.months_found || [];
            const bestMatch = validation.extracted_data?.best_month_match || 'None';
            
            errors.month = `Month "${selectedMonth}" not found in certificate. ` +
                          `Found: ${foundMonths.length > 0 ? foundMonths.join(', ') : 'No months detected'}. ` +
                          `Best match: ${bestMatch}`;
        }

        console.log('[VALIDATION] GPT-4 validation completed:', {
            isValid,
            confidence: validation.confidence?.overall_confidence || 0
        });

        return {
            isValid,
            errors,
            method: 'gpt4_vision',
            confidence: validation.confidence?.overall_confidence || 0,
            extractedText: JSON.stringify(validation.extracted_data, null, 2), // For debugging
            fallback: false
        };
        
    } catch (error) {
        console.error('[VALIDATION] GPT-4 validation failed:', error);
        
        // Fallback for any errors
        return {
            isValid: true,
            fallback: true,
            errors: {},
            warning: 'AI validation system temporarily unavailable. File accepted pending manual review.',
            method: 'emergency_fallback'
        };
    }
},


// 🔥 UPDATED: Replace your enhancedCertificates section in api.js with this optimized version

enhancedCertificates: {
    // 🔥 OPTIMIZED: List certificates with pagination and timeout handling
    listFromBothLocations: async (page = 1, perPage = 200) => {
        console.log('[LOAMS] 🔍 FIXED: Fetching certificates with validation:', { page, perPage });
        try {
            const response = await pythonApi.get('/api/certificates/all-enhanced', {
                params: { page, per_page: perPage },
                headers: {
                    'Accept': 'application/json'
                },
                timeout: 180000 // 3 minute timeout
            });
            
            if (!response.data) {
                throw new Error('No response data received from server');
            }
            
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to fetch certificates');
            }
            
            // 🔥 VALIDATION: Check if certificates have proper data
            const certificates = response.data.certificates || [];
            let validCount = 0;
            let invalidCount = 0;
            
            certificates.forEach(cert => {
                if (cert.clusterName && cert.clusterName !== 'Unknown' &&
                    cert.agreementNo && cert.agreementNo !== 'Unknown' &&
                    cert.month && cert.month !== 'Unknown') {
                    validCount++;
                } else {
                    invalidCount++;
                    console.warn('[LOAMS] ⚠️ Invalid certificate data:', {
                        filename: cert.filename,
                        cluster: cert.clusterName,
                        agreement: cert.agreementNo,
                        month: cert.month,
                        type: cert.certificate_type
                    });
                }
            });
            
            console.log('[LOAMS] ✅ FIXED: Certificates loaded with validation:', {
                page: response.data.page,
                total: response.data.total,
                returned: certificates.length,
                valid: validCount,
                invalid: invalidCount,
                original: response.data.breakdown?.original || 0,
                ccLoams: response.data.breakdown?.cc_loams || 0
            });
            
            return response;
        } catch (error) {
            console.error('[LOAMS] ❌ FIXED: Certificate fetch error:', error);
            
            // Enhanced error reporting
            if (error.response) {
                console.error('[LOAMS] ❌ Server response error:', {
                    status: error.response.status,
                    data: error.response.data
                });
            }
            
            throw error;
        }
    },




    getExcludedInvoices: async () => {
        console.log('[LOAMS] 🔍 Getting excluded invoices...');
        try {
            const response = await pythonApi.get('/api/certificates/invoice-exclusions', {
                timeout: 30000,
                headers: {
                    'Accept': 'application/json'
                }
            });
            
            if (!response.data || !response.data.success) {
                throw new Error('Failed to get excluded invoices');
            }
            
            console.log('[LOAMS] ✅ Excluded invoices loaded:', response.data.total_excluded);
            return response.data;
            
        } catch (error) {
            console.error('[LOAMS] ❌ Get excluded invoices error:', error);
            throw error;
        }
    },

    excludeInvoice: async (serialNumber, reason) => {
        console.log('[LOAMS] 🚫 Excluding invoice:', serialNumber);
        try {
            const response = await pythonApi.post('/api/certificates/exclude-invoice', {
                serial_number: serialNumber,
                reason: reason
            }, {
                timeout: 30000,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            
            if (!response.data || !response.data.success) {
                throw new Error(response.data?.error || 'Failed to exclude invoice');
            }
            
            console.log('[LOAMS] ✅ Invoice excluded successfully:', serialNumber);
            return response.data;
            
        } catch (error) {
            console.error('[LOAMS] ❌ Exclude invoice error:', error);
            throw error;
        }
    },

    includeInvoice: async (serialNumber) => {
        console.log('[LOAMS] ✅ Including invoice:', serialNumber);
        try {
            const response = await pythonApi.post('/api/certificates/include-invoice', {
                serial_number: serialNumber
            }, {
                timeout: 30000,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            
            if (!response.data || !response.data.success) {
                throw new Error(response.data?.error || 'Failed to include invoice');
            }
            
            console.log('[LOAMS] ✅ Invoice included successfully:', serialNumber);
            return response.data;
            
        } catch (error) {
            console.error('[LOAMS] ❌ Include invoice error:', error);
            throw error;
        }
    },

    // Enhanced missing certificates report
    getMissingCertificatesReportEnhanced: async (year = '2025', excludedInvoices = new Set()) => {
        console.log('[LOAMS] 🔍 ENHANCED: Getting enhanced missing certificates report for:', year);
        try {
            const response = await pythonApi.get('/api/certificates/report/missing-certificates-enhanced', {
                params: { year },
                headers: {
                    'Accept': 'application/json'
                },
                timeout: 120000 // 2 minute timeout
            });
            
            if (!response.data) {
                throw new Error('No response data received');
            }
            
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to get enhanced missing certificates report');
            }
            
            console.log('[LOAMS] ✅ ENHANCED: Missing certificates report loaded successfully:', {
                total: response.data.statistics.total_invoices_with_cc,
                missing: response.data.statistics.missing_certificates,
                present: response.data.statistics.present_certificates,
                excluded: response.data.statistics.excluded_invoices,
                year: response.data.statistics.year_filter
            });
            
            return response.data;
            
        } catch (error) {
            console.error('[LOAMS] ❌ ENHANCED: Missing certificates report error:', error);
            throw error;
        }
    },

    // Enhanced all months report with exclusions
    

    // Enhanced single month report with exclusions
    

    // Enhanced export functionality with traditional Excel formatting
    exportReportEnhanced: async (reportData, reportMonth, selectedYear, excludedInvoices = new Set()) => {
        console.log('[LOAMS] 📤 ENHANCED: Exporting enhanced report...');
        try {
            if (!reportData) {
                throw new Error('No report data available to export');
            }

            const workbook = XLSX.utils.book_new();
            
            if (reportMonth === 'all-months') {
                // Enhanced export with traditional formatting and serial numbers
                const excelData = reportData.agreements.map((agreement, index) => {
    const isExcluded = excludedInvoices.has(agreement.serial_number);
    
    // 🔥 FIXED: Calculate duration properly (inclusive months)
    let calculatedDuration = 12; // Default
    if (agreement.contract_start_date && agreement.contract_finish_date) {
        const startDate = new Date(agreement.contract_start_date);
        const endDate = new Date(agreement.contract_finish_date);
        
        // Calculate inclusive months between start and end date
        let months = (endDate.getFullYear() - startDate.getFullYear()) * 12;
        months += endDate.getMonth() - startDate.getMonth();
        months += 1; // Include both start and end months
        
        calculatedDuration = months;
    }
    
    return {
        'Serial No': index + 1,
        'Invoice Serial Number': agreement.serial_number || '',
        'Project Name': agreement.project || '',
        'Agreement Reference Number': agreement.agreement_ref_no || '',
        'Service Type': agreement.service_type || 'General Service',
        'Month': agreement.monthLabel || agreement.month || '',
        'Contract Start Date': agreement.contract_start_date ? loamsApi.enhancedCertificates.formatDateTraditional(agreement.contract_start_date) : '',
        'Contract End Date': agreement.contract_finish_date ? loamsApi.enhancedCertificates.formatDateTraditional(agreement.contract_finish_date) : '',
        'Duration (Months)': calculatedDuration, // 🔥 FIXED: Added back with proper calculation
        'Contract Value AED': loamsApi.enhancedCertificates.formatCurrencyTraditional(agreement.total_amount),
        'Monthly Amount AED': loamsApi.enhancedCertificates.formatCurrencyTraditional(agreement.monthly_amount || agreement.total_amount),
        'Certificate Status': agreement.certificate_uploaded ? 'Uploaded' : 'Missing',
        'Upload Date': agreement.upload_date ? loamsApi.enhancedCertificates.formatDateTraditional(agreement.upload_date) : '',
        'Certificate Filename': agreement.certificate_filename || '',
        'CC Reference': agreement.cc_status || '',
        'Overall Status': isExcluded ? 'Excluded' : (agreement.certificate_uploaded ? 'Complete' : 'Pending'),
        'Excluded from Calculation': isExcluded ? 'Yes' : 'No',
        'Exclusion Reason': agreement.exclusion_reason || '',
        'Remarks': isExcluded ? 'Excluded from completion rate calculation' : ''
    };
});
                
                const worksheet = XLSX.utils.json_to_sheet(excelData);
                
                // Enhanced statistics section with traditional formatting
                const stats = reportData.statistics;
                const statsData = [
                    ['CERTIFICATE COMPLETION REPORT - ALL MONTHS', ''],
                    ['Report Period', selectedYear || 'All Years'],
                    ['Generated On', new Date().toLocaleDateString('en-GB')],
                    ['Report Generated By', 'LOAMS Certificate Management System'],
                    ['Report Type', 'Enhanced Database Integration with S3 Verification'],
                    ['', ''],
                    ['EXECUTIVE SUMMARY', ''],
                    ['Total Service Agreements', stats.total_agreements || 0],
                    ['Certificates Successfully Uploaded', stats.total_with_certificates || 0],
                    ['Missing Certificates', (stats.total_agreements || 0) - (stats.total_with_certificates || 0)],
                    ['Excluded Invoices', stats.excluded_invoices || 0],
                    ['Effective Completion Rate Percentage', `${stats.completion_rate || 0}%`],
                    ['Number of Unique Projects', stats.unique_projects || 0],
                    ['Total Certificate Files in System', stats.total_certificates || 0],
                    ['', ''],
                    ['CALCULATION METHODOLOGY', ''],
                    ['Completion Rate Formula', 'Certificates Uploaded / (Total Agreements - Excluded Invoices) * 100'],
                    ['Excluded Invoices Treatment', 'Removed from denominator for fair calculation'],
                    ['Data Sources', 'PTS Invoice Database + S3 Certificate Storage'],
                    ['', '']
                ];
                
                if (reportData.monthly_breakdown && reportData.monthly_breakdown.length > 0) {
                    statsData.push(['MONTHLY BREAKDOWN ANALYSIS', '']);
                    statsData.push(['Month', 'Total Agreements', 'Certificates Uploaded', 'Completion Percentage', 'Missing Count']);
                    reportData.monthly_breakdown.forEach(month => {
                        const missing = (month.total || 0) - (month.uploaded || 0);
                        statsData.push([
                            month.month || '',
                            month.total || 0,
                            month.uploaded || 0,
                            `${month.percentage || 0}%`,
                            missing
                        ]);
                    });
                }
                
                const statsSheet = XLSX.utils.aoa_to_sheet(statsData);
                
                // Traditional Excel column widths - no symbols, clean formatting
                const wscols = [
                    {wch: 10},  // Serial No
                    {wch: 20},  // Invoice Serial Number
                    {wch: 30},  // Project Name
                    {wch: 25},  // Agreement Reference Number
                    {wch: 25},  // Service Type
                    {wch: 15},  // Month
                    {wch: 18},  // Contract Start Date
                    {wch: 18},  // Contract End Date
                    {wch: 15},  // Duration in Months
                    {wch: 20},  // Contract Value AED
                    {wch: 20},  // Monthly Amount AED
                    {wch: 18},  // Certificate Status
                    {wch: 15},  // Upload Date
                    {wch: 30},  // Certificate Filename
                    {wch: 20},  // CC Reference
                    {wch: 15},  // Overall Status
                    {wch: 20},  // Excluded from Calculation
                    {wch: 30},  // Exclusion Reason
                    {wch: 40}   // Remarks
                ];
                worksheet['!cols'] = wscols;
                
                // Add sheets in traditional order
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Certificate Completion Report');
                XLSX.utils.book_append_sheet(workbook, statsSheet, 'Executive Summary and Analysis');
                
            } else {
                // Single month export with traditional formatting
                const monthDate = new Date(`${reportMonth}-01`);
                const monthName = monthDate.toLocaleString('default', { month: 'long' });
                const year = monthDate.getFullYear();
                
                // FIND the excelData mapping and ADD back Duration with fixed calculation:
const excelData = reportData.agreements.map((agreement, index) => {
    const isExcluded = excludedInvoices.has(agreement.serial_number);
    
    // 🔥 FIXED: Calculate duration properly (inclusive months)
    let calculatedDuration = 12; // Default
    if (agreement.contract_start_date && agreement.contract_finish_date) {
        const startDate = new Date(agreement.contract_start_date);
        const endDate = new Date(agreement.contract_finish_date);
        
        // Calculate inclusive months between start and end date
        let months = (endDate.getFullYear() - startDate.getFullYear()) * 12;
        months += endDate.getMonth() - startDate.getMonth();
        months += 1; // Include both start and end months
        
        calculatedDuration = months;
    }
    
    return {
        'Serial No': index + 1,
        'Invoice Serial Number': agreement.serial_number || '',
        'Project Name': agreement.project || '',
        'Agreement Reference Number': agreement.agreement_ref_no || '',
        'Service Type': agreement.service_type || 'General Service',
        'Month': agreement.monthLabel || agreement.month || '',
        'Contract Start Date': agreement.contract_start_date ? loamsApi.enhancedCertificates.formatDateTraditional(agreement.contract_start_date) : '',
        'Contract End Date': agreement.contract_finish_date ? loamsApi.enhancedCertificates.formatDateTraditional(agreement.contract_finish_date) : '',
        'Duration (Months)': calculatedDuration, // 🔥 FIXED: Added back with proper calculation
        'Contract Value AED': loamsApi.enhancedCertificates.formatCurrencyTraditional(agreement.total_amount),
        'Monthly Amount AED': loamsApi.enhancedCertificates.formatCurrencyTraditional(agreement.monthly_amount || agreement.total_amount),
        'Certificate Status': agreement.certificate_uploaded ? 'Uploaded' : 'Missing',
        'Upload Date': agreement.upload_date ? loamsApi.enhancedCertificates.formatDateTraditional(agreement.upload_date) : '',
        'Certificate Filename': agreement.certificate_filename || '',
        'CC Reference': agreement.cc_status || '',
        'Overall Status': isExcluded ? 'Excluded' : (agreement.certificate_uploaded ? 'Complete' : 'Pending'),
        'Excluded from Calculation': isExcluded ? 'Yes' : 'No',
        'Exclusion Reason': agreement.exclusion_reason || '',
        'Remarks': isExcluded ? 'Excluded from completion rate calculation' : ''
    };
});
                
                const worksheet = XLSX.utils.json_to_sheet(excelData);
                
                const stats = reportData.statistics;
                const statsData = [
                    ['CERTIFICATE COMPLETION REPORT - SINGLE MONTH', ''],
                    ['Report Period', `${monthName} ${year}`],
                    ['Generated On', new Date().toLocaleDateString('en-GB')],
                    ['Report Generated By', 'LOAMS Certificate Management System'],
                    ['', ''],
                    ['EXECUTIVE SUMMARY', ''],
                    ['Total Expected Agreements', stats.total_agreements || 0],
                    ['Certificates Successfully Uploaded', stats.total_with_certificates || 0],
                    ['Missing Certificates', (stats.total_agreements || 0) - (stats.total_with_certificates || 0)],
                    ['Excluded Invoices', stats.excluded_invoices || 0],
                    ['Completion Rate Percentage', `${stats.completion_rate || 0}%`],
                    ['Number of Unique Projects', stats.unique_projects || 0],
                    ['Total Certificate Files Found', stats.total_certificates || 0]
                ];
                
                const statsSheet = XLSX.utils.aoa_to_sheet(statsData);
                
                const wscols = [
                    {wch: 10},  // Serial No
                    {wch: 20},  // Invoice Serial Number
                    {wch: 30},  // Project Name
                    {wch: 25},  // Agreement Reference Number
                    {wch: 25},  // Service Type
                    {wch: 18},  // Report Month
                    {wch: 18},  // Contract Start Date
                    {wch: 18},  // Contract End Date
                    {wch: 15},  // Duration in Months
                    {wch: 20},  // Contract Value AED
                    {wch: 20},  // Monthly Amount AED
                    {wch: 18},  // Certificate Status
                    {wch: 15},  // Upload Date
                    {wch: 30},  // Certificate Filename
                    {wch: 20},  // CC Reference
                    {wch: 20}   // Excluded from Calculation
                ];
                worksheet['!cols'] = wscols;
                
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Certificate Report');
                XLSX.utils.book_append_sheet(workbook, statsSheet, 'Executive Summary');
            }
            
            // Generate and download file with traditional naming
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            const currentDate = new Date().toISOString().split('T')[0].replace(/-/g, '');
            
            if (reportMonth === 'all-months') {
                link.download = `Certificate_Completion_Report_All_Months_${selectedYear || 'All_Years'}_${currentDate}.xlsx`;
            } else {
                const monthDate = new Date(`${reportMonth}-01`);
                const monthName = monthDate.toLocaleString('default', { month: 'long' });
                const year = monthDate.getFullYear();
                link.download = `Certificate_Completion_Report_${monthName}_${year}_${currentDate}.xlsx`;
            }
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            console.log('[LOAMS] ✅ ENHANCED: Excel export completed successfully');
            return { success: true, filename: link.download };
            
        } catch (error) {
            console.error('[LOAMS] ❌ ENHANCED: Export error:', error);
            throw new Error(`Failed to export enhanced report: ${error.message}`);
        }
    },

    // Utility functions for traditional Excel formatting
    formatDateTraditional: (dateString) => {
        try {
            if (!dateString) return '';
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
        } catch (error) {
            return '';
        }
    },

    formatCurrencyTraditional: (amount) => {
        try {
            if (!amount || isNaN(amount)) return 0;
            return parseFloat(amount); // Return as number for Excel (no symbols)
        } catch (error) {
            return 0;
        }
    },

    // Enhanced month label formatting
    formatMonthLabelEnhanced: (monthStr) => {
        try {
            if (!monthStr || monthStr === 'Unknown') {
                return 'Unknown';
            }
            
            let month = String(monthStr).trim();
            if (month.includes('T')) {
                month = month.substring(0, 7);
            } else if (month.includes(' ')) {
                month = month.substring(0, 7);
            }
            
            if (/^\d{4}-\d{2}$/.test(month)) {
                const [year, monthNum] = month.split('-');
                const date = new Date(parseInt(year), parseInt(monthNum) - 1);
                return `${date.toLocaleString('default', { month: 'short' })} ${year}`;
            }
            
            return monthStr;
        } catch (error) {
            console.error('[LOAMS] ❌ Month formatting error:', error);
            return monthStr || 'Unknown';
        }
    },

    // Enhanced validation for certificate data integrity
    validateCertificateDataEnhanced: (certificates) => {
        console.log('[LOAMS] 🔍 ENHANCED: Validating certificate data integrity...');
        
        const validation = {
            totalCertificates: certificates.length,
            validCertificates: 0,
            issuesFound: [],
            typeDistribution: {},
            dataQualityStats: {
                highQuality: 0,
                mediumQuality: 0,
                lowQuality: 0
            },
            missingDataStats: {
                clusterIssues: 0,
                agreementIssues: 0,
                monthIssues: 0,
                invoiceNumberIssues: 0
            }
        };

        certificates.forEach((cert, index) => {
            let isValid = true;
            const issues = [];

            // Validate certificate type
            if (!cert.certificate_type || !['original', 'cc_loams'].includes(cert.certificate_type)) {
                issues.push('Invalid or missing certificate type');
                isValid = false;
            }

            // Track type distribution
            const type = cert.certificate_type || 'unknown';
            validation.typeDistribution[type] = (validation.typeDistribution[type] || 0) + 1;

            // Validate cluster information
            if (!cert.clusterName || cert.clusterName === 'Unknown' || cert.clusterName.trim() === '') {
                issues.push('Missing or unknown cluster name');
                validation.missingDataStats.clusterIssues++;
                isValid = false;
            }

            // Validate agreement information
            if (!cert.agreementNo || cert.agreementNo === 'Unknown' || cert.agreementNo.trim() === '') {
                issues.push('Missing or unknown agreement number');
                validation.missingDataStats.agreementIssues++;
                isValid = false;
            }

            // Validate month information
            if (!cert.month || cert.month === 'Unknown' || !cert.month.match(/^\d{4}-\d{2}$/)) {
                issues.push('Missing or invalid month format');
                validation.missingDataStats.monthIssues++;
                isValid = false;
            }

            // Validate CC-LOAMS specific requirements
            if (cert.certificate_type === 'cc_loams' || cert.isCCLoams) {
                if (!cert.invoice_number || cert.invoice_number === 'Unknown' || cert.invoice_number.trim() === '') {
                    issues.push('CC-LOAMS certificate missing invoice number');
                    validation.missingDataStats.invoiceNumberIssues++;
                    isValid = false;
                }
            }

            // Calculate data quality score
            const qualityScore = cert.dataQualityScore || 0;
            if (qualityScore >= 75) {
                validation.dataQualityStats.highQuality++;
            } else if (qualityScore >= 50) {
                validation.dataQualityStats.mediumQuality++;
            } else {
                validation.dataQualityStats.lowQuality++;
            }

            if (isValid) {
                validation.validCertificates++;
            } else {
                validation.issuesFound.push({
                    index,
                    filename: cert.filename,
                    key: cert.key,
                    issues,
                    dataQualityScore: qualityScore
                });
            }
        });

        const validationRate = certificates.length > 0 
            ? (validation.validCertificates / certificates.length * 100).toFixed(2)
            : 0;

        console.log(`[LOAMS] 📊 ENHANCED: Certificate validation complete: ${validation.validCertificates}/${certificates.length} (${validationRate}%) valid`);
        console.log(`[LOAMS] 📊 ENHANCED: Data quality - High: ${validation.dataQualityStats.highQuality}, Medium: ${validation.dataQualityStats.mediumQuality}, Low: ${validation.dataQualityStats.lowQuality}`);

        return validation;
    },


    getMissingCertificatesReportEnhanced2: async (filters = {}) => {
        console.log('[LOAMS] 🔍 MISSING: Getting enhanced missing certificates report with filters:', filters);
        try {
            const params = new URLSearchParams();
            
            if (filters.year) params.append('year', filters.year);
            if (filters.month && filters.month !== 'all') params.append('month', filters.month);
            if (filters.project && filters.project !== 'all') params.append('project', filters.project);
            
            const response = await pythonApi.get(`/api/certificates/missing-report?${params.toString()}`, {
                headers: {
                    'Accept': 'application/json'
                },
                timeout: 60000 // 1 minute timeout
            });
            
            if (!response.data) {
                throw new Error('No response data received');
            }
            
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to get missing certificates report');
            }
            
            console.log('[LOAMS] ✅ MISSING: Enhanced report loaded successfully:', {
                total: response.data.statistics.total_invoices_with_cc,
                missing: response.data.statistics.missing_certificates,
                present: response.data.statistics.present_certificates,
                filters: response.data.filters
            });
            
            return response.data;
            
        } catch (error) {
            console.error('[LOAMS] ❌ MISSING: Enhanced report error:', error);
            throw error;
        }
    },


    configureCertificateRequirements: async (serialNumbers, reason, action = 'exclude') => {
        console.log('[LOAMS] ⚙️ CONFIG: Configuring certificate requirements:', {
            count: serialNumbers.length,
            action,
            reason
        });
        try {
            const response = await pythonApi.post('/api/certificates/missing-config', {
                serial_numbers: serialNumbers,
                reason: reason,
                action: action
            }, {
                headers: {
                    'Content-Type': 'application/json'
                },
                timeout: 30000
            });
            
            if (!response.data.success) {
                throw new Error(response.data.error || 'Configuration update failed');
            }
            
            console.log('[LOAMS] ✅ CONFIG: Configuration updated successfully');
            return response.data;
            
        } catch (error) {
            console.error('[LOAMS] ❌ CONFIG: Configuration error:', error);
            throw error;
        }
    },

    getCompletionReportEnhanced: async (year = '2025', month = 'all') => {
    console.log('[LOAMS] 📊 ENHANCED: Getting completion report with serial numbers:', { year, month });
    try {
        const params = new URLSearchParams();
        params.append('year', year);
        if (month !== 'all') params.append('month', month);
        
        const response = await pythonApi.get(`/api/certificates/completion-report-enhanced?${params.toString()}`, {
            headers: {
                'Accept': 'application/json'
            },
            timeout: 120000 // 2 minute timeout for comprehensive processing
        });
        
        if (!response.data) {
            throw new Error('No data received from server');
        }
        
        if (!response.data.success) {
            throw new Error(response.data.error || 'Failed to get enhanced completion report');
        }
        
        console.log('[LOAMS] ✅ ENHANCED: Completion report loaded successfully:', {
            totalAgreements: response.data.statistics?.total_agreements || 0,
            totalCertificates: response.data.statistics?.total_with_certificates || 0,
            completionRate: response.data.statistics?.completion_rate || 0,
            method: response.data.method,
            year: response.data.year,
            month: response.data.month,
            contractRecords: response.data.statistics?.contract_records || 0,
            totalInvoices: response.data.statistics?.total_invoices || 0
        });
        
        return response.data;
        
    } catch (error) {
        console.error('[LOAMS] ❌ ENHANCED: Completion report error:', error);
        throw error;
    }
},


    exportMissingCertificatesReport: async (reportData, filters) => {
        console.log('[LOAMS] 📤 EXPORT: Exporting missing certificates report to Excel');
        try {
            if (!reportData || !reportData.missing_certificates) {
                throw new Error('No report data available to export');
            }
            
            const workbook = XLSX.utils.book_new();
            
            // 🔥 SHEET 1: Missing Certificates
            const missingData = reportData.missing_certificates.map((cert, index) => ({
                'Row': index + 1,
                'Serial Number': cert.serial_number || '',
                'Project': cert.project || '',
                'Agreement Reference': cert.agreement_ref_no || '',
                'Service Type': cert.service_type || '',
                'Month': cert.month_label || '',
                'Invoice Date': cert.date ? new Date(cert.date).toLocaleDateString() : '',
                'Amount (AED)': cert.total_amount || 0,
                'CC Reference': cert.cc_reference || '',
                'Expected Filename': cert.expected_filename || '',
                'Status': 'Missing',
                'Required': cert.certificate_required ? 'Yes' : 'No',
                'Exclusion Reason': cert.exclusion_reason || '',
                'Created Date': cert.created_at ? new Date(cert.created_at).toLocaleDateString() : ''
            }));
            
            const missingSheet = XLSX.utils.json_to_sheet(missingData);
            
            // Set column widths
            const missingCols = [
                { wch: 8 },   // Row
                { wch: 20 },  // Serial Number
                { wch: 30 },  // Project
                { wch: 25 },  // Agreement Reference
                { wch: 20 },  // Service Type
                { wch: 15 },  // Month
                { wch: 15 },  // Invoice Date
                { wch: 15 },  // Amount
                { wch: 15 },  // CC Reference
                { wch: 30 },  // Expected Filename
                { wch: 10 },  // Status
                { wch: 10 },  // Required
                { wch: 20 },  // Exclusion Reason
                { wch: 15 }   // Created Date
            ];
            missingSheet['!cols'] = missingCols;
            
            XLSX.utils.book_append_sheet(workbook, missingSheet, 'Missing Certificates');
            
            // 🔥 SHEET 2: Present Certificates (Sample)
            if (reportData.present_certificates && reportData.present_certificates.length > 0) {
                const presentData = reportData.present_certificates.map((cert, index) => ({
                    'Row': index + 1,
                    'Serial Number': cert.serial_number || '',
                    'Project': cert.project || '',
                    'Agreement Reference': cert.agreement_ref_no || '',
                    'Service Type': cert.service_type || '',
                    'Month': cert.month_label || '',
                    'Invoice Date': cert.date ? new Date(cert.date).toLocaleDateString() : '',
                    'Amount (AED)': cert.total_amount || 0,
                    'Actual Filename': cert.actual_filename || '',
                    'Upload Date': cert.upload_date ? new Date(cert.upload_date).toLocaleDateString() : '',
                    'File Size (KB)': cert.file_size ? Math.round(cert.file_size / 1024) : 0,
                    'Status': 'Present',
                    'S3 Location': cert.s3_key || ''
                }));
                
                const presentSheet = XLSX.utils.json_to_sheet(presentData);
                presentSheet['!cols'] = [
                    { wch: 8 },   // Row
                    { wch: 20 },  // Serial Number
                    { wch: 30 },  // Project
                    { wch: 25 },  // Agreement Reference
                    { wch: 20 },  // Service Type
                    { wch: 15 },  // Month
                    { wch: 15 },  // Invoice Date
                    { wch: 15 },  // Amount
                    { wch: 30 },  // Actual Filename
                    { wch: 15 },  // Upload Date
                    { wch: 12 },  // File Size
                    { wch: 10 },  // Status
                    { wch: 40 }   // S3 Location
                ];
                
                XLSX.utils.book_append_sheet(workbook, presentSheet, 'Present Certificates');
            }
            
            // 🔥 SHEET 3: Summary Statistics
            const summaryData = [
                ['Report Information', ''],
                ['Generated On', new Date().toLocaleDateString()],
                ['Year Filter', filters.year || 'All'],
                ['Month Filter', filters.month || 'All'],
                ['Project Filter', filters.project || 'All'],
                ['', ''],
                ['Summary Statistics', ''],
                ['Total Invoices with CC', reportData.statistics.total_invoices_with_cc],
                ['Missing Certificates', reportData.statistics.missing_certificates],
                ['Present Certificates', reportData.statistics.present_certificates],
                ['Missing Percentage', `${reportData.statistics.missing_percentage}%`],
                ['Completion Percentage', `${reportData.statistics.completion_percentage}%`],
                ['', ''],
                ['Missing by Project', ''],
                ...Object.entries(reportData.groupings.by_project || {}).map(([project, count]) => [project, count]),
                ['', ''],
                ['Missing by Month', ''],
                ...Object.entries(reportData.groupings.by_month || {}).map(([month, count]) => [month, count])
            ];
            
            const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
            summarySheet['!cols'] = [{ wch: 30 }, { wch: 20 }];
            XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
            
            // Generate filename
            const currentDate = new Date().toISOString().split('T')[0];
            const filterSuffix = filters.year !== 'all' ? `_${filters.year}` : '';
            const filename = `Missing_Certificates_Report${filterSuffix}_${currentDate}.xlsx`;
            
            // Export file
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([excelBuffer], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            console.log('[LOAMS] ✅ EXPORT: Missing certificates report exported successfully');
            return { success: true, filename };
            
        } catch (error) {
            console.error('[LOAMS] ❌ EXPORT: Export error:', error);
            throw new Error(`Failed to export missing certificates report: ${error.message}`);
        }
    },


    exportCompletionReportEnhanced: async (reportData, filters) => {
    console.log('[LOAMS] 📤 EXPORT: Exporting enhanced completion report to Excel');
    try {
        if (!reportData || !reportData.agreements) {
            throw new Error('No report data available to export');
        }
        
        const workbook = XLSX.utils.book_new();
        
        // 🔥 MAIN SHEET: Enhanced Certificate Completion Report
        const agreementData = reportData.agreements.map((agreement, index) => ({
            'Row': index + 1,
            'Project': agreement.project || '',
            'Agreement Reference': agreement.agreement_ref_no || '',
            'Service Type': agreement.service_type || '',
            'Month': agreement.month_label || '',
            'Contract Start Date': agreement.contract_start_date ? 
                new Date(agreement.contract_start_date).toLocaleDateString() : '',
            'Contract End Date': agreement.contract_finish_date ? 
                new Date(agreement.contract_finish_date).toLocaleDateString() : '',
            'Duration (Months)': agreement.duration_months || 12,
            'Monthly Amount (AED)': agreement.monthly_amount || 0,
            'Total Contract Value (AED)': agreement.total_contract_value || 0,
            
            // 🔥 ENHANCED: Invoice and Certificate Details
            'Serial Number': agreement.serial_number || 'No Invoice',
            'Invoice ID': agreement.invoice_id || '',
            'Invoice Number': agreement.invoice_number || '',
            'CC Status': agreement.cc_status || '',
            'Has Invoice': agreement.has_invoice ? 'Yes' : 'No',
            'Certificate Status': agreement.certificate_uploaded ? 'Uploaded' : 'Missing',
            'Certificate Filename': agreement.certificate_filename || '',
            'Upload Date': agreement.upload_date ? 
                new Date(agreement.upload_date).toLocaleDateString() : '',
            'File Size (MB)': agreement.file_size_mb || 0,
            'S3 Key': agreement.s3_key || '',
            'Overall Status': agreement.status || 'Unknown',
            'Completion Percentage': agreement.completion_percentage || 0,
            'Compliance Status': agreement.compliance_status || 'Non-Compliant'
        }));
        
        const mainSheet = XLSX.utils.json_to_sheet(agreementData);
        
        // Set column widths for better readability
        const mainCols = [
            { wch: 8 },   // Row
            { wch: 25 },  // Project
            { wch: 20 },  // Agreement Reference
            { wch: 18 },  // Service Type
            { wch: 12 },  // Month
            { wch: 15 },  // Contract Start
            { wch: 15 },  // Contract End
            { wch: 12 },  // Duration
            { wch: 15 },  // Monthly Amount
            { wch: 18 },  // Total Value
            { wch: 20 },  // Serial Number
            { wch: 12 },  // Invoice ID
            { wch: 15 },  // Invoice Number
            { wch: 12 },  // CC Status
            { wch: 12 },  // Has Invoice
            { wch: 18 },  // Certificate Status
            { wch: 30 },  // Certificate Filename
            { wch: 15 },  // Upload Date
            { wch: 12 },  // File Size
            { wch: 40 },  // S3 Key
            { wch: 25 },  // Overall Status
            { wch: 12 },  // Completion Percentage
            { wch: 15 }   // Compliance Status
        ];
        mainSheet['!cols'] = mainCols;
        
        XLSX.utils.book_append_sheet(workbook, mainSheet, 'Enhanced Completion Report');
        
        // 🔥 MONTHLY BREAKDOWN SHEET
        if (reportData.monthly_breakdown && reportData.monthly_breakdown.length > 0) {
            const monthlyData = reportData.monthly_breakdown.map((month, index) => ({
                'Row': index + 1,
                'Month': month.month || '',
                'Total Expected': month.total || 0,
                'With Invoices': month.with_invoices || 0,
                'Certificates Uploaded': month.uploaded || 0,
                'Missing Certificates': (month.total || 0) - (month.uploaded || 0),
                'Completion Percentage': `${month.percentage || 0}%`,
                'Invoice Coverage': month.with_invoices && month.total ? 
                    `${Math.round((month.with_invoices / month.total) * 100)}%` : '0%',
                'Compliance Rate': month.percentage >= 90 ? 'Excellent' : 
                                 month.percentage >= 70 ? 'Good' : 
                                 month.percentage >= 50 ? 'Fair' : 'Poor'
            }));
            
            const monthlySheet = XLSX.utils.json_to_sheet(monthlyData);
            monthlySheet['!cols'] = [
                { wch: 8 },   // Row
                { wch: 15 },  // Month
                { wch: 15 },  // Total Expected
                { wch: 15 },  // With Invoices
                { wch: 18 },  // Certificates Uploaded
                { wch: 18 },  // Missing Certificates
                { wch: 18 },  // Completion Percentage
                { wch: 15 },  // Invoice Coverage
                { wch: 15 }   // Compliance Rate
            ];
            
            XLSX.utils.book_append_sheet(workbook, monthlySheet, 'Monthly Breakdown');
        }
        
        // 🔥 ENHANCED SUMMARY SHEET
        const stats = reportData.statistics;
        const summaryData = [
            ['Enhanced Certificate Completion Report Summary', ''],
            ['Generated On', new Date().toLocaleString()],
            ['Report Period', filters.month === 'all' ? `All Months ${filters.year}` : `${filters.month}/${filters.year}`],
            ['Report Method', reportData.method || 'Enhanced with Serial Numbers'],
            ['Data Integration', 'Contract Details + Invoice Database + S3 Verification'],
            ['', ''],
            ['Overall Statistics', ''],
            ['Total Service Agreements', stats.total_agreements || 0],
            ['Agreements with Invoices', reportData.agreements.filter(a => a.has_invoice).length],
            ['Certificates Uploaded', stats.total_with_certificates || 0],
            ['Missing Certificates', stats.missing_certificates || 0],
            ['Overall Completion Rate', `${stats.completion_rate || 0}%`],
            ['Compliance Rate', `${stats.compliance_rate || 0}%`],
            ['Unique Projects', stats.unique_projects || 0],
            ['Total S3 Certificate Files', stats.total_certificates || 0],
            ['', ''],
            ['Data Source Details', ''],
            ['Contract Records Processed', stats.contract_records || 0],
            ['Total Invoice Records', stats.total_invoices || 0],
            ['S3 Certificates Found', stats.total_certificates || 0],
            ['Year Filter Applied', stats.year_filter || filters.year],
            ['Month Filter Applied', stats.month_filter || filters.month],
            ['', ''],
            ['Performance Metrics', ''],
            ['Invoice Coverage Rate', reportData.agreements.length > 0 ? 
                `${Math.round((reportData.agreements.filter(a => a.has_invoice).length / reportData.agreements.length) * 100)}%` : '0%'],
            ['Certificate Upload Rate', `${stats.completion_rate || 0}%`],
            ['Compliance Status', stats.completion_rate >= 90 ? 'Excellent' : 
                               stats.completion_rate >= 70 ? 'Good' : 
                               stats.completion_rate >= 50 ? 'Fair' : 'Needs Improvement'],
            ['Action Required', stats.completion_rate < 90 ? 'Yes - Upload Missing Certificates' : 'No - Maintain Current Performance'],
            ['Priority Level', stats.completion_rate < 50 ? 'High Priority' : 
                            stats.completion_rate < 80 ? 'Medium Priority' : 'Low Priority'],
            ['', ''],
            ['Technical Details', ''],
            ['S3 Bucket Integration', 'Real-time certificate file verification'],
            ['Invoice Matching Logic', 'PO Number + Month Period matching'],
            ['Certificate Identification', 'Serial number pattern matching'],
            ['Data Validation', 'Cross-reference contract periods with actual invoices'],
            ['Export Timestamp', new Date().toISOString()]
        ];
        
        const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
        summarySheet['!cols'] = [{ wch: 35 }, { wch: 30 }];
        XLSX.utils.book_append_sheet(workbook, summarySheet, 'Executive Summary');
        
        // Generate filename
        const currentDate = new Date().toISOString().split('T')[0];
        const periodSuffix = filters.month === 'all' ? 
            `_All_Months_${filters.year}` : 
            `_${filters.month}_${filters.year}`;
        const filename = `Enhanced_Certificate_Completion_Report${periodSuffix}_${currentDate}.xlsx`;
        
        // Export file
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log('[LOAMS] ✅ EXPORT: Enhanced completion report exported successfully');
        return { success: true, filename };
        
    } catch (error) {
        console.error('[LOAMS] ❌ EXPORT: Enhanced export error:', error);
        throw new Error(`Failed to export enhanced completion report: ${error.message}`);
    }
},

getDetailedCompletionAnalysis: async (year = '2025', month = 'all') => {
    console.log('[LOAMS] 📊 DETAILED: Getting detailed completion analysis');
    try {
        const response = await loamsApi.enhancedCertificates.getCompletionReportEnhanced(year, month);
        
        if (!response.success) {
            throw new Error('Failed to get completion report data');
        }
        
        // Enhanced analysis of the data
        const analysis = {
            ...response,
            enhanced_analysis: {
                invoice_coverage: {
                    total_expected: response.statistics.total_agreements,
                    with_invoices: response.agreements.filter(a => a.has_invoice).length,
                    without_invoices: response.agreements.filter(a => !a.has_invoice).length,
                    coverage_percentage: response.agreements.length > 0 ? 
                        Math.round((response.agreements.filter(a => a.has_invoice).length / response.agreements.length) * 100) : 0
                },
                certificate_analysis: {
                    total_with_certificates: response.statistics.total_with_certificates,
                    missing_certificates: response.statistics.missing_certificates,
                    completion_rate: response.statistics.completion_rate,
                    certificates_without_invoices: response.agreements.filter(a => a.certificate_uploaded && !a.has_invoice).length,
                    invoices_without_certificates: response.agreements.filter(a => a.has_invoice && !a.certificate_uploaded).length
                },
                project_breakdown: analyzeProjectPerformance(response.agreements),
                monthly_trends: analyzeMonthlyTrends(response.monthly_breakdown),
                recommendations: generateRecommendations(response)
            }
        };
        
        console.log('[LOAMS] ✅ DETAILED: Enhanced analysis completed');
        return analysis;
        
    } catch (error) {
        console.error('[LOAMS] ❌ DETAILED: Analysis error:', error);
        throw error;
    }
},



    getFilterOptions: async () => {
        console.log('[LOAMS] 🔍 FILTERS: Getting available filter options');
        try {
            // This could be enhanced to call a backend endpoint for dynamic filters
            return {
                years: ['2023', '2024', '2025', 'all'],
                months: [
                    { value: 'all', label: 'All Months' },
                    { value: '1', label: 'January' },
                    { value: '2', label: 'February' },
                    { value: '3', label: 'March' },
                    { value: '4', label: 'April' },
                    { value: '5', label: 'May' },
                    { value: '6', label: 'June' },
                    { value: '7', label: 'July' },
                    { value: '8', label: 'August' },
                    { value: '9', label: 'September' },
                    { value: '10', label: 'October' },
                    { value: '11', label: 'November' },
                    { value: '12', label: 'December' }
                ],
                projects: [] // This would be populated from the missing report data
            };
        } catch (error) {
            console.error('[LOAMS] ❌ FILTERS: Error getting filter options:', error);
            return {
                years: ['2025', '2024', '2023'],
                months: [{ value: 'all', label: 'All Months' }],
                projects: []
            };
        }
    },

    validateReportData: (reportData) => {
        console.log('[LOAMS] 🔍 VALIDATE: Validating report data');
        try {
            const validation = {
                isValid: true,
                issues: [],
                summary: {
                    totalRecords: 0,
                    recordsWithIssues: 0,
                    missingSerialNumbers: 0,
                    invalidAmounts: 0,
                    missingDates: 0
                }
            };
            
            if (!reportData || !reportData.agreements) {
                validation.isValid = false;
                validation.issues.push('No report data available');
                return validation;
            }
            
            validation.summary.totalRecords = reportData.agreements.length;
            
            reportData.agreements.forEach((agreement, index) => {
                const recordIssues = [];
                
                // Check serial number
                if (!agreement.serial_number) {
                    recordIssues.push('Missing serial number');
                    validation.summary.missingSerialNumbers++;
                }
                
                // Check amounts
                if (agreement.monthly_amount && (isNaN(agreement.monthly_amount) || agreement.monthly_amount < 0)) {
                    recordIssues.push('Invalid monthly amount');
                    validation.summary.invalidAmounts++;
                }
                
                // Check dates
                if (!agreement.contract_start_date) {
                    recordIssues.push('Missing contract start date');
                    validation.summary.missingDates++;
                }
                
                if (recordIssues.length > 0) {
                    validation.summary.recordsWithIssues++;
                    validation.issues.push({
                        row: index + 1,
                        agreement: agreement.agreement_ref_no,
                        issues: recordIssues
                    });
                }
            });
            
            if (validation.summary.recordsWithIssues > 0) {
                validation.isValid = false;
            }
            
            console.log('[LOAMS] 📊 VALIDATE: Validation complete:', validation.summary);
            return validation;
            
        } catch (error) {
            console.error('[LOAMS] ❌ VALIDATE: Validation error:', error);
            return {
                isValid: false,
                issues: [`Validation failed: ${error.message}`],
                summary: { totalRecords: 0, recordsWithIssues: 0 }
            };
        }
    },




    

    // 🔥 NEW: Get simple counts only (fast fallback)
    getSimpleCounts: async () => {
        console.log('[LOAMS] 🔍 Getting simple certificate counts...');
        try {
            const response = await pythonApi.get('/api/certificates/counts', {
                headers: {
                    'Accept': 'application/json'
                },
                timeout: 30000 // 30 second timeout
            });
            
            if (!response.data || !response.data.success) {
                throw new Error('Failed to get certificate counts');
            }
            
            console.log('[LOAMS] ✅ Certificate counts retrieved:', response.data.counts);
            
            // Return in the expected format
            return {
                data: {
                    success: true,
                    certificates: [], // Empty array since we only got counts
                    total: response.data.counts.total,
                    breakdown: {
                        original: response.data.counts.original,
                        cc_loams: response.data.counts.cc_loams
                    },
                    counts_only: true // Flag to indicate this is counts only
                }
            };
        } catch (error) {
            console.error('[LOAMS] ❌ Simple counts error:', error);
            throw error;
        }
    },

    // 🔥 UPDATED: Get all certificates with automatic pagination
    getAllCertificates: async () => {
        console.log('[LOAMS] 🔍 FIXED: Fetching ALL certificates with validation...');
        try {
            let allCertificates = [];
            let page = 1;
            let totalPages = 1;
            const perPage = 200;
            
            // Get first page
            const firstResponse = await loamsApi.enhancedCertificates.listFromBothLocations(page, perPage);
            
            if (!firstResponse.data.success) {
                throw new Error('Failed to fetch first page');
            }
            
            if (firstResponse.data.counts_only) {
                console.log('[LOAMS] 📊 Only counts available');
                return firstResponse;
            }
            
            allCertificates = [...firstResponse.data.certificates];
            totalPages = firstResponse.data.total_pages || 1;
            
            console.log(`[LOAMS] 📄 FIXED: Got page 1 of ${totalPages}, certificates: ${allCertificates.length}`);
            
            // Fetch remaining pages
            for (page = 2; page <= Math.min(totalPages, 10); page++) {
                try {
                    console.log(`[LOAMS] 📄 FIXED: Fetching page ${page}...`);
                    const response = await loamsApi.enhancedCertificates.listFromBothLocations(page, perPage);
                    
                    if (response.data.success && response.data.certificates) {
                        allCertificates = [...allCertificates, ...response.data.certificates];
                        console.log(`[LOAMS] 📄 FIXED: Page ${page} added ${response.data.certificates.length} certificates`);
                    }
                } catch (pageError) {
                    console.warn(`[LOAMS] ⚠️ FIXED: Failed to fetch page ${page}:`, pageError.message);
                    break;
                }
            }
            
            console.log(`[LOAMS] ✅ FIXED: Final collection: ${allCertificates.length} certificates`);
            
            // 🔥 FIXED: Enhanced transformation with proper validation
            const transformedCertificates = allCertificates.map(cert => {
                const isOriginal = cert.certificate_type === 'original';
                const isCCLoams = cert.certificate_type === 'cc_loams';
                
                // 🔥 VALIDATION: Check for proper data
                const hasValidCluster = cert.clusterName && cert.clusterName !== 'Unknown';
                const hasValidAgreement = cert.agreementNo && cert.agreementNo !== 'Unknown';
                const hasValidMonth = cert.month && cert.month !== 'Unknown';
                
                if (!hasValidCluster || !hasValidAgreement || !hasValidMonth) {
                    console.warn(`[LOAMS] ⚠️ FIXED: Certificate missing data:`, {
                        filename: cert.filename,
                        type: cert.certificate_type,
                        cluster: cert.clusterName,
                        agreement: cert.agreementNo,
                        month: cert.month
                    });
                }
                
                return {
                    ...cert,
                    // Ensure proper type flags
                    isOriginal: isOriginal,
                    isCCLoams: isCCLoams,
                    locationLabel: isOriginal ? 'Original' : 'CC-LOAMS',
                    locationBadge: isOriginal ? 'success' : 'info',
                    
                    // 🔥 FIXED: Enhanced date formatting
                    date: new Date(cert.last_modified),
                    formattedDate: new Date(cert.last_modified).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: 'Asia/Dubai'
                    }),
                    
                    // Enhanced size formatting
                    size: loamsApi.enhancedCertificates.formatFileSize(cert.size),
                    
                    // 🔥 FIXED: Validation flags
                    hasValidData: hasValidCluster && hasValidAgreement && hasValidMonth,
                    validationIssues: {
                        missingCluster: !hasValidCluster,
                        missingAgreement: !hasValidAgreement,
                        missingMonth: !hasValidMonth
                    },
                    
                    // Debug info
                    debugInfo: {
                        originalType: cert.certificate_type,
                        hasInvoiceNumber: !!cert.invoice_number,
                        clusterParsed: hasValidCluster,
                        agreementParsed: hasValidAgreement,
                        monthParsed: hasValidMonth
                    }
                };
            });
            
            // 🔥 FIXED: Enhanced statistics
            const finalStats = {
                total: transformedCertificates.length,
                original: transformedCertificates.filter(c => c.isOriginal).length,
                ccLoams: transformedCertificates.filter(c => c.isCCLoams).length,
                withValidData: transformedCertificates.filter(c => c.hasValidData).length,
                withValidCluster: transformedCertificates.filter(c => c.clusterName !== 'Unknown').length,
                withValidAgreement: transformedCertificates.filter(c => c.agreementNo !== 'Unknown').length,
                withValidMonth: transformedCertificates.filter(c => c.month !== 'Unknown').length,
                validationRate: transformedCertificates.length > 0 
                    ? (transformedCertificates.filter(c => c.hasValidData).length / transformedCertificates.length * 100).toFixed(2)
                    : 0
            };
            
            console.log('[LOAMS] 📊 FIXED: Final certificate statistics:', finalStats);
            
            return {
                data: {
                    success: true,
                    certificates: transformedCertificates,
                    total: firstResponse.data.total,
                    breakdown: firstResponse.data.breakdown,
                    pages_fetched: page - 1,
                    total_pages: totalPages,
                    statistics: finalStats
                }
            };
            
        } catch (error) {
            console.error('[LOAMS] ❌ FIXED: Get all certificates error:', error);
            throw error;
        }
    },

    // 🔥 FIXED: Enhanced preview URL with proper fallback
    getEnhancedPreviewUrl: async (certificateKey) => {
        console.log('[LOAMS] 🔍 FIXED: Getting preview URL for:', certificateKey);
        try {
            if (!certificateKey) {
                throw new Error('Certificate key is required');
            }
            
            // Clean and encode the key
            const cleanKey = certificateKey.trim();
            const encodedKey = encodeURIComponent(cleanKey);
            
            console.log('[LOAMS] 🔗 FIXED: Encoded key:', encodedKey);
            
            // Try enhanced preview first with timeout and retry
            let response;
            let lastError;
            
            // Attempt 1: Enhanced preview
            try {
                console.log('[LOAMS] 🔄 FIXED: Attempting enhanced preview...');
                response = await Promise.race([
                    pythonApi.get(`/api/certificate/preview-enhanced/${encodedKey}`, {
                        timeout: 30000,
                        headers: {
                            'Accept': 'application/json',
                            'Cache-Control': 'no-cache'
                        }
                    }),
                    new Promise((_, reject) => 
                        setTimeout(() => reject(new Error('Enhanced preview timeout')), 30000)
                    )
                ]);
                
                if (response.data && response.data.success && response.data.url) {
                    console.log('[LOAMS] ✅ FIXED: Enhanced preview URL successful');
                    return response.data;
                }
                throw new Error('Enhanced preview returned invalid data');
                
            } catch (enhancedError) {
                console.log('[LOAMS] ⚠️ FIXED: Enhanced preview failed:', enhancedError.message);
                lastError = enhancedError;
            }
            
            // Attempt 2: Fallback preview
            try {
                console.log('[LOAMS] 🔄 FIXED: Attempting fallback preview...');
                response = await Promise.race([
                    pythonApi.get(`/api/certificate/preview/${encodedKey}`, {
                        timeout: 20000,
                        headers: {
                            'Accept': 'application/json',
                            'Cache-Control': 'no-cache'
                        }
                    }),
                    new Promise((_, reject) => 
                        setTimeout(() => reject(new Error('Fallback preview timeout')), 20000)
                    )
                ]);
                
                if (response.data && response.data.success && response.data.url) {
                    console.log('[LOAMS] ✅ FIXED: Fallback preview URL successful');
                    return { url: response.data.url };
                }
                throw new Error('Fallback preview returned invalid data');
                
            } catch (fallbackError) {
                console.log('[LOAMS] ⚠️ FIXED: Fallback preview failed:', fallbackError.message);
                lastError = fallbackError;
            }
            
            // Both attempts failed
            throw new Error(`All preview methods failed. Last error: ${lastError.message}`);
            
        } catch (error) {
            console.error('[LOAMS] ❌ FIXED: Preview URL error:', error);
            throw new Error(`Failed to get preview URL: ${error.message}`);
        }
    },

    // 🔥 FIXED: Download single certificate with proper error handling
    downloadSingle: async (certificate, options = {}) => {
        console.log('[LOAMS] 🔽 SIMPLIFIED: Single download for:', certificate.filename);
        try {
            if (!certificate || !certificate.key) {
                throw new Error('Invalid certificate data');
            }
            
            // Generate appropriate filename
            let filename = 'certificate.pdf';
            try {
                if (certificate.certificate_type === 'cc_loams' || certificate.isCCLoams) {
                    if (certificate.clusterName && certificate.clusterName !== 'Unknown' &&
                        certificate.month && certificate.month !== 'Unknown') {
                        const cluster = certificate.clusterName.replace(/[<>:"/\\|?*]/g, '_');
                        const month = certificate.month.replace(/[<>:"/\\|?*]/g, '_');
                        filename = `CC-${cluster}-${month}.pdf`;
                    } else {
                        filename = certificate.filename || 'CC-Certificate.pdf';
                    }
                } else {
                    if (certificate.clusterName && certificate.clusterName !== 'Unknown' &&
                        certificate.agreementNo && certificate.agreementNo !== 'Unknown' &&
                        certificate.month && certificate.month !== 'Unknown') {
                        const cluster = certificate.clusterName.replace(/[<>:"/\\|?*]/g, '_');
                        const agreement = certificate.agreementNo.replace(/[<>:"/\\|?*]/g, '_');
                        const month = certificate.month.replace(/[<>:"/\\|?*]/g, '_');
                        filename = `${cluster}-${agreement}-${month}.pdf`;
                    } else {
                        filename = certificate.filename || 'Certificate.pdf';
                    }
                }
            } catch (filenameError) {
                console.warn('[LOAMS] ⚠️ Filename generation error:', filenameError);
                filename = certificate.filename || 'certificate.pdf';
            }
            
            // Method 1: Try direct download first
            try {
                console.log('[LOAMS] 🔄 Trying direct download method...');
                const result = await loamsApi.enhancedCertificates.downloadDirect(certificate.key, filename);
                console.log('[LOAMS] ✅ Direct download successful');
                return result;
            } catch (directError) {
                console.log('[LOAMS] ⚠️ Direct download failed:', directError.message);
            }
            
            // Method 2: Try preview URL method
            try {
                console.log('[LOAMS] 🔄 Trying preview URL method...');
                
                const previewData = await loamsApi.enhancedCertificates.getSimplePreviewUrl(certificate.key);
                
                if (!previewData || !previewData.url) {
                    throw new Error('No preview URL received');
                }
                
                // Download using preview URL
                const response = await fetch(previewData.url, {
                    method: 'GET',
                    mode: 'cors'
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                
                const blob = await response.blob();
                
                if (!blob || blob.size === 0) {
                    throw new Error('Downloaded file is empty');
                }
                
                // Create download
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                link.style.display = 'none';
                
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                
                console.log('[LOAMS] ✅ Preview URL download successful');
                return { success: true, filename };
                
            } catch (previewError) {
                console.log('[LOAMS] ⚠️ Preview URL method failed:', previewError.message);
                throw new Error(`All download methods failed. Last error: ${previewError.message}`);
            }
            
        } catch (error) {
            console.error('[LOAMS] ❌ Single download error:', error);
            throw new Error(`Download failed: ${error.message}`);
        }
    },


    getSimplePreviewUrl: async (certificateKey) => {
        console.log('[LOAMS] 🔍 SIMPLE: Getting preview URL for:', certificateKey);
        try {
            if (!certificateKey) {
                throw new Error('Certificate key is required');
            }
            
            const cleanKey = certificateKey.trim();
            const encodedKey = encodeURIComponent(cleanKey);
            
            console.log('[LOAMS] 🔗 SIMPLE: Encoded key:', encodedKey);
            
            // Use simple fetch without problematic headers
            const response = await fetch(`${pythonApi.defaults.baseURL}/api/certificate/preview/${encodedKey}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                    // ✅ Removed Cache-Control header that was causing CORS issues
                },
                mode: 'cors'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const data = await response.json();
            
            if (!data.success || !data.url) {
                throw new Error(data.error || 'Invalid response from server');
            }
            
            console.log('[LOAMS] ✅ SIMPLE: Preview URL obtained successfully');
            return data;
            
        } catch (error) {
            console.error('[LOAMS] ❌ SIMPLE: Preview URL error:', error);
            throw new Error(`Failed to get preview URL: ${error.message}`);
        }
    },

    downloadDirect: async (certificateKey, filename = null) => {
        console.log('[LOAMS] 🔽 DIRECT: Starting direct download for:', certificateKey);
        try {
            const cleanKey = certificateKey.trim();
            const encodedKey = encodeURIComponent(cleanKey);
            
            // Use direct download endpoint
            const response = await fetch(`${pythonApi.defaults.baseURL}/api/certificate/download/${encodedKey}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/pdf, application/octet-stream'
                },
                mode: 'cors'
            });
            
            if (!response.ok) {
                throw new Error(`Direct download failed: ${response.status} ${response.statusText}`);
            }
            
            const blob = await response.blob();
            
            if (!blob || blob.size === 0) {
                throw new Error('Downloaded file is empty');
            }
            
            // Create download
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename || cleanKey.split('/').pop() || 'certificate.pdf';
            link.style.display = 'none';
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
            
            console.log('[LOAMS] ✅ DIRECT: Download completed successfully');
            return { success: true, filename: link.download };
            
        } catch (error) {
            console.error('[LOAMS] ❌ DIRECT: Download error:', error);
            throw new Error(`Direct download failed: ${error.message}`);
        }
    },

     downloadBatch: async (certificateKeys, options = {}) => {
        console.log('[LOAMS] 📦 BATCH: Starting batch download for', certificateKeys.length, 'certificates');
        try {
            if (!certificateKeys || certificateKeys.length === 0) {
                throw new Error('No certificates selected');
            }
            
            // Use backend batch endpoint
            const response = await fetch(`${pythonApi.defaults.baseURL}/api/certificates/download-batch`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/zip, application/json'
                },
                mode: 'cors',
                body: JSON.stringify({
                    keys: certificateKeys,
                    options: options
                })
            });
            
            if (!response.ok) {
                // Try to get error message
                try {
                    const errorData = await response.json();
                    throw new Error(errorData.error || `HTTP ${response.status}`);
                } catch (jsonError) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
            }
            
            const blob = await response.blob();
            
            if (!blob || blob.size === 0) {
                throw new Error('Downloaded ZIP file is empty');
            }
            
            // Create download
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            
            const currentDate = new Date().toISOString().split('T')[0];
            link.download = `certificates-batch-${currentDate}-${certificateKeys.length}files.zip`;
            link.style.display = 'none';
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
            
            console.log('[LOAMS] ✅ BATCH: Download completed successfully');
            return { 
                success: true, 
                filename: link.download,
                count: certificateKeys.length 
            };
            
        } catch (error) {
            console.error('[LOAMS] ❌ BATCH: Download error:', error);
            throw new Error(`Batch download failed: ${error.message}`);
        }
    },

    // 🔥 FIXED: Multiple download with better error handling and progress tracking
    downloadMultiple: async (certificates, options = {}) => {
        console.log('[LOAMS] 🔽 SIMPLIFIED: Multiple download for', certificates.length, 'certificates');
        try {
            if (!certificates || certificates.length === 0) {
                throw new Error('No certificates selected');
            }
            
            if (certificates.length === 1) {
                console.log('[LOAMS] 🔄 Single certificate, using single download');
                return await loamsApi.enhancedCertificates.downloadSingle(certificates[0], options);
            }
            
            // Extract certificate keys
            const certificateKeys = certificates.map(cert => cert.key).filter(key => key);
            
            if (certificateKeys.length === 0) {
                throw new Error('No valid certificate keys found');
            }
            
            console.log('[LOAMS] 📦 Using backend batch download for', certificateKeys.length, 'certificates');
            
            // Use backend batch download
            const result = await loamsApi.enhancedCertificates.downloadBatch(certificateKeys, options);
            
            return {
                success: true,
                filename: result.filename,
                successCount: result.count,
                errorCount: 0,
                errors: []
            };
            
        } catch (error) {
            console.error('[LOAMS] ❌ Multiple download error:', error);
            throw error;
        }
    },


    filterByMonth: (certificates, monthFilter) => {
        if (!monthFilter || monthFilter === 'all' || monthFilter === '') {
            return certificates;
        }
        
        console.log(`[LOAMS] 🔍 SIMPLIFIED: Filtering by month: "${monthFilter}"`);
        
        const filtered = certificates.filter(cert => {
            if (!cert.month || cert.month === 'Unknown' || cert.month === null) {
                return false;
            }
            
            try {
                let certMonth = String(cert.month).trim();
                
                // Extract YYYY-MM format
                if (certMonth.includes('T')) {
                    certMonth = certMonth.substring(0, 7);
                } else if (certMonth.includes(' ')) {
                    certMonth = certMonth.substring(0, 7);
                } else if (certMonth.length > 7) {
                    certMonth = certMonth.substring(0, 7);
                }
                
                return /^\d{4}-\d{2}$/.test(certMonth) && certMonth === monthFilter;
            } catch (error) {
                console.error(`[LOAMS] ❌ Month parsing error for "${cert.month}":`, error);
                return false;
            }
        });
        
        console.log(`[LOAMS] ✅ SIMPLIFIED: Filtered ${filtered.length}/${certificates.length} certificates for month ${monthFilter}`);
        return filtered;
    },

    // 🔥 NEW: Filter certificates by cluster
    filterByCluster: (certificates, clusterFilter) => {
        if (!clusterFilter || clusterFilter === 'all') {
            return certificates;
        }
        
        console.log(`[LOAMS] 🔍 FIXED: Filtering by cluster: ${clusterFilter}`);
        
        const filtered = certificates.filter(cert => {
            return cert.clusterName && cert.clusterName !== 'Unknown' && cert.clusterName === clusterFilter;
        });
        
        console.log(`[LOAMS] ✅ FIXED: Filtered ${filtered.length} certificates for cluster ${clusterFilter}`);
        return filtered;
    },

    // 🔥 NEW: Get available months for filtering
    getAvailableMonths: (certificates) => {
        const monthSet = new Set();
        
        certificates.forEach(cert => {
            if (cert.month && cert.month !== 'Unknown' && cert.month !== null) {
                try {
                    let month = String(cert.month).trim();
                    
                    if (month.includes('T')) {
                        month = month.substring(0, 7);
                    } else if (month.includes(' ')) {
                        month = month.substring(0, 7);
                    } else if (month.length > 7) {
                        month = month.substring(0, 7);
                    }
                    
                    if (/^\d{4}-\d{2}$/.test(month)) {
                        monthSet.add(month);
                    }
                } catch (error) {
                    console.warn(`[LOAMS] ⚠️ Error processing month "${cert.month}":`, error);
                }
            }
        });
        
        return Array.from(monthSet).sort().reverse();
    },

    testConnectivity: async () => {
        console.log('[LOAMS] 🔍 Testing backend connectivity...');
        try {
            const response = await fetch(`${pythonApi.defaults.baseURL}/api/health`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                },
                mode: 'cors'
            });
            
            if (!response.ok) {
                throw new Error(`Health check failed: ${response.status}`);
            }
            
            const data = await response.json();
            console.log('[LOAMS] ✅ Backend connectivity test successful:', data);
            return { success: true, data };
            
        } catch (error) {
            console.error('[LOAMS] ❌ Backend connectivity test failed:', error);
            return { success: false, error: error.message };
        }
    },

    // 🔥 NEW: Test S3 connectivity
    testS3: async () => {
        console.log('[LOAMS] 🔍 Testing S3 connectivity...');
        try {
            const response = await fetch(`${pythonApi.defaults.baseURL}/api/debug/s3-test`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                },
                mode: 'cors'
            });
            
            if (!response.ok) {
                throw new Error(`S3 test failed: ${response.status}`);
            }
            
            const data = await response.json();
            console.log('[LOAMS] ✅ S3 connectivity test result:', data);
            return data;
            
        } catch (error) {
            console.error('[LOAMS] ❌ S3 connectivity test failed:', error);
            return { success: false, error: error.message };
        }
    },


    // 🔥 NEW: Get available clusters for filtering
    getAvailableClusters: (certificates) => {
        const clusters = new Set();
        certificates.forEach(cert => {
            if (cert.clusterName && cert.clusterName !== 'Unknown') {
                clusters.add(cert.clusterName);
            }
        });
        
        return Array.from(clusters).sort();
    },


    // 🔥 FIXED: Single method for all months report (removed duplicate)
    getAllMonthsReport: async () => {
    console.log('[LOAMS] 🔍 FIXED: Fetching CC-LOAMS certificate report...');
    try {
        // Try optimized endpoint first (with shorter timeout)
        console.log('[LOAMS] 🚀 Trying optimized endpoint...');
        try {
            const response = await pythonApi.get('/api/certificates/report/all-months-optimized', {
                headers: {
                    'Accept': 'application/json'
                },
                timeout: 30000 // 30 seconds for optimized version
            });
            
            if (response.data && response.data.success) {
                console.log('[LOAMS] ✅ Optimized report successful');
                return response.data;
            }
        } catch (optimizedError) {
            console.log('[LOAMS] ⚠️ Optimized endpoint failed:', optimizedError.message);
            
            // If dataset too large, inform user
            if (optimizedError.response?.status === 413) {
                throw new Error('Dataset too large for real-time processing. Please contact administrator or try filtering by specific months.');
            }
        }
        
        // Fallback to regular endpoint with longer timeout
        console.log('[LOAMS] 🔄 Trying regular endpoint...');
        const response = await pythonApi.get('/api/certificates/report/all-months', {
            headers: {
                'Accept': 'application/json'
            },
            timeout: 120000 // 2 minutes for regular version
        });
        
        if (!response.data) {
            throw new Error('No data received from server');
        }
        
        if (!response.data.success) {
            throw new Error(response.data.error || 'Failed to load CC-LOAMS report');
        }
        
        console.log('[LOAMS] ✅ CC-LOAMS report loaded successfully:', {
            totalAgreements: response.data.statistics?.total_agreements || 0,
            totalCertificates: response.data.statistics?.total_certificates || 0,
            completionRate: response.data.statistics?.completion_rate || 0
        });
        
        return response.data;
        
    } catch (error) {
        console.error('[LOAMS] ❌ CC-LOAMS report error:', error);
        
        // Enhanced error handling
        if (error.code === 'ECONNABORTED') {
            // If timeout, try to create fallback from certificates
            console.log('[LOAMS] 🔄 Timeout occurred, trying certificate-based fallback...');
            try {
                return await loamsApi.createCCLoamsFallbackReport();
            } catch (fallbackError) {
                throw new Error('Report generation timed out and fallback failed. The dataset may be too large. Please try filtering by specific months or contact administrator.');
            }
        }
        
        throw error;
    }
},


createCCLoamsFallbackReport: async () => {
    console.log('[LOAMS] 🔄 Creating CC-LOAMS fallback report from certificates...');
    try {
        // Get certificates that we already have loaded
        const certificatesResponse = await loamsApi.enhancedCertificates.getAllCertificates();
        
        if (!certificatesResponse.data?.success || !certificatesResponse.data.certificates) {
            throw new Error('No certificate data available for fallback');
        }
        
        // Filter only CC-LOAMS certificates
        const ccLoamsCertificates = certificatesResponse.data.certificates.filter(cert => 
            cert.isCCLoams || cert.certificate_type === 'cc_loams'
        );
        
        console.log(`[LOAMS] 📊 Processing ${ccLoamsCertificates.length} CC-LOAMS certificates for fallback report`);
        
        if (ccLoamsCertificates.length === 0) {
            return {
                success: true,
                agreements: [],
                statistics: {
                    total_agreements: 0,
                    total_with_certificates: 0,
                    total_certificates: 0,
                    completion_rate: 0,
                    unique_projects: 0
                },
                monthly_breakdown: [],
                method: 'cc_loams_fallback_empty'
            };
        }
        
        // Process certificates
        const agreementMap = new Map();
        const monthlyStats = new Map();
        const uniqueProjects = new Set();
        
        ccLoamsCertificates.forEach(cert => {
            // Extract invoice number from filename (CC-LOAMS-1852.pdf -> 1852)
            let invoiceNumber = 'Unknown';
            if (cert.filename) {
                const match = cert.filename.match(/CC-(?:LOAMS-)?(\d+)\.pdf/i);
                if (match) {
                    invoiceNumber = match[1];
                }
            }
            
            const project = cert.clusterName || cert.displayCluster || 'Unknown Project';
            const agreement = cert.agreementNo || cert.displayAgreement || `LOAMS-${invoiceNumber}`;
            const month = cert.month || 'Unknown';
            
            // Create unique key
            const agreementKey = `${project}|||${agreement}|||${month}`;
            
            if (!agreementMap.has(agreementKey)) {
                agreementMap.set(agreementKey, {
                    project: project,
                    agreement_ref_no: agreement,
                    service_type: 'General Service', // Default
                    month: month,
                    monthLabel: loamsApi.enhancedCertificates.formatMonthLabel(month),
                    serial_number: `LOAMS-${invoiceNumber}`,
                    invoice_number: invoiceNumber,
                    certificate_uploaded: true,
                    upload_date: cert.last_modified || new Date().toISOString(),
                    certificate_filename: cert.filename,
                    status: 'Uploaded',
                    total_amount: 0 // We don't have amount from certificates
                });
                
                uniqueProjects.add(project);
            }
            
            // Track monthly statistics
            if (month && month !== 'Unknown') {
                if (!monthlyStats.has(month)) {
                    monthlyStats.set(month, {
                        month: loamsApi.enhancedCertificates.formatMonthLabel(month),
                        total: 0,
                        uploaded: 0,
                        percentage: 0
                    });
                }
                
                const monthStat = monthlyStats.get(month);
                monthStat.total++;
                monthStat.uploaded++;
            }
        });
        
        // Calculate percentages
        monthlyStats.forEach(stat => {
            stat.percentage = 100; // All certificates exist
        });
        
        const agreements = Array.from(agreementMap.values());
        const monthlyBreakdown = Array.from(monthlyStats.values()).sort((a, b) => {
            const aMonth = agreements.find(ag => ag.monthLabel === a.month)?.month || '0000-00';
            const bMonth = agreements.find(ag => ag.monthLabel === b.month)?.month || '0000-00';
            return bMonth.localeCompare(aMonth);
        });
        
        const result = {
            success: true,
            agreements: agreements,
            statistics: {
                total_agreements: agreements.length,
                total_with_certificates: agreements.length,
                total_certificates: ccLoamsCertificates.length,
                completion_rate: 100,
                unique_projects: uniqueProjects.size
            },
            monthly_breakdown: monthlyBreakdown,
            method: 'cc_loams_certificate_fallback'
        };
        
        console.log('[LOAMS] ✅ CC-LOAMS fallback report created:', {
            agreements: agreements.length,
            projects: uniqueProjects.size,
            months: monthlyBreakdown.length
        });
        
        return result;
        
    } catch (error) {
        console.error('[LOAMS] ❌ CC-LOAMS fallback error:', error);
        throw new Error(`Failed to generate CC-LOAMS fallback report: ${error.message}`);
    }
},







createAllMonthsReportFromCertificates: async () => {
    console.log('[LOAMS] 🔄 FIXED: Creating all months report from available data...');
    try {
        // Get all certificates first
        const certificatesResponse = await loamsApi.enhancedCertificates.getAllCertificates();
        
        if (!certificatesResponse.data?.success || !certificatesResponse.data.certificates) {
            throw new Error('No certificate data available for fallback report');
        }
        
        const certificates = certificatesResponse.data.certificates;
        console.log(`[LOAMS] 📊 FIXED: Processing ${certificates.length} certificates for all months report`);
        
        if (certificates.length === 0) {
            return {
                success: true,
                agreements: [],
                statistics: {
                    total_agreements: 0,
                    total_with_certificates: 0,
                    total_certificates: 0,
                    completion_rate: 0,
                    unique_projects: 0
                },
                monthly_breakdown: [],
                method: 'fallback_empty'
            };
        }
        
        // Group certificates by project, agreement, and month
        const agreementMap = new Map();
        const monthlyStats = new Map();
        const uniqueProjects = new Set();
        
        certificates.forEach(cert => {
            const project = cert.clusterName || cert.displayCluster || 'Unknown Project';
            const agreement = cert.agreementNo || cert.displayAgreement || 'Unknown Agreement';
            const month = cert.month || 'Unknown';
            
            // Create unique key for each agreement-month combination
            const agreementKey = `${project}|||${agreement}|||${month}`;
            
            if (!agreementMap.has(agreementKey)) {
                agreementMap.set(agreementKey, {
                    project: project,
                    agreement_ref_no: agreement,
                    service_type: 'General Service', // Default since we don't have this info
                    month: month,
                    monthLabel: loamsApi.enhancedCertificates.formatMonthLabel(month),
                    contract_start_date: null,
                    contract_finish_date: null,
                    duration: 12, // Default duration
                    certificate_uploaded: true, // If certificate exists, it's uploaded
                    upload_date: cert.last_modified || new Date().toISOString(),
                    status: 'Uploaded'
                });
                
                uniqueProjects.add(project);
            }
            
            // Track monthly statistics
            if (month && month !== 'Unknown') {
                if (!monthlyStats.has(month)) {
                    monthlyStats.set(month, {
                        month: loamsApi.enhancedCertificates.formatMonthLabel(month),
                        total: 0,
                        uploaded: 0,
                        percentage: 0
                    });
                }
                
                const monthStat = monthlyStats.get(month);
                monthStat.total++;
                monthStat.uploaded++;
            }
        });
        
        // Calculate percentages for monthly breakdown
        monthlyStats.forEach(stat => {
            stat.percentage = stat.total > 0 ? Math.round((stat.uploaded / stat.total) * 100) : 0;
        });
        
        // Convert maps to arrays
        const agreements = Array.from(agreementMap.values());
        const monthlyBreakdown = Array.from(monthlyStats.values()).sort((a, b) => {
            // Sort by month (newest first)
            const aMonth = agreements.find(ag => ag.monthLabel === a.month)?.month || '0000-00';
            const bMonth = agreements.find(ag => ag.monthLabel === b.month)?.month || '0000-00';
            return bMonth.localeCompare(aMonth);
        });
        
        const result = {
            success: true,
            agreements: agreements,
            statistics: {
                total_agreements: agreements.length,
                total_with_certificates: agreements.length, // All have certificates since they exist
                total_certificates: certificates.length,
                completion_rate: 100, // 100% since all agreements have certificates
                unique_projects: uniqueProjects.size
            },
            monthly_breakdown: monthlyBreakdown,
            method: 'enhanced_certificate_fallback'
        };
        
        console.log('[LOAMS] ✅ FIXED: Enhanced fallback all months report created:', {
            agreements: agreements.length,
            projects: uniqueProjects.size,
            months: monthlyBreakdown.length,
            totalCertificates: certificates.length
        });
        
        return result;
        
    } catch (error) {
        console.error('[LOAMS] ❌ FIXED: Enhanced fallback all months report error:', error);
        throw new Error(`Failed to generate enhanced all months report: ${error.message}`);
    }
},

    // 🔥 ENHANCED: Fallback method for all months report (client-side aggregation)
    getAllMonthsReportFallback: async () => {
        console.log('[LOAMS] Using enhanced fallback method for all months report');
        try {
            // Try to get basic certificate data first
            const certificatesResponse = await loamsApi.enhancedCertificates.getAllCertificates();
            
            if (!certificatesResponse.data?.success) {
                throw new Error('Failed to get certificates list for fallback');
            }
            
            const certificates = certificatesResponse.data.certificates || [];
            
            if (certificates.length === 0) {
                return {
                    success: true,
                    agreements: [],
                    statistics: {
                        total_agreements: 0,
                        total_with_certificates: 0,
                        total_certificates: 0,
                        completion_rate: 0,
                        unique_projects: 0
                    },
                    monthly_breakdown: [],
                    method: 'fallback_empty'
                };
            }
            
            // Extract unique months from certificates
            const uniqueMonths = [...new Set(certificates.map(cert => {
                if (cert.month && cert.month !== 'Unknown') {
                    return cert.month;
                }
                return null;
            }))].filter(Boolean);
            
            console.log('[LOAMS] Found months for aggregation:', uniqueMonths);
            
            // Create basic report from certificate data
            const projectGroups = {};
            
            certificates.forEach(cert => {
                const project = cert.clusterName || 'Unknown';
                const agreement = cert.agreementNo || 'Unknown';
                const month = cert.month || 'Unknown';
                
                const key = `${project}-${agreement}`;
                if (!projectGroups[key]) {
                    projectGroups[key] = {
                        project,
                        agreement_ref_no: agreement,
                        service_type: 'General Service',
                        month,
                        certificate_uploaded: true,
                        upload_date: cert.last_modified,
                        status: 'Uploaded'
                    };
                }
            });
            
            const agreements = Object.values(projectGroups);
            const uniqueProjects = new Set(agreements.map(a => a.project));
            
            return {
                success: true,
                agreements,
                statistics: {
                    total_agreements: agreements.length,
                    total_with_certificates: agreements.length,
                    total_certificates: certificates.length,
                    completion_rate: 100,
                    unique_projects: uniqueProjects.size
                },
                monthly_breakdown: [],
                method: 'certificate_based_fallback'
            };
            
        } catch (error) {
            console.error('[LOAMS] Enhanced fallback all months report error:', error);
            throw new Error(`Failed to generate all months report: ${error.message}`);
        }
    },

    // 🔥 NEW: Debug CC-LOAMS mapping
    debugCCLoamsMapping: async () => {
        console.log('[LOAMS] 🔍 Debugging CC-LOAMS certificate mapping...');
        try {
            const response = await pythonApi.get('/api/certificates/debug-cc-loams', {
                headers: {
                    'Accept': 'application/json'
                },
                timeout: 30000
            });
            
            if (!response.data || !response.data.success) {
                throw new Error('Failed to get debug information');
            }
            
            console.log('[LOAMS] 🔍 CC-LOAMS Debug Information:', response.data.debug_info);
            return response.data;
        } catch (error) {
            console.error('[LOAMS] ❌ Debug CC-LOAMS mapping error:', error);
            throw error;
        }
    },

    // 🔥 NEW: Get certificate details
    getCertificateDetails: async (certificateKey) => {
        console.log('[LOAMS] 🔍 Getting certificate details for:', certificateKey);
        try {
            if (!certificateKey) {
                throw new Error('Certificate key is required');
            }
            
            const encodedKey = encodeURIComponent(certificateKey);
            const response = await pythonApi.get(`/api/certificate/details/${encodedKey}`, {
                timeout: 30000,
                headers: {
                    'Accept': 'application/json'
                }
            });
            
            if (!response.data || !response.data.success) {
                throw new Error('Failed to get certificate details');
            }
            
            console.log('[LOAMS] ✅ Certificate details retrieved successfully');
            return response.data.certificate;
        } catch (error) {
            console.error('[LOAMS] ❌ Get certificate details error:', error);
            throw error;
        }
    },


    getAllMonthsReportByYear: async (year = '2025') => {
    console.log('[LOAMS] 🔍 PROPER: Fetching invoice details report for year:', year);
    try {
        const response = await pythonApi.get('/api/certificates/report/all-months', {
            params: { year },
            headers: {
                'Accept': 'application/json'
            },
            timeout: 120000 // 2 minute timeout
        });
        
        if (!response.data) {
            throw new Error('No data received from server');
        }
        
        if (!response.data.success) {
            throw new Error(response.data.error || 'Failed to load invoice details report');
        }
        
        console.log('[LOAMS] ✅ PROPER: Invoice details report loaded successfully:', {
            totalAgreements: response.data.statistics?.total_agreements || 0,
            totalCertificates: response.data.statistics?.total_certificates || 0,
            completionRate: response.data.statistics?.completion_rate || 0,
            year: response.data.year
        });
        
        return response.data;
        
    } catch (error) {
        console.error('[LOAMS] ❌ PROPER: Invoice details report error:', error);
        throw error;
    }
},

    // 🔥 HELPER: Calculate certificate statistics
    calculateStats: (certificates) => {
        const stats = {
            total: certificates.length,
            original: certificates.filter(c => c.certificate_type === 'original' || c.isOriginal).length,
            ccLoams: certificates.filter(c => c.certificate_type === 'cc_loams' || c.isCCLoams).length,
            withValidCluster: certificates.filter(c => c.clusterName && c.clusterName !== 'Unknown').length,
            withValidAgreement: certificates.filter(c => c.agreementNo && c.agreementNo !== 'Unknown').length,
            withValidMonth: certificates.filter(c => c.month && c.month !== 'Unknown').length,
            byCluster: {},
            byMonth: {},
            byType: {}
        };

        // Group by cluster
        certificates.forEach(cert => {
            const cluster = cert.clusterName || 'Unknown';
            if (!stats.byCluster[cluster]) {
                stats.byCluster[cluster] = { total: 0, original: 0, ccLoams: 0 };
            }
            stats.byCluster[cluster].total++;
            if (cert.certificate_type === 'original' || cert.isOriginal) {
                stats.byCluster[cluster].original++;
            } else {
                stats.byCluster[cluster].ccLoams++;
            }
        });

        // Group by month
        certificates.forEach(cert => {
            const month = cert.month || 'Unknown';
            if (!stats.byMonth[month]) {
                stats.byMonth[month] = { total: 0, original: 0, ccLoams: 0 };
            }
            stats.byMonth[month].total++;
            if (cert.certificate_type === 'original' || cert.isOriginal) {
                stats.byMonth[month].original++;
            } else {
                stats.byMonth[month].ccLoams++;
            }
        });

        // Group by type
        certificates.forEach(cert => {
            const type = cert.certificate_type || (cert.isOriginal ? 'original' : 'cc_loams');
            stats.byType[type] = (stats.byType[type] || 0) + 1;
        });

        return stats;
    },



    getMissingCertificatesReport: async (year = '2025') => {
    console.log('[LOAMS] 🔍 MISSING: Getting missing certificates report for:', year);
    try {
        const response = await pythonApi.get('/api/certificates/report/missing-certificates', {
            params: { year },
            headers: {
                'Accept': 'application/json'
            },
            timeout: 60000 // 1 minute timeout
        });
        
        if (!response.data) {
            throw new Error('No response data received');
        }
        
        if (!response.data.success) {
            throw new Error(response.data.error || 'Failed to get missing certificates report');
        }
        
        console.log('[LOAMS] ✅ MISSING: Report loaded successfully:', {
            total: response.data.statistics.total_invoices_with_cc,
            missing: response.data.statistics.missing_certificates,
            present: response.data.statistics.present_certificates
        });
        
        return response.data;
        
    } catch (error) {
        console.error('[LOAMS] ❌ MISSING: Report error:', error);
        throw error;
    }
},

checkCertificateFileExists: async (serialNumber) => {
    console.log('[LOAMS] 🔍 CHECK: Checking file exists for:', serialNumber);
    try {
        const response = await pythonApi.get(`/api/certificates/check-file-exists/${serialNumber}`, {
            timeout: 10000
        });
        
        return response.data;
    } catch (error) {
        console.error('[LOAMS] ❌ CHECK: File check error:', error);
        throw error;
    }
},

bulkCheckCertificateFiles: async (serialNumbers) => {
    console.log('[LOAMS] 🔍 BULK CHECK: Checking files for:', serialNumbers.length, 'certificates');
    try {
        const response = await pythonApi.post('/api/certificates/bulk-check-files', {
            serial_numbers: serialNumbers
        }, {
            timeout: 30000
        });
        
        return response.data;
    } catch (error) {
        console.error('[LOAMS] ❌ BULK CHECK: Error:', error);
        throw error;
    }
},

    // 🔥 HELPER: Validate certificate data integrity
    validateCertificateData: (certificates) => {
        console.log('[LOAMS] 🔍 Validating certificate data integrity...');
        
        const validation = {
            totalCertificates: certificates.length,
            validCertificates: 0,
            issuesFound: [],
            typeDistribution: {},
            clusterIssues: 0,
            agreementIssues: 0,
            monthIssues: 0
        };

        certificates.forEach((cert, index) => {
            let isValid = true;
            const issues = [];

            // Check certificate type
            if (!cert.certificate_type || !['original', 'cc_loams'].includes(cert.certificate_type)) {
                issues.push('Invalid or missing certificate_type');
                isValid = false;
            }

            // Track type distribution
            const type = cert.certificate_type || 'unknown';
            validation.typeDistribution[type] = (validation.typeDistribution[type] || 0) + 1;

            // Check cluster information
            if (!cert.clusterName || cert.clusterName === 'Unknown') {
                issues.push('Missing or unknown cluster name');
                validation.clusterIssues++;
                isValid = false;
            }

            // Check agreement information
            if (!cert.agreementNo || cert.agreementNo === 'Unknown') {
                issues.push('Missing or unknown agreement number');
                validation.agreementIssues++;
                isValid = false;
            }

            // Check month information
            if (!cert.month || cert.month === 'Unknown') {
                issues.push('Missing or unknown month');
                validation.monthIssues++;
                isValid = false;
            }

            // Check CC-LOAMS specific requirements
            if (cert.certificate_type === 'cc_loams' || cert.isCCLoams) {
                if (!cert.invoice_number || cert.invoice_number === 'Unknown') {
                    issues.push('CC-LOAMS certificate missing invoice number');
                    isValid = false;
                }
            }

            if (isValid) {
                validation.validCertificates++;
            } else {
                validation.issuesFound.push({
                    index,
                    filename: cert.filename,
                    key: cert.key,
                    issues
                });
            }
        });

        const validationRate = certificates.length > 0 
            ? (validation.validCertificates / certificates.length * 100).toFixed(2)
            : 0;

        console.log(`[LOAMS] 📊 Certificate validation complete: ${validation.validCertificates}/${certificates.length} (${validationRate}%) valid`);
        console.log(`[LOAMS] 📊 Issues: Cluster=${validation.clusterIssues}, Agreement=${validation.agreementIssues}, Month=${validation.monthIssues}`);

        return validation;
    },

    // 🔥 NEW: Refresh certificate cache
    refreshCache: async () => {
        console.log('[LOAMS] 🔄 Refreshing certificate cache...');
        try {
            // Clear any client-side cache
            if (typeof window !== 'undefined' && window.localStorage) {
                const keys = Object.keys(window.localStorage);
                keys.forEach(key => {
                    if (key.startsWith('certificates_') || key.startsWith('cert_cache_')) {
                        window.localStorage.removeItem(key);
                    }
                });
            }

            // Fetch fresh data
            const result = await loamsApi.enhancedCertificates.getAllCertificates();
            console.log('[LOAMS] ✅ Certificate cache refreshed successfully');
            return result;
        } catch (error) {
            console.error('[LOAMS] ❌ Certificate cache refresh error:', error);
            throw error;
        }
    },

    // 🔥 HELPER: Format file size
    formatFileSize: (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    // 🔥 HELPER: Format month label
    formatMonthLabel: (monthStr) => {
        try {
            if (!monthStr || monthStr === 'Unknown') {
                return 'Unknown';
            }
            
            // Extract YYYY-MM if longer
            let month = String(monthStr).trim();
            if (month.includes('T')) {
                month = month.substring(0, 7);
            } else if (month.includes(' ')) {
                month = month.substring(0, 7);
            }
            
            // Validate and format
            if (/^\d{4}-\d{2}$/.test(month)) {
                const [year, monthNum] = month.split('-');
                const date = new Date(parseInt(year), parseInt(monthNum) - 1);
                return `${date.toLocaleString('default', { month: 'short' })} ${year}`;
            }
            
            return monthStr;
        } catch (error) {
            console.error('[LOAMS] ❌ FIXED: Month formatting error:', error);
            return monthStr;
        }
    },


    testDownloadUrl: async (certificateKey) => {
        console.log('[LOAMS] 🔍 FIXED: Testing download URL for:', certificateKey);
        
        try {
            const previewData = await loamsApi.enhancedCertificates.getEnhancedPreviewUrl(certificateKey);
            
            if (!previewData || !previewData.url) {
                throw new Error('No URL received');
            }
            
            // Test the URL with a HEAD request
            const response = await fetch(previewData.url, {
                method: 'HEAD',
                mode: 'cors',
                cache: 'no-cache'
            });
            
            console.log('[LOAMS] 🔍 FIXED: URL test response:', {
                status: response.status,
                statusText: response.statusText,
                contentType: response.headers.get('content-type'),
                contentLength: response.headers.get('content-length'),
                url: previewData.url.substring(0, 100) + '...'
            });
            
            return {
                success: response.ok,
                status: response.status,
                statusText: response.statusText,
                contentType: response.headers.get('content-type'),
                contentLength: response.headers.get('content-length')
            };
            
        } catch (error) {
            console.error('[LOAMS] ❌ FIXED: URL test error:', error);
            return {
                success: false,
                error: error.message
            };
        }
    },

    

    

    

    // 🔥 HELPER: Filter certificates by type
    filterByType: (certificates, type) => {
        if (type === 'all') return certificates;
        return certificates.filter(cert => cert.certificate_type === type);
    },

    // 🔥 HELPER: Get certificate statistics
    getCertificateStats: (certificates) => {
        const stats = {
            total: certificates.length,
            original: certificates.filter(c => c.certificate_type === 'original').length,
            ccLoams: certificates.filter(c => c.certificate_type === 'cc_loams').length,
            byCluster: {},
            byMonth: {}
        };

        // Group by cluster
        certificates.forEach(cert => {
            const cluster = cert.clusterName || 'Unknown';
            if (!stats.byCluster[cluster]) {
                stats.byCluster[cluster] = { total: 0, original: 0, ccLoams: 0 };
            }
            stats.byCluster[cluster].total++;
            if (cert.certificate_type === 'original') {
                stats.byCluster[cluster].original++;
            } else {
                stats.byCluster[cluster].ccLoams++;
            }
        });

        // Group by month
        certificates.forEach(cert => {
            const month = cert.month || 'Unknown';
            if (!stats.byMonth[month]) {
                stats.byMonth[month] = { total: 0, original: 0, ccLoams: 0 };
            }
            stats.byMonth[month].total++;
            if (cert.certificate_type === 'original') {
                stats.byMonth[month].original++;
            } else {
                stats.byMonth[month].ccLoams++;
            }
        });

        return stats;
    }
},

// 🔥 HELPER: Global month formatting function (add outside loamsApi object)




















    // In api.js, update the certificates object
    certificates: {


      formatFileSize: (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },




    




      getUserCertificates: async () => {
      try {
        console.log('[CERT] Fetching user certificates from localStorage');
        
        // Get certificates from localStorage (fallback)
        const stored = localStorage.getItem('downloaded-certificates');
        if (stored) {
          const certificates = JSON.parse(stored);
          console.log('[CERT] Found stored certificates:', certificates.length);
          return certificates;
        }
        
        // Alternative: Get recent certificates from API
        try {
          const response = await loamsApi.certificateService.list();
          if (response.data && response.data.certificates) {
            console.log('[CERT] Fetched certificates from API:', response.data.certificates.length);
            return response.data.certificates.slice(0, 10); // Return last 10
          }
        } catch (apiError) {
          console.warn('[CERT] API fetch failed, using localStorage fallback:', apiError);
        }
        
        return [];
      } catch (error) {
        console.error('[CERT] Error fetching user certificates:', error);
        return [];
      }
    },

    list: async (cluster, agreementRef) => {
    console.log('[LOAMS] 🔍 Basic certificate list (fallback):', { cluster, agreementRef });
    
    // Input validation
    if (!cluster || !agreementRef) {
        throw new Error('Both cluster and agreement reference are required');
    }
    
    try {
        // Clean parameters
        const cleanCluster = cluster.trim();
        const cleanAgreementRef = agreementRef.trim();
        
        console.log('[LOAMS] 🔍 Using BASIC endpoint as fallback:', {
            cluster: cleanCluster,
            agreement: cleanAgreementRef
        });
        
        // 🔥 Call the BASIC endpoint, not the enhanced one
        const response = await pythonApi.get('/api/certificate/list', {
            params: {
                cluster: cleanCluster,
                agreement_ref: cleanAgreementRef
            },
            timeout: 60000,
            headers: {
                'Accept': 'application/json'
            }
        });
        
        // Validate response structure
        if (!response.data) {
            throw new Error('No response data received from server');
        }
        
        console.log('[LOAMS] ✅ Basic endpoint response:', {
            success: response.data?.success,
            certificateCount: response.data?.certificates?.length || 0
        });
        
        return response;
    } catch (error) {
        console.error('[LOAMS] ❌ Basic certificate list error:', {
            message: error.message,
            status: error.response?.status,
            cluster: cluster,
            agreementRef: agreementRef
        });
        
        // Handle 404 gracefully for the basic endpoint too
        if (error.response?.status === 404) {
            console.log('[LOAMS] 🔍 No certificates found via basic endpoint (404)');
            return {
                data: {
                    success: true,
                    certificates: [],
                    count: 0,
                    message: 'No certificates found for this PO'
                }
            };
        }
        
        throw error;
    }
},


    listEnhanced: async (cluster, agreementRef) => {
    console.log('[LOAMS] 🔍 Enhanced certificate search:', { cluster, agreementRef });
    
    // Input validation
    if (!cluster || !agreementRef) {
      throw new Error('Both cluster and agreement reference are required');
    }
    
    try {
      // Clean parameters
      const cleanCluster = cluster.trim();
      const cleanAgreementRef = agreementRef.trim();
      
      console.log('[LOAMS] 🔍 Searching with parameters:', {
        originalCluster: cluster,
        cleanCluster: cleanCluster,
        originalAgreement: agreementRef,
        cleanAgreement: cleanAgreementRef
      });
      
      // 🔥 FIX: Use the correct endpoint
      const response = await pythonApi.get('/api/certificate/list-enhanced', {
        params: {
          cluster: cleanCluster,
          agreement_ref: cleanAgreementRef
        },
        timeout: 60000, // Increased timeout for comprehensive search
        headers: {
          'Accept': 'application/json'
        }
      });
      
      // Validate response structure
      if (!response.data) {
        throw new Error('No response data received from server');
      }
      
      console.log('[LOAMS] 🎯 Enhanced search response:', {
        success: response.data?.success,
        certificateCount: response.data?.certificates?.length || 0,
        formats: response.data?.formats_found || {},
        summary: response.data?.summary || {}
      });
      
      return response;
    } catch (error) {
      console.error('[LOAMS] ❌ Enhanced certificate search error:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        cluster: cluster,
        agreementRef: agreementRef
      });
      
      // Enhanced error handling with specific messages
      if (error.response?.status === 404) {
        console.log('[LOAMS] 🔍 No certificates found (404) - this is normal for new POs');
        return {
          data: {
            success: true,
            certificates: [],
            count: 0,
            formats_found: {},
            summary: {
              total_certificates: 0,
              new_format: 0,
              old_format: 0,
              with_invoices: 0
            },
            message: 'No certificates found for this PO'
          }
        };
      }
      
      if (error.response?.status === 500) {
        throw new Error('Server error during enhanced certificate search. Please try again.');
      }
      
      if (error.code === 'ECONNABORTED') {
        throw new Error('Enhanced search timed out. Please try again.');
      }
      
      throw error;
    }
  },

    // Fallback to original list method if enhanced fails
    listWithFallback: async (cluster, agreementRef) => {
        console.log('[LOAMS] 🔄 Attempting enhanced search with fallback');
        
        try {
            // Try enhanced search first
            return await loamsApi.certificates.listEnhanced(cluster, agreementRef);
        } catch (enhancedError) {
            console.warn('[LOAMS] ⚠️ Enhanced search failed, falling back to original:', enhancedError.message);
            
            try {
                // Fallback to original method
                return await loamsApi.certificates.list(cluster, agreementRef);
            } catch (fallbackError) {
                console.error('[LOAMS] ❌ Both enhanced and fallback searches failed:', fallbackError.message);
                throw fallbackError;
            }
        }
    },

    // Debug cluster mapping
    debugClusterMapping: async (cluster) => {
        console.log('[LOAMS] 🔍 Debugging cluster mapping for:', cluster);
        
        try {
            const response = await pythonApi.get('/api/certificate/cluster-mapping', {
                params: { cluster },
                headers: { 'Accept': 'application/json' }
            });
            
            console.log('[LOAMS] 🔍 Cluster mapping debug:', response.data);
            return response.data;
        } catch (error) {
            console.error('[LOAMS] ❌ Cluster mapping debug failed:', error);
            throw error;
        }
    },


    upload: async (formData) => {
        console.log('[LOAMS] Uploading certificate');
        try {
            const response = await pythonApi.post('/api/certificate/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                timeout: 60000
            });
            return response;
        } catch (error) {
            console.error('[LOAMS] Certificate upload failed:', error);
            throw error;
        }
    },


        generatePdf: async (data) => {
            console.log('[LOAMS] Generating completion certificate:', data);
            try {
                const response = await pythonApi.post('/api/certificate/generate-pdf', data, {
                    timeout: 600000, // 10-minute timeout for PDF/ZIP generation
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    responseType: data.multiple ? 'blob' : 'json', // ZIP for multiple, JSON for single
                    retry: 3, // Allow 3 retries for network/server errors
                    retryDelay: 1000 // 1-second delay between retries
                });
    
                if (data.multiple) {
                    // Handle ZIP download
                    const blob = new Blob([response.data], { type: 'application/zip' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = response.headers['content-disposition']?.split('filename=')[1] || 'completion_certificates.zip';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                } else {
                    return response.data; // Return URL for single certificate
                }
    
                return { success: true };
            } catch (error) {
                console.error('[LOAMS] Certificate generation error:', {
                    message: error.message,
                    status: error.response?.status,
                    data: error.response?.data,
                    config: error.config,
                    request: error.request
                });
    
                // Enhanced error handling for 500
                if (error.response?.status === 500) {
                    throw new Error(`Server error (500): ${error.response.data?.error || 'Internal server error'}`);
                }
    
                throw error;
            }
        },
    
        getMonthAgreements: async (month) => {
            console.log('[LOAMS] Fetching agreements for month:', month);
            try {
              const response = await pythonApi.get('/api/certificate/month-agreements', {
                params: { month }
              });
              return response;
            } catch (error) {
              console.error('[LOAMS] Get month agreements error:', error);
              throw error;
            }
        },
    
        generate: async (data) => {
            console.log('[LOAMS] Generating completion certificate:', data);
            try {
              const response = await pythonApi.post('/api/certificate/generate', data);
              return response;
            } catch (error) {
              console.error('[LOAMS] Certificate generation error:', error);
              throw error;
            }
        },

        
        
        download: async (certificateData) => {
            try {
              const response = await pythonApi.post('/api/certificate/download', 
                certificateData,
                { responseType: 'blob' }
              );
        
              // Create download
              const blob = new Blob([response.data], { type: 'application/pdf' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `CC_${certificateData.month}.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
        
              return { success: true };
            } catch (error) {
              console.error('[LOAMS] Certificate download error:', error);
              throw error;
            }
        },
    
        // Method to handle bulk certificate generation
        generateBatch: async (monthData) => {
            console.log('[LOAMS] Generating batch certificates for month:', monthData);
            try {
                const response = await pythonApi.post('/api/certificate/batch-generate', 
                    monthData,
                    { 
                        responseType: monthData.download_zip ? 'blob' : 'json',
                        timeout: 600000  // 10-minute timeout for batch processing
                    }
                );
    
                // If requesting ZIP download
                if (monthData.download_zip) {
                    const blob = new Blob([response.data], { type: 'application/zip' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `certificates_${monthData.month}.zip`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
    
                    return { success: true };
                }
    
                return response;
            } catch (error) {
                console.error('[LOAMS] Batch certificate generation error:', error);
                throw error;
            }
        },
    
        // Method to fetch service types for filtering
        getServiceTypes: async () => {
            console.log('[LOAMS] Fetching service types');
            try {
                const response = await pythonApi.get('/api/service-types');
                return response;
            } catch (error) {
                console.error('[LOAMS] Service types fetch error:', error);
                throw error;
            }
        },


        getHealth: async () => {
    try {
        console.log('[LOAMS] 🔍 Checking service health...');
        const response = await pythonApi.get('/health', {
            timeout: 10000,  // 10 second timeout
            retry: 1,        // Only 1 retry for health check
            retryDelay: 2000 // 2 second delay
        });

        if (!response.data) {
            throw new Error('No response data received');
        }

        console.log('[LOAMS] ✅ Health check successful:', response.data);
        return response;
    } catch (error) {
        console.error('[LOAMS] ❌ Health check failed:', error);
        
        // Don't throw error for health check failures, just return status
        return {
            data: {
                status: 'unhealthy',
                error: error.message,
                service: 'loams-backend'
            }
        };
    }
},



        



        // 🔧 FIX 1: Add the missing formatFileSize function to your api.js

// Add this helper function at the top of your certificates object in api.js:
certificates: {
    // 🔧 MISSING FUNCTION - Add this at the beginning of certificates object
    formatFileSize: (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    // 🔥 FIXED: Enhanced certificate listing
    listEnhanced: async (cluster, agreementRef) => {
    console.log('[LOAMS] 🔍 Enhanced certificate search:', { cluster, agreementRef });
    
    // Input validation
    if (!cluster || !agreementRef) {
      throw new Error('Both cluster and agreement reference are required');
    }
    
    try {
      // Clean parameters
      const cleanCluster = cluster.trim();
      const cleanAgreementRef = agreementRef.trim();
      
      console.log('[LOAMS] 🔍 Searching with enhanced mapping:', {
        originalCluster: cluster,
        cleanCluster: cleanCluster,
        originalAgreement: agreementRef,
        cleanAgreement: cleanAgreementRef
      });
      
      const response = await pythonApi.get('/api/certificate/list-enhanced', {
        params: {
          cluster: cleanCluster,
          agreement_ref: cleanAgreementRef
        },
        timeout: 60000, // Increased timeout for comprehensive search
        headers: {
          'Accept': 'application/json'
        }
      });
      
      // Validate response structure
      if (!response.data) {
        throw new Error('No response data received from server');
      }
      
      console.log('[LOAMS] 🎯 Enhanced search response:', {
        success: response.data?.success,
        certificateCount: response.data?.certificates?.length || 0,
        formats: response.data?.formats_found || [],
        strategies: response.data?.successful_strategies || [],
        summary: response.data?.summary || {}
      });
      
      // Transform certificates for consistent frontend usage
      if (response.data.success && response.data.certificates) {
        const transformedCertificates = response.data.certificates.map((cert, index) => {
          const transformedCert = {
            ...cert,
            id: cert.id || `enhanced_cert_${index}`,
            displayName: cert.displayName || cert.name || `Certificate ${index + 1}`,
            cluster: cert.cluster || cleanCluster,
            agreement: cert.agreement || cleanAgreementRef,
            month: cert.month || 'Unknown',
            format: cert.format || 'unknown',
            
            // Enhanced metadata
            size: cert.size || 0,
            sizeFormatted: cert.size ? formatFileSize(cert.size) : 'Unknown',
            lastModified: cert.last_modified || null,
            lastModifiedFormatted: cert.last_modified ? 
              new Date(cert.last_modified).toLocaleDateString('en-AE', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'Asia/Dubai'
              }) : 'Unknown',
            
            // Invoice information
            hasInvoice: cert.hasInvoice || false,
            invoiceNumber: cert.invoiceNumber || null,
            invoiceAmount: cert.invoiceAmount || null,
            
            // Search debug info (helpful for troubleshooting)
            searchStrategy: cert.search_strategy || null,
            clusterVariation: cert.cluster_variation || null,
            agreementVariation: cert.agreement_variation || null
          };
          
          console.log('[LOAMS] 📄 Processed certificate:', {
            key: transformedCert.key,
            month: transformedCert.month,
            format: transformedCert.format,
            hasInvoice: transformedCert.hasInvoice,
            strategy: transformedCert.searchStrategy
          });
          
          return transformedCert;
        });
        
        // Sort certificates by month (newest first)
        transformedCertificates.sort((a, b) => {
          if (a.month === 'Unknown') return 1;
          if (b.month === 'Unknown') return -1;
          return b.month.localeCompare(a.month);
        });
        
        // Update response with transformed data
        response.data.certificates = transformedCertificates;
        
        console.log('[LOAMS] ✅ Enhanced certificate transformation complete:', {
          totalProcessed: transformedCertificates.length,
          monthsFound: transformedCertificates.map(c => c.month).filter(m => m !== 'Unknown'),
          formatsFound: [...new Set(transformedCertificates.map(c => c.format))]
        });
      }
      
      return response;
    } catch (error) {
      console.error('[LOAMS] ❌ Enhanced certificate search error:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        cluster: cluster,
        agreementRef: agreementRef
      });
      
      // Enhanced error handling with specific messages
      if (error.response?.status === 404) {
        console.log('[LOAMS] 🔍 No certificates found (404) - this is normal for new POs');
        return {
          data: {
            success: true,
            certificates: [],
            count: 0,
            formats_found: [],
            successful_strategies: [],
            summary: {
              total_certificates: 0,
              new_format: 0,
              old_format: 0,
              with_invoices: 0
            },
            search_debug: {
              strategies_tried: [],
              matches_found: [],
              cluster_variations: []
            },
            message: 'No certificates found for this PO'
          }
        };
      }
      
      if (error.response?.status === 500) {
        throw new Error('Server error during enhanced certificate search. Please try again.');
      }
      
      if (error.code === 'ECONNABORTED') {
        throw new Error('Enhanced search timed out. Please try again.');
      }
      
      throw error;
    }
  }
  } ,

    // 🔥 NEW: Dedicated method for checking if certificates exist for a specific month
    checkMonthExists: async (cluster, agreementRef, month) => {
        console.log('[LOAMS] 🔍 Checking if certificate exists for month:', { cluster, agreementRef, month });
        
        try {
            const response = await loamsApi.certificates.listEnhanced(cluster, agreementRef);

            if (response.data?.success && response.data.certificates) {
                const existingCert = response.data.certificates.find(cert => cert.month === month);
                
                const result = {
                    exists: !!existingCert,
                    certificate: existingCert || null,
                    format: existingCert?.format || null,
                    hasInvoice: existingCert?.hasInvoice || false,
                    allMonths: response.data.certificates.map(cert => cert.month).filter(m => m !== 'Unknown')
                };
                
                console.log('[LOAMS] 📅 Month existence check result:', result);
                return result;
            }
            
            return {
                exists: false,
                certificate: null,
                format: null,
                hasInvoice: false,
                allMonths: []
            };
        } catch (error) {
            console.error('[LOAMS] ❌ Month existence check error:', error);
            throw error;
        }
    },

    lists: async () => {
        console.log('[LOAMS] Fetching all certificates');
        try {
            const response = await pythonApi.get('/api/certificates/all');
            console.log('[LOAMS] Raw response:', response);
            
            if (!response.data) {
                throw new Error('No data received from server');
            }

            return response.data;
        } catch (error) {
            console.error('[LOAMS] Certificate list error:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data
            });
            throw error;
        }
    },
    
       getPreviewUrl: async (certificateKey) => {
    console.log('[LOAMS] 🔍 Generating preview URL for:', certificateKey);
    
    if (!certificateKey) {
      throw new Error('Certificate key is required');
    }
    
    try {
      // Ensure proper encoding of the key
      const encodedKey = encodeURIComponent(certificateKey);
      
      const response = await pythonApi.get(`/api/certificate/preview/${encodedKey}`, {
        timeout: 20000, // 20 second timeout
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.data || !response.data.url) {
        throw new Error('Invalid preview URL response from server');
      }
      
      console.log('[LOAMS] ✅ Preview URL generated successfully');
      return response.data.url;
    } catch (error) {
      console.error('[LOAMS] ❌ Preview URL generation error:', {
        message: error.message,
        status: error.response?.status,
        certificateKey: certificateKey
      });
      
      if (error.response?.status === 404) {
        throw new Error('Certificate not found on server');
      }
      
      if (error.response?.status === 403) {
        throw new Error('Access denied to certificate');
      }
      
      throw new Error(`Failed to generate preview: ${error.message}`);
    }
  },
    
        deleteMultiple: async (keys) => {
            console.log('[LOAMS] Deleting certificates:', keys);
            try {
                const response = await pythonApi.post('/api/certificates/delete', { keys });
                return response.data;
            } catch (error) {
                console.error('[LOAMS] Certificate deletion error:', error);
                throw error;
            }
        },

        
    
        downloadMultiple: async (keys) => {
            console.log('[LOAMS] Downloading multiple certificates:', keys);
            try {
                const response = await pythonApi.post('/api/certificates/download-batch', 
                    { keys },
                    { 
                        responseType: 'blob',
                        timeout: 300000, // 5 minutes timeout for large downloads
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/zip'
                        }
                    }
                );
                
                console.log('[LOAMS] Successfully downloaded certificates');
                return response;
            } catch (error) {
                console.error('[LOAMS] Batch download error:', error);
                
                if (error.response?.data) {
                    try {
                        const text = await error.response.data.text();
                        const errorData = JSON.parse(text);
                        throw new Error(errorData.error || 'Download failed');
                    } catch (e) {
                        throw new Error('Failed to download certificates');
                    }
                }
                
                throw error;
            }
        },

        

        



        
        
          



        validateMonth: async (params) => {
        console.log('[LOAMS] 🔍 Enhanced month validation:', params);
        
        if (!params.cluster || !params.agreement_ref || !params.month) {
            throw new Error('Missing required parameters for validation');
        }
        
        try {
            const response = await pythonApi.post('/api/certificate/validate-month', {
                cluster: params.cluster.trim(),
                agreement_ref: params.agreement_ref.trim(),
                month: params.month.trim()
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                timeout: 20000
            });
            
            console.log('[LOAMS] ✅ Enhanced validation response:', response.data);
            return response;
        } catch (error) {
            console.error('[LOAMS] ❌ Enhanced validation error:', error);
            
            // Enhanced error handling for validation
            if (error.response?.data?.sequential_validation_failed) {
                const enhancedError = new Error(error.response.data.error || 'Sequential validation failed');
                enhancedError.response = error.response;
                enhancedError.sequential_validation_failed = true;
                enhancedError.missing_month = error.response.data.missing_month;
                throw enhancedError;
            }
            
            if (error.response?.data?.certificate_exists) {
                const enhancedError = new Error('Certificate already exists for this month');
                enhancedError.response = error.response;
                enhancedError.certificate_exists = true;
                enhancedError.existing_format = error.response.data.existing_format;
                throw enhancedError;
            }
            
            throw error;
        }
    },

       checkExists: async (cluster, agreementRef, month) => {
  console.log('[LOAMS] 🔍 Enhanced existence check:', { cluster, agreementRef, month });
  try {
    // 🔥 FIX: Use listEnhanced instead of list (which doesn't exist)
    const response = await loamsApi.certificates.listEnhanced(cluster, agreementRef);
    
    if (response.data?.success && response.data.certificates) {
      const certificates = response.data.certificates;
      const existingCert = certificates.find(cert => cert.month === month);
      
      return {
        exists: !!existingCert,
        certificate: existingCert || null,
        format: existingCert?.format || null,
        key: existingCert?.key || null,
        total_certificates: certificates.length,
        all_months: certificates.map(cert => cert.month)
      };
    }
    
    return {
      exists: false,
      certificate: null,
      total_certificates: 0,
      all_months: []
    };
  } catch (error) {
    console.error('[LOAMS] ❌ Enhanced existence check error:', error);
    throw error;
  }
},
    
        requestApproval: async (data) => {
            console.log('[LOAMS] Requesting certificate approval:', data);
            try {
                const response = await pythonApi.post('/api/certificate/request-approval', data);
                console.log('[LOAMS] Approval request submitted:', response.data);
                return response;
            } catch (error) {
                console.error('[LOAMS] Approval request error:', error);
                throw error;
            }
        },
    
        getApprovalStatus: async (cluster, agreementRef, month) => {
            console.log('[LOAMS] Checking approval status:', { cluster, agreementRef, month });
            try {
                const response = await pythonApi.get('/api/certificate/check', {
                    params: {
                        cluster,
                        agreement_ref: agreementRef,
                        month
                    }
                });
                return response.data;
            } catch (error) {
                console.error('[LOAMS] Approval status check error:', error);
                throw error;
            }
        },
    
        // Added the new methods INSIDE the certificates object
        getReport: async (month) => {
    console.log('[LOAMS] Fetching certificate report for month:', month);
    try {
        // Handle all months case
        if (month === 'all-months') {
            return await loamsApi.certificates.getAllMonthsReport();
        }
        
        // Validate month format for single month
        if (!month || !month.match(/^\d{4}-\d{2}$/)) {
            throw new Error('Invalid month format. Please use YYYY-MM format.');
        }
        
        const response = await pythonApi.get('/api/certificates/report', {
            params: { month },
            headers: {
                'Accept': 'application/json'
            },
            timeout: 30000
        });
        
        if (!response.data) {
            throw new Error('No data received from server');
        }
        
        if (!response.data.success) {
            throw new Error(response.data.error || 'Failed to load report');
        }
        
        console.log('[LOAMS] Single month report loaded successfully:', {
            month,
            totalAgreements: response.data.statistics?.total_agreements || 0,
            uploadedCertificates: response.data.statistics?.total_with_certificates || 0
        });
        
        return response.data;
    } catch (error) {
        console.error('[LOAMS] Certificate report error:', error);
        throw error;
    }
},

exportAllMonthsReport: async (reportData) => {
    console.log('[LOAMS] Exporting all months report to Excel');
    try {
        if (!reportData || !reportData.agreements) {
            throw new Error('No report data available for export');
        }
        
        // Import XLSX if needed
        const XLSX = window.XLSX || await import('xlsx').then(module => module.default);
        if (!XLSX) {
            throw new Error('XLSX library not available');
        }
        
        const workbook = XLSX.utils.book_new();
        
        // Create main report data
        const excelData = reportData.agreements.map(agreement => ({
            'Project': agreement.project,
            'Agreement Ref': agreement.agreement_ref_no,
            'Service Type': agreement.service_type || 'General Service',
            'Month': agreement.monthLabel || agreement.month || 'N/A',
            'Contract Start': new Date(agreement.contract_start_date).toLocaleDateString(),
            'Contract End': new Date(agreement.contract_finish_date).toLocaleDateString(),
            'Duration (Months)': agreement.duration,
            'Upload Date': agreement.upload_date ? new Date(agreement.upload_date).toLocaleDateString() : '-',
            'Certificate Status': agreement.certificate_uploaded ? 'Uploaded' : 'Missing'
        }));
        
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        
        // Create summary sheet
        const stats = reportData.statistics;
        const statsData = [
            ['Report Period', 'All Months Combined'],
            ['Generated On', new Date().toLocaleDateString()],
            [''],
            ['Overall Summary'],
            ['Total Service Agreements', stats.total_agreements || 0],
            ['Certificates Uploaded', stats.total_with_certificates || 0],
            ['Total Certificates', stats.total_certificates || 0],
            ['Completion Rate', `${stats.completion_rate || 0}%`],
            ['Unique Projects', stats.unique_projects || 0],
            [''],
            ['Monthly Breakdown'],
            ['Month', 'Total Agreements', 'Uploaded', 'Completion %'],
            ...(reportData.monthly_breakdown || []).map(month => [
                month.month,
                month.total,
                month.uploaded,
                `${month.percentage}%`
            ])
        ];
        const statsSheet = XLSX.utils.aoa_to_sheet(statsData);
        
        // Set column widths
        const wscols = [
            {wch: 30}, {wch: 20}, {wch: 35}, {wch: 15}, 
            {wch: 15}, {wch: 15}, {wch: 12}, {wch: 15}, {wch: 15}
        ];
        worksheet['!cols'] = wscols;
        
        // Add sheets to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'All Months Report');
        XLSX.utils.book_append_sheet(workbook, statsSheet, 'Summary & Breakdown');
        
        // Generate and download Excel file
        const currentDate = new Date().toISOString().split('T')[0];
        const filename = `Certificate_Report_All_Months_${currentDate}.xlsx`;
        
        XLSX.writeFile(workbook, filename);
        
        console.log('[LOAMS] All months Excel export completed:', filename);
        return { success: true, filename };
    } catch (error) {
        console.error('[LOAMS] All months Excel export error:', error);
        throw error;
    }
},

getCertificateStatistics: async () => {
    console.log('[LOAMS] Fetching certificate statistics');
    try {
        const response = await pythonApi.get('/api/certificates/statistics', {
            headers: {
                'Accept': 'application/json'
            },
            timeout: 30000
        });
        
        if (!response.data) {
            throw new Error('No statistics data received');
        }
        
        console.log('[LOAMS] Certificate statistics retrieved:', response.data);
        return response.data;
    } catch (error) {
        console.error('[LOAMS] Certificate statistics error:', error);
        
        // Fallback: calculate from certificates list
        try {
            const certificatesResponse = await loamsApi.certificateService.list();
            if (certificatesResponse.data?.success && certificatesResponse.data.certificates) {
                const certificates = certificatesResponse.data.certificates;
                
                return {
                    success: true,
                    total_certificates: certificates.length,
                    unique_clusters: [...new Set(certificates.map(cert => cert.clusterName))].filter(Boolean).length,
                    unique_agreements: [...new Set(certificates.map(cert => cert.agreementNo))].filter(Boolean).length,
                    latest_upload: certificates.length > 0 ? 
                        Math.max(...certificates.map(cert => new Date(cert.last_modified).getTime())) : null
                };
            }
        } catch (fallbackError) {
            console.warn('[LOAMS] Statistics fallback failed:', fallbackError);
        }
        
        throw error;
    }
},

        

        
    
        // Method to export report as Excel
        exportReportAsExcel: async (reportData, month) => {
    console.log('[LOAMS] Exporting certificate report as Excel');
    try {
        const monthDate = new Date(`${month}-01`);
        const monthName = monthDate.toLocaleString('default', { month: 'long' });
        const year = monthDate.getFullYear();
        
        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        
        // Format the data for Excel - Updated to include Month column and remove Certificate Status
        const excelData = reportData.agreements.map(agreement => ({
            'Project': agreement.project,
            'Agreement Ref': agreement.agreement_ref_no,
            'Service Type': agreement.service_type || 'General Service',
            'Month': `${monthName} ${year}`, // Added Month column
            'Contract Start': new Date(agreement.contract_start_date).toLocaleDateString(),
            'Contract End': new Date(agreement.contract_finish_date).toLocaleDateString(),
            'Duration (Months)': agreement.duration,
            'Upload Date': agreement.upload_date ? new Date(agreement.upload_date).toLocaleDateString() : '-'
            // Removed 'Certificate Status' column
        }));
        
        // Create worksheet with data
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        
        // Add statistics sheet
        const stats = reportData.statistics;
        const statsData = [
            ['Report Period', `${monthName} ${year}`],
            [''],
            ['Total Service Agreements', stats.total_agreements],
            ['Certificates Uploaded', stats.total_with_certificates],
            ['Completion Rate', `${stats.completion_rate}%`],
            ['Unique Projects', stats.unique_projects],
            ['Total Certificates', stats.total_certificates]
        ];
        const statsSheet = XLSX.utils.aoa_to_sheet(statsData);
        
        // Set column widths - Updated for new column structure
        const wscols = [
            {wch: 30}, // Project
            {wch: 20}, // Agreement Ref
            {wch: 35}, // Service Type
            {wch: 15}, // Month
            {wch: 15}, // Contract Start
            {wch: 15}, // Contract End
            {wch: 12}, // Duration
            {wch: 15}  // Upload Date
        ];
        worksheet['!cols'] = wscols;
        
        // Add sheets to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Certificates Report');
        XLSX.utils.book_append_sheet(workbook, statsSheet, 'Summary');
        
        // Generate Excel file
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Certificate_Report_${monthName}_${year}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return true;
    } catch (error) {
        console.error('[LOAMS] Excel export error:', error);
        throw error;
    }
}
},

// Add to existing loamsApi object in api.js
searchInvoiceRange: async (startSerial, endSerial) => {
    console.log('[LOAMS] Searching invoice range:', { startSerial, endSerial });
    try {
        const response = await pythonApi.get('/api/invoices/range', {
            params: { 
                start: startSerial, 
                end: endSerial 
            },
            headers: {
                'Accept': 'application/json'
            },
            timeout: 30000
        });
        
        if (!response.data) {
            throw new Error('No data received from server');
        }
        
        if (response.data.error) {
            throw new Error(response.data.error);
        }
        
        console.log('[LOAMS] Found invoices in range:', response.data.length);
        return { data: response.data };
    } catch (error) {
        console.error('[LOAMS] Range search error:', error);
        throw error;
    }
},

generateBulkInvoices: async (formData) => {
    console.log('[LOAMS] Generating bulk invoices');
    try {
        for (let pair of formData.entries()) {
            console.log('[LOAMS] FormData contains:', pair[0], 
                pair[1] instanceof File ? `File: ${pair[1].name} (${pair[1].size} bytes)` : pair[1]);
        }
        
        const response = await pythonApi.post('/generate', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            responseType: 'blob',
            timeout: 600000 // 10 minutes for bulk generation
        });
        
        console.log('[LOAMS] Bulk generation successful');
        return response;
    } catch (error) {
        console.error('[LOAMS] Bulk generation error:', error);
        if (error.code === 'ECONNABORTED') {
            throw new Error('Generation timed out. Try with fewer invoices.');
        }
        throw error;
    }
},






// Add this method to your loamsApi object in api.js

// Complete Invoice & Status Upload Method
uploadCompleteInvoiceData: async (uploadData) => {
    console.log('[LOAMS] Uploading complete invoice and status data:', {
        total_records: uploadData.records.length,
        with_status: uploadData.records.filter(r => r.has_status_data).length,
        sample_record: uploadData.records[0]
    });
    
    try {
        // Validate the upload data structure
        if (!uploadData.records || !Array.isArray(uploadData.records)) {
            throw new Error('Invalid upload data structure');
        }

        if (uploadData.records.length === 0) {
            throw new Error('No records to upload');
        }

        // Log sample record for debugging
        console.log('[LOAMS] Sample record structure:', {
            sample: uploadData.records[0],
            has_invoice_data: !!uploadData.records[0].invoice_data,
            has_status_data: !!uploadData.records[0].status_data,
            serial_number: uploadData.records[0].serial_number
        });

        const response = await pythonApi.post('/api/invoices/complete-upload', uploadData, {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            timeout: 120000 // 2 minute timeout for large uploads
        });

        // Validate response
        if (!response.data) {
            throw new Error('No response data received from server');
        }

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        console.log('[LOAMS] Complete upload successful:', {
            invoices_updated: response.data.invoices_updated,
            statuses_updated: response.data.statuses_updated,
            errors: response.data.errors,
            details: response.data.details
        });

        return response;
        
    } catch (error) {
        console.error('[LOAMS] Complete upload error:', {
            message: error.message,
            status: error.response?.status,
            data: error.response?.data
        });

        // Enhanced error handling
        if (error.code === 'ECONNABORTED') {
            throw new Error('Upload timed out. The file may be too large. Please try with fewer records.');
        }

        if (error.response?.status === 400) {
            throw new Error(error.response.data?.message || 'Invalid data format');
        }

        if (error.response?.status === 500) {
            throw new Error('Server error during processing. Please check the data format and try again.');
        }

        throw new Error(error.message || 'Failed to upload invoice data');
    }
},


    // Request invoice approval
// Request invoice approval
requestInvoiceApproval: async (requestData) => {
    console.log('[LOAMS] Requesting invoice approval:', requestData);
    try {
      // Validate required fields
      if (!requestData.cluster || !requestData.agreementRef || !requestData.month) {
        throw new Error('Missing required fields for approval request');
      }
  
      // Format the month properly if needed (YYYY-MM)
      let month = requestData.month;
      if (!month.match(/^\d{4}-\d{2}$/)) {
        throw new Error('Invalid month format. Please use YYYY-MM format.');
      }
  
      const response = await pythonApi.post('/api/invoice/request-approval', {
        cluster: requestData.cluster,
        agreement_ref: requestData.agreementRef,
        month: month,
        amount: requestData.amount || 0,
        reason: requestData.reason || 'No Operation'
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        timeout: 30000 // 30 second timeout
      });
  
      // Check for errors in response
      if (response.data?.error) {
        throw new Error(response.data.error);
      }
  
      console.log('[LOAMS] Approval request successful:', response.data);
      return response;
    } catch (error) {
      console.error('[LOAMS] Approval request error:', error);
      
      // Handle specific error cases
      if (error.response?.status === 409) {
        throw new Error('An approval request already exists for this project and month');
      }
      if (error.response?.status === 404) {
        throw new Error('Project not found or not available for approval');
      }
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.error || 'Invalid approval request data');
      }
      
      throw error;
    }
  },
  
  // Get approvals list for a month
  getApprovalsList: async (month) => {
    console.log('[LOAMS] Getting approvals for month:', month);
    try {
      // Validate month format
      if (!month.match(/^\d{4}-\d{2}$/)) {
        throw new Error('Invalid month format. Please use YYYY-MM format.');
      }
      
      const response = await pythonApi.get('/api/invoice/approvals', {
        params: { month },
        headers: {
          'Accept': 'application/json'
        },
        timeout: 30000 // 30 second timeout
      });
  
      // Handle response errors
      if (response.data?.error) {
        throw new Error(response.data.error);
      }
  
      console.log('[LOAMS] Got approvals:', response.data?.approvals?.length || 0);
      return response;
    } catch (error) {
      console.error('[LOAMS] Get approvals error:', error);
      
      // For get operations, return empty array on error to avoid UI failures
      if (error.response?.status === 404) {
        return { data: { approvals: [] } };
      }
      
      throw error;
    }
  },
  
  // Update status of an approval (for admin/super users)
  updateApprovalStatus: async (approvalId, status, comments) => {
    console.log('[LOAMS] Updating approval status:', { approvalId, status });
    try {
      if (!approvalId) {
        throw new Error('Approval ID is required');
      }
      
      if (!['APPROVED', 'REJECTED'].includes(status)) {
        throw new Error('Invalid status. Must be APPROVED or REJECTED');
      }
      
      const response = await pythonApi.put(`/api/invoice/approvals/${approvalId}`, {
        status,
        comments: comments || ''
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        timeout: 30000 // 30 second timeout
      });
      
      if (response.data?.error) {
        throw new Error(response.data.error);
      }
      
      console.log(`[LOAMS] Approval ${status.toLowerCase()} successfully`);
      return response;
    } catch (error) {
      console.error('[LOAMS] Update approval status error:', error);
      throw error;
    }
  },
    





// Add this method to your loamsApi object in api.js with a different name

// Invoice Status Excel Upload Method with a different name
uploadStatusExcelFiles: async (formData, isDirectMode = false) => {
  console.log('[LOAMS] Uploading invoice status Excel files', isDirectMode ? '(Direct Bank mode)' : '');
  try {
    // Log what we're uploading for debugging
    for (let pair of formData.entries()) {
      console.log('[LOAMS] FormData contains:', pair[0], 
        pair[1] instanceof File ? `File: ${pair[1].name} (${pair[1].size} bytes)` : pair[1]);
    }
    
    // Create a custom Axios instance just for this request to ensure headers are set correctly
    const uploadInstance = axios.create({
      baseURL: pythonApi.defaults.baseURL,
      timeout: 60000,
      withCredentials: true
    });
    
    // Make the API call WITHOUT setting Content-Type header
    const response = await uploadInstance.post('/api/invoicess-status/upload', formData);
    
    return response;
  } catch (error) {
    console.error('[LOAMS] Status upload error:', error);
    throw error;
  }
},




// Add this to your loamsApi object in api.js

// Get amount mismatch logs
// In your API client (loamsApi)
getAmountMismatchLogs: async (params = {}) => {
  console.log('[API] Fetching amount mismatch logs', params);
  try {
    // Build query parameters for filtering
    const queryParams = new URLSearchParams();
    if (params.invoice_number) {
      queryParams.append('invoice_number', params.invoice_number);
    }
    if (params.start_date) {
      queryParams.append('start_date', params.start_date);
    }
    if (params.end_date) {
      queryParams.append('end_date', params.end_date);
    }
    
    // Use the pythonApi instance instead of direct axios call
    const url = `/api/invoice-amount-logs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    console.log('[API] Request URL:', url);
    
    const response = await pythonApi.get(url, {
      headers: {
        'Accept': 'application/json'
      },
      timeout: 30000
    });
    
    return response;
  } catch (error) {
    console.error('[API] Get amount logs error:', error);
    throw error;
  }
},

// Export amount mismatch logs to Excel
exportAmountMismatchLogs: async (params = {}) => {
  console.log('[LOAMS] Exporting amount mismatch logs', params);
  try {
      const queryParams = new URLSearchParams();
      if (params.invoiceNumber) {
          queryParams.append('invoice_number', params.invoiceNumber);
      }
      if (params.startDate) {
          queryParams.append('start_date', params.startDate);
      }
      if (params.endDate) {
          queryParams.append('end_date', params.endDate);
      }
      queryParams.append('export', 'true');
      
      const response = await pythonApi.get(
          `/api/invoice-amount-logs?${queryParams}`,
          {
              responseType: 'blob',
              timeout: 30000
          }
      );
      
      // Create and trigger download
      const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from content-disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition 
          ? contentDisposition.split('filename=')[1].replace(/"/g, '')
          : `amount_mismatch_logs_${new Date().toISOString().split('T')[0]}.xlsx`;
          
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true };
  } catch (error) {
      console.error('[LOAMS] Export amount logs error:', error);
      throw error;
  }
},



    // Add these methods to your loamsApi object invoice no and for that invoice no it will loo

// Invoice Status Update Methods
// In api.js - Replace the existing uploadInvoiceStatusExcel function
uploadInvoiceStatusExcel: async (oracleFile, bankFile, isDirectMode = false) => {
  console.log('[LOAMS] Uploading invoice status Excel files', isDirectMode ? '(Direct Bank mode)' : '');
  try {
      const formData = new FormData();
      
      if (oracleFile) {
          formData.append('oracle_file', oracleFile);
          console.log('[LOAMS] Adding Oracle file:', oracleFile.name, `(${(oracleFile.size / 1024).toFixed(2)} KB)`);
      }
      
      if (bankFile) {
          // In direct mode, use special parameter name to signal backend
          const paramName = isDirectMode ? 'direct_bank_file' : 'bank_file';
          formData.append(paramName, bankFile);
          console.log('[LOAMS] Adding Bank file:', bankFile.name, `(${(bankFile.size / 1024).toFixed(2)} KB)`);
          
          // Add flag for direct bank mode
          if (isDirectMode) {
              formData.append('direct_update', 'true');
          }
      }
      
      // Log FormData contents for debugging
      for (let pair of formData.entries()) {
          console.log('[LOAMS] FormData contains:', pair[0], 
              pair[1] instanceof File ? `File: ${pair[1].name} (${pair[1].size} bytes)` : pair[1]);
      }
      
      // Create a custom Axios instance just for this request to ensure headers are set correctly
      const uploadInstance = axios.create({
          baseURL: pythonApi.defaults.baseURL,
          timeout: 60000,
          withCredentials: true
      });
      
      // Make the API call WITHOUT setting Content-Type header
      const response = await uploadInstance.post('/api/invoice-status/upload', formData);
      
      return response;
  } catch (error) {
      console.error('[LOAMS] Status upload error:', error);
      throw error;
  }
},


// this one is for the downlaod the status template for the uploaded status througgh excel file ... 

downloStatusTemplate: async (templateType = 'oracle') => {
  console.log('[LOAMS] Downloading invoice status template:', templateType);
  try {
      const response = await pythonApi.get(`/api/invoice-status/templates/${templateType}`, {
          responseType: 'blob',
          timeout: 30000 // 30 second timeout
      });
      
      // Create download
      const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Set the filename based on template type
      link.download = templateType === 'oracle' 
          ? 'oracle_invoice_status_template.xlsx' 
          : templateType === 'bank'
          ? 'bank_payment_template.xlsx'
          : 'direct_bank_template.xlsx';
          
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true };
  } catch (error) {
      console.error('[LOAMS] Template download failed:', {
          message: error.message,
          status: error.response?.status,
          code: error.code
      });
      
      throw new Error('Failed to download template: ' + (error.message || 'Unknown error'));
  }
},

// Method to batch update invoice statuses from JSON data
updateInvoiceStatuses: async (statusData) => {
  console.log('[LOAMS] Batch updating invoice statuses:', statusData.length);
  try {
      const response = await pythonApi.post('/api/invoice-status/batch-update', statusData, {
          headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
          },
          timeout: 30000 // 30 second timeout
      });
      
      if (!response.data) {
          throw new Error('No data received from server');
      }
      
      if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to update invoice statuses');
      }
      
      console.log('[LOAMS] Batch update successful:', {
          updated: response.data.updated,
          failed: response.data.failed,
          notFound: response.data.notFound,
          amountMismatches: response.data.amountMismatches || 0
      });
      
      return response;
  } catch (error) {
      console.error('[LOAMS] Batch update failed:', {
          message: error.message,
          status: error.response?.status,
          code: error.code
      });
      
      // Handle specific error cases
      if (error.code === 'ECONNABORTED') {
          throw new Error('Update request timed out. Please try again later.');
      }
      
      if (error.response?.data?.message) {
          throw new Error(error.response.data.message);
      }
      
      throw error;
  }
},

// Method to download template files
downloadInvoiceStatusTemplate: async (templateType = 'oracle') => {
    console.log('[LOAMS] Downloading invoice status template:', templateType);
    try {
        const response = await pythonApi.get(`/api/invoice-status/templates/${templateType}`, {
            responseType: 'blob',
            timeout: 30000 // 30 second timeout
        });
        
        // Create download
        const blob = new Blob([response.data], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Set the filename based on template type
        link.download = templateType === 'oracle' 
            ? 'oracle_invoice_status_template.xlsx' 
            : 'bank_payment_template.xlsx';
            
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true };
    } catch (error) {
        console.error('[LOAMS] Template download failed:', {
            message: error.message,
            status: error.response?.status,
            code: error.code
        });
        
        throw new Error('Failed to download template: ' + (error.message || 'Unknown error'));
    }
},


// REPLACE YOUR EXISTING pettyCash SECTION IN api.js WITH THIS:

pettyCash: {
    // Extract expense data from PDF using OpenAI
    extractExpenseData: async (formData) => {
        console.log('[PETTY_CASH] Extracting expense data from PDF');
        try {
            // Log FormData for debugging
            for (let pair of formData.entries()) {
                console.log('[PETTY_CASH] FormData contains:', pair[0], 
                    pair[1] instanceof File ? `File: ${pair[1].name} (${pair[1].size} bytes)` : pair[1]);
            }

            const response = await pythonApi.post('/api/petty-cash/extract', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                timeout: 180000, // 3 minutes for AI processing
                retry: 2,        // Allow 2 retries
                retryDelay: 5000 // 5 second delay between retries
            });
            
            console.log('[PETTY_CASH] Extraction successful:', {
                success: response.data?.success,
                reportId: response.data?.report_id,
                expenseCount: response.data?.data?.expenses?.length || 0,
                message: response.data?.message
            });
            
            return response.data;
        } catch (error) {
            console.error('[PETTY_CASH] Extract error:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data
            });
            
            // Enhanced error handling for petty cash
            if (error.code === 'ECONNABORTED') {
                throw new Error('AI processing timed out. Please try again with a smaller PDF or check your connection.');
            }
            
            if (error.response?.status === 400) {
                throw new Error(error.response.data?.detail || 'Invalid PDF file or request');
            }
            
            if (error.response?.status === 413) {
                throw new Error('File too large. Please use a PDF file smaller than 10MB.');
            }
            
            if (error.response?.status === 500) {
                throw new Error('Server error during processing. Please try again or contact support.');
            }
            
            throw error;
        }
    },

    // Get list of expense reports with enhanced parameters
    getExpenseReports: async (skip = 0, limit = 50, sortBy = 'date', order = 'desc') => {
        console.log('[PETTY_CASH] Getting expense reports list', { skip, limit, sortBy, order });
        try {
            const response = await pythonApi.get('/api/petty-cash/reports', {
                params: { 
                    skip, 
                    limit,
                    sort_by: sortBy,
                    order 
                },
                headers: {
                    'Accept': 'application/json'
                },
                timeout: 30000 // 30 second timeout
            });
            
            if (!response.data) {
                throw new Error('No data received from server');
            }
            
            console.log('[PETTY_CASH] Found reports:', {
                count: response.data.reports?.length || 0,
                total: response.data.total || 0,
                sortBy: response.data.sort_by,
                order: response.data.order
            });
            
            return response.data;
        } catch (error) {
            console.error('[PETTY_CASH] Get reports error:', error);
            
            // Return empty result on error to avoid UI crashes
            if (error.response?.status === 404 || error.response?.status === 500) {
                return {
                    success: true,
                    reports: [],
                    total: 0,
                    skip: 0,
                    limit: limit,
                    sort_by: sortBy,
                    order: order
                };
            }
            
            throw error;
        }
    },

    // Search expense reports with query
    searchExpenseReports: async (query, skip = 0, limit = 50) => {
        console.log('[PETTY_CASH] Searching expense reports:', { query, skip, limit });
        try {
            if (!query || query.trim().length === 0) {
                // If no query, return regular reports list
                return await this.getExpenseReports(skip, limit);
            }

            const response = await pythonApi.get('/api/petty-cash/search', {
                params: { 
                    q: query.trim(),
                    skip, 
                    limit 
                },
                headers: {
                    'Accept': 'application/json'
                },
                timeout: 30000 // 30 second timeout
            });
            
            if (!response.data) {
                throw new Error('No data received from server');
            }
            
            console.log('[PETTY_CASH] Search results:', {
                query: query,
                count: response.data.reports?.length || 0,
                total: response.data.total || 0
            });
            
            return response.data;
        } catch (error) {
            console.error('[PETTY_CASH] Search reports error:', error);
            
            // Fallback to client-side filtering if search endpoint fails
            if (error.response?.status === 404) {
                console.log('[PETTY_CASH] Search endpoint not available, falling back to client-side filtering');
                const allReports = await this.getExpenseReports(0, 100);
                const filtered = allReports.reports.filter(report => 
                    (report.reportNumber && report.reportNumber.toLowerCase().includes(query.toLowerCase())) ||
                    (report.company && report.company.toLowerCase().includes(query.toLowerCase())) ||
                    (report.projectName && report.projectName.toLowerCase().includes(query.toLowerCase())) ||
                    (report.sourceFile && report.sourceFile.toLowerCase().includes(query.toLowerCase()))
                );
                
                return {
                    success: true,
                    reports: filtered,
                    total: filtered.length,
                    skip: 0,
                    limit: limit,
                    query: query
                };
            }
            
            throw error;
        }
    },

    // Get detailed expense report
    getExpenseReportDetails: async (reportId) => {
        console.log('[PETTY_CASH] Getting expense report details for ID:', reportId);
        try {
            if (!reportId) {
                throw new Error('Report ID is required');
            }
            
            const response = await pythonApi.get(`/api/petty-cash/reports/${reportId}`, {
                headers: {
                    'Accept': 'application/json'
                },
                timeout: 30000 // 30 second timeout
            });
            
            if (!response.data) {
                throw new Error('No data received from server');
            }
            
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to load report details');
            }
            
            console.log('[PETTY_CASH] Report details loaded:', {
                reportId: reportId,
                expenseCount: response.data.report?.expenses?.length || 0,
                grandTotal: response.data.report?.totals?.grandTotal || response.data.report?.grandTotal || 0
            });
            
            return response.data;
        } catch (error) {
            console.error('[PETTY_CASH] Get report details error:', error);
            
            if (error.response?.status === 404) {
                throw new Error('Report not found');
            }
            
            throw error;
        }
    },

    // Get very detailed report (enhanced version)
    getDetailedExpenseReport: async (reportId) => {
        console.log('[PETTY_CASH] Getting detailed expense report for ID:', reportId);
        try {
            if (!reportId) {
                throw new Error('Report ID is required');
            }
            
            const response = await pythonApi.get(`/api/petty-cash/reports/${reportId}/details`, {
                headers: {
                    'Accept': 'application/json'
                },
                timeout: 30000
            });
            
            if (!response.data) {
                throw new Error('No data received from server');
            }
            
            return response.data;
        } catch (error) {
            console.error('[PETTY_CASH] Get detailed report error:', error);
            
            // Fallback to regular details if enhanced endpoint not available
            if (error.response?.status === 404) {
                return await this.getExpenseReportDetails(reportId);
            }
            
            throw error;
        }
    },

    // Delete expense report
    deleteExpenseReport: async (reportId) => {
        console.log('[PETTY_CASH] Deleting expense report ID:', reportId);
        try {
            if (!reportId) {
                throw new Error('Report ID is required');
            }
            
            const response = await pythonApi.delete(`/api/petty-cash/reports/${reportId}`, {
                headers: {
                    'Accept': 'application/json'
                },
                timeout: 30000 // 30 second timeout
            });
            
            if (!response.data) {
                throw new Error('No response from server');
            }
            
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to delete report');
            }
            
            console.log('[PETTY_CASH] Report deleted successfully:', reportId);
            return response.data;
        } catch (error) {
            console.error('[PETTY_CASH] Delete report error:', error);
            
            if (error.response?.status === 404) {
                throw new Error('Report not found or already deleted');
            }
            
            if (error.response?.status === 403) {
                throw new Error('You do not have permission to delete this report');
            }
            
            throw error;
        }
    },

    // Batch delete multiple reports
    batchDeleteReports: async (reportIds) => {
        console.log('[PETTY_CASH] Batch deleting reports:', reportIds);
        try {
            if (!reportIds || !Array.isArray(reportIds) || reportIds.length === 0) {
                throw new Error('Report IDs array is required');
            }

            if (reportIds.length > 50) {
                throw new Error('Too many reports selected (max 50)');
            }
            
            const response = await pythonApi.post('/api/petty-cash/batch-delete', reportIds, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                timeout: 60000 // 60 second timeout for batch operations
            });
            
            if (!response.data) {
                throw new Error('No response from server');
            }
            
            console.log('[PETTY_CASH] Batch delete successful:', {
                deleted: response.data.deleted_reports,
                expenses: response.data.deleted_expenses
            });
            
            return response.data;
        } catch (error) {
            console.error('[PETTY_CASH] Batch delete error:', error);
            throw error;
        }
    },

    // Get statistics
    getStatistics: async () => {
        console.log('[PETTY_CASH] Getting statistics');
        try {
            const response = await pythonApi.get('/api/petty-cash/stats', {
                headers: {
                    'Accept': 'application/json'
                },
                timeout: 30000
            });
            
            if (!response.data) {
                throw new Error('No data received from server');
            }
            
            console.log('[PETTY_CASH] Statistics loaded:', {
                totalReports: response.data.stats?.total_reports || 0,
                totalAmount: response.data.stats?.total_amount || 0
            });
            
            return response.data;
        } catch (error) {
            console.error('[PETTY_CASH] Get statistics error:', error);
            
            // Return empty stats on error
            return {
                success: false,
                stats: {},
                error: error.message
            };
        }
    },

    // Get export data for Excel
    getExportData: async (reportId) => {
        console.log('[PETTY_CASH] Getting export data for report:', reportId);
        try {
            if (!reportId) {
                throw new Error('Report ID is required');
            }
            
            const response = await pythonApi.get(`/api/petty-cash/reports/${reportId}/export`, {
                headers: {
                    'Accept': 'application/json'
                },
                timeout: 30000
            });
            
            if (!response.data) {
                throw new Error('No data received from server');
            }
            
            return response.data;
        } catch (error) {
            console.error('[PETTY_CASH] Get export data error:', error);
            throw error;
        }
    },

    // Health check
    healthCheck: async () => {
        console.log('[PETTY_CASH] Checking system health');
        try {
            const response = await pythonApi.get('/api/petty-cash/health', {
                headers: {
                    'Accept': 'application/json'
                },
                timeout: 10000 // 10 second timeout
            });
            
            return response.data;
        } catch (error) {
            console.error('[PETTY_CASH] Health check error:', error);
            return {
                success: false,
                status: 'unhealthy',
                error: error.message
            };
        }
    },

    // Helper method to format file size
    formatFileSize: (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    // Helper method to format amounts
    formatAmount: (amount) => {
        if (amount === null || amount === undefined || amount === '') return '0.00';
        return Number(amount).toFixed(2);
    },

    // Helper method to format dates
    formatDate: (dateString) => {
        try {
            if (!dateString) return 'N/A';
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            console.error('Date formatting error:', error);
            return dateString || 'N/A';
        }
    },

    // Method to export report data to Excel (client-side)
    exportReportToExcel: async (reportData, fileName = null) => {
        console.log('[PETTY_CASH] Exporting report to Excel');
        try {
            // Import XLSX dynamically
            const XLSX = window.XLSX || await import('xlsx');
            if (!XLSX) {
                throw new Error('XLSX library not available');
            }
            
            const wb = XLSX.utils.book_new();
            
            // Prepare data for Excel export
            const excelData = [];
            
            // Add header row
            excelData.push([
                'Source File', 'S.No', 'Date', 'Description', 'Project Code',
                'General Amount', 'Materials Amount', 'Pantry & Office Amount',
                'Overtime Food Amount', 'VAT Amount', 'Total Amount (AED)',
                'Report Number', 'Company', 'Prepared By', 'Approved By',
                'Reviewed By', 'Verified By'
            ]);
            
            // Handle both extracted data and saved report data structures
            const expenses = reportData.expenses || [];
            const isExtractedData = reportData.reportNumber !== undefined;
            
            // Add expense data rows
            if (expenses.length > 0) {
                expenses.forEach(expense => {
                    excelData.push([
                        reportData.sourceFile || reportData.source_file_name || '',
                        expense.sn || expense.serial_number || '',
                        expense.date || expense.expense_date_original || '',
                        expense.description || '',
                        expense.project || expense.project_code || '',
                        this.formatAmount(expense.general || expense.general_amount),
                        this.formatAmount(expense.materials || expense.materials_amount),
                        this.formatAmount(expense.pantryOffice || expense.pantry_office_amount),
                        this.formatAmount(expense.overtimeFood || expense.overtime_food_amount),
                        this.formatAmount(expense.vat || expense.vat_amount),
                        this.formatAmount(expense.totalAmount || expense.total_amount),
                        reportData.reportNumber || reportData.report_number || '',
                        reportData.company || '',
                        (isExtractedData ? reportData.signatures?.preparedBy : reportData.prepared_by) || '',
                        (isExtractedData ? reportData.signatures?.approvedBy : reportData.approved_by) || '',
                        (isExtractedData ? reportData.signatures?.reviewedBy : reportData.reviewed_by) || '',
                        (isExtractedData ? reportData.signatures?.verifiedBy : reportData.verified_by) || ''
                    ]);
                });
                
                // Add totals row
                const totals = reportData.totals || {};
                excelData.push([
                    '', '', '', 'TOTALS', '',
                    this.formatAmount(totals.general || reportData.total_general),
                    this.formatAmount(totals.materials || reportData.total_materials),
                    this.formatAmount(totals.pantryOffice || reportData.total_pantry_office),
                    this.formatAmount(totals.overtimeFood || reportData.total_overtime_food),
                    this.formatAmount(totals.vat || reportData.total_vat),
                    this.formatAmount(totals.grandTotal || reportData.grand_total),
                    '', '', '', '', '', ''
                ]);
            }
            
            // Create worksheet
            const ws = XLSX.utils.aoa_to_sheet(excelData);
            
            // Set column widths
            const colWidths = [
                { wch: 20 }, { wch: 8 }, { wch: 12 }, { wch: 40 }, { wch: 12 },
                { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 10 },
                { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 20 },
                { wch: 20 }, { wch: 20 }
            ];
            ws['!cols'] = colWidths;
            
            XLSX.utils.book_append_sheet(wb, ws, "Expense Report");
            
            // Generate filename
            const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
            const reportNum = (reportData.reportNumber || reportData.report_number || 'Report').replace(/[^a-zA-Z0-9]/g, '');
            const finalFileName = fileName || `ExpenseReport_${reportNum}_${timestamp}.xlsx`;
            
            // Save the file
            XLSX.writeFile(wb, finalFileName);
            
            console.log('[PETTY_CASH] Excel export successful:', finalFileName);
            return { success: true, fileName: finalFileName };
            
        } catch (error) {
            console.error('[PETTY_CASH] Excel export error:', error);
            throw new Error('Failed to export to Excel: ' + (error.message || 'Unknown error'));
        }
    },

    // Validation helpers
    validateFile: (file) => {
        const errors = [];
        
        if (!file) {
            errors.push('No file selected');
            return errors;
        }
        
        if (file.type !== 'application/pdf') {
            errors.push('Only PDF files are allowed');
        }
        
        if (file.size > 10 * 1024 * 1024) {
            errors.push('File size too large (max 10MB)');
        }
        
        if (file.size === 0) {
            errors.push('File appears to be empty');
        }
        
        return errors;
    },

    // Cache management
    clearCache: () => {
        console.log('[PETTY_CASH] Clearing cache');
        // Implement any client-side cache clearing if needed
        if (typeof window !== 'undefined' && window.localStorage) {
            const keys = Object.keys(window.localStorage);
            keys.forEach(key => {
                if (key.startsWith('petty_cash_')) {
                    window.localStorage.removeItem(key);
                }
            });
        }
    }
}, 



// Add these OCR-related methods to the loamsApi object in api.js

// OCR API endpoints
// Add these OCR-related methods to the loamsApi object

ocr: {
    // Get all S3 documents with extracted reference numbers
    getAllDocuments: async (searchParams = {}) => {
      console.log('[LOAMS] Fetching all OCR processed documents with params:', searchParams);
      try {
        // Build query parameters
        const queryParams = new URLSearchParams();
        
        if (searchParams.damacRef) {
          queryParams.append('damac_ref', searchParams.damacRef);
        }
        
        if (searchParams.month) {
          queryParams.append('month', searchParams.month);
        }
        
        if (searchParams.project) {
          queryParams.append('project', searchParams.project);
        }
  
        // Use the correct endpoint
        const response = await pythonApi.get(`/api/ocr/documents${queryParams.toString() ? `?${queryParams}` : ''}`, {
          headers: {
            'Accept': 'application/json'
          },
          timeout: 60000 // 60-second timeout
        });
        
        if (!response.data) {
          throw new Error('Invalid response format from server');
        }
        
        console.log('[LOAMS] Found OCR documents:', response.data.documents?.length || 0);
        
        // Transform document data for UI display
        const transformedData = {
          documents: (response.data.documents || []).map(doc => ({
            id: doc.id || Math.random().toString(36).substring(7),
            key: doc.s3_key,
            name: doc.filename || extractFilenameFromPath(doc.s3_key),
            damacRef: doc.damac_ref || 'Unknown',
            invoiceNo: doc.invoice_no || 'Unknown',
            project: doc.project || 'Unknown',
            cluster: doc.cluster || 'Unknown',
            extractedDate: doc.extracted_date ? new Date(doc.extracted_date).toLocaleDateString() : 'Unknown',
            confidence: doc.confidence || 0,
            url: doc.url,
            preview: doc.preview_url,
            size: doc.size ? formatFileSize(doc.size) : 'Unknown',
            lastModified: doc.last_modified ? new Date(doc.last_modified).toLocaleDateString() : 'Unknown'
          })),
          total: response.data.total || response.data.documents?.length || 0,
          availableProjects: response.data.available_projects || [],
          availableClusters: response.data.available_clusters || [],
          availableMonths: response.data.available_months || []
        };
        
        return { data: transformedData };
      } catch (error) {
        console.error('[LOAMS] Get OCR documents error:', error);
        
        // Handle specific error cases
        if (error.code === 'ECONNABORTED') {
          throw new Error('Request timed out. There may be too many documents to retrieve at once.');
        }
        
        // In case of 404, return empty results instead of throwing error
        if (error.response?.status === 404) {
          return { 
            data: {
              documents: [],
              total: 0,
              availableProjects: [],
              availableClusters: [],
              availableMonths: []
            }
          };
        }
        
        throw error;
      }
    },
    
    // Upload new documents to S3 and process with OCR
    uploadDocuments: async (files, options = {}) => {
      console.log('[LOAMS] Uploading documents for OCR processing:', files.length);
      try {
        if (!files || files.length === 0) {
          throw new Error('No files selected for upload');
        }
        
        const formData = new FormData();
        
        // Add files to form data
        Array.from(files).forEach((file, index) => {
          formData.append('files', file);
        });
        
        // Add options like prefix, project, etc.
        if (options.prefix) {
          formData.append('prefix', options.prefix);
        }
        
        if (options.project) {
          formData.append('project', options.project);
        }
        
        if (options.processImmediately !== undefined) {
          formData.append('process_immediately', options.processImmediately.toString());
        }
        
        const response = await pythonApi.post('/api/ocr/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 300000, // 5-minute timeout for large uploads
          onUploadProgress: options.onProgress
        });
        
        console.log('[LOAMS] Upload successful:', response.data);
        return response;
      } catch (error) {
        console.error('[LOAMS] Upload error:', error);
        
        // Enhanced error handling
        if (error.code === 'ECONNABORTED') {
          throw new Error('Upload timed out. Try with fewer or smaller files.');
        }
        
        if (error.response?.status === 413) {
          throw new Error('The files are too large. Maximum total size is 50MB.');
        }
        
        throw error;
      }
    },
    
    // Process all unprocessed documents in S3
    processUnprocessedDocuments: async () => {
      console.log('[LOAMS] Processing all unprocessed documents');
      try {
        const response = await pythonApi.post('/api/ocr/process-all', null, {
          timeout: 60000 // 1-minute timeout
        });
        
        console.log('[LOAMS] Processing initiated successfully:', response.data);
        return response;
      } catch (error) {
        console.error('[LOAMS] Processing initiation error:', error);
        throw error;
      }
    },
    
    // Get document preview URL
    getDocumentPreviewUrl: async (s3Key) => {
      console.log('[LOAMS] Getting document preview URL for:', s3Key);
      try {
        if (!s3Key) {
          throw new Error('S3 key is required');
        }
        
        const encodedKey = encodeURIComponent(s3Key);
        const response = await pythonApi.get(`/api/ocr/preview/${encodedKey}`);
        
        if (!response.data || !response.data.url) {
          throw new Error('Invalid preview URL response');
        }
        
        return response.data.url;
      } catch (error) {
        console.error('[LOAMS] Preview URL error:', error);
        throw error;
      }
    },
    
    // Get processing status
    getProcessingStatus: async () => {
      try {
        const response = await pythonApi.get('/api/ocr/status', {
          timeout: 10000 // 10-second timeout
        });
        
        return response.data;
      } catch (error) {
        console.error('[LOAMS] Processing status check failed:', error);
        // Default response if error - assume not processing
        return { is_processing: false, remaining_count: 0 };
      }
    },
    
    // Search for documents by DAMAC reference number
    searchByDamacRef: async (damacRef) => {
      try {
        if (!damacRef) {
          throw new Error('DAMAC reference is required');
        }
        
        const response = await pythonApi.get('/api/ocr/search', {
          params: { damac_ref: damacRef }
        });
        
        return response.data;
      } catch (error) {
        console.error('[LOAMS] Search by DAMAC reference failed:', error);
        throw error;
      }
    },
    
    // Download document
    downloadDocument: async (s3Key) => {
      try {
        if (!s3Key) {
          throw new Error('S3 key is required');
        }
        
        const encodedKey = encodeURIComponent(s3Key);
        const response = await pythonApi.get(`/api/ocr/download/${encodedKey}`, {
          responseType: 'blob'
        });
        
        // Extract filename from S3 key
        const filename = s3Key.split('/').pop() || 'document.pdf';
        
        // Create download
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true };
      } catch (error) {
        console.error('[LOAMS] Download error:', error);
        throw error;
      }
    }
  },




  // Add these methods to your loamsApi object in api.js

// Add to the loamsApi object
invoiceFiles: {
  // List all invoice files with optional search
  listFiles: async (searchTerm = '') => {
      console.log('[LOAMS] Fetching invoice files with search:', searchTerm);
      try {
          const response = await pythonApi.get('/api/invoices/files', {
              params: { search: searchTerm },
              headers: {
                  'Accept': 'application/json'
              },
              timeout: 30000 // 30 second timeout
          });
          
          if (!response.data) {
              throw new Error('Invalid response format from server');
          }
          
          console.log('[LOAMS] Found invoice files:', response.data.files?.length || 0);
          return response.data;
      } catch (error) {
          console.error('[LOAMS] Error fetching invoice files:', error);
          
          // Handle specific error cases
          if (error.code === 'ECONNABORTED') {
              throw new Error('Request timed out. There may be too many files to retrieve at once.');
          }
          
          throw error;
      }
  },
  
  // Get view URL for a specific file
  getViewUrl: async (fileKey) => {
      console.log('[LOAMS] Getting view URL for file:', fileKey);
      try {
          if (!fileKey) {
              throw new Error('File key is required');
          }
          
          const response = await pythonApi.get('/api/invoices/files/view', {
              params: { key: fileKey },
              headers: {
                  'Accept': 'application/json'
              }
          });
          
          if (!response.data || !response.data.url) {
              throw new Error('Failed to get file view URL');
          }
          
          return response.data.url;
      } catch (error) {
          console.error('[LOAMS] Error getting file view URL:', error);
          throw error;
      }
  },
  
  // Open file in new tab
  openFile: async (fileKey) => {
      try {
          const url = await loamsApi.invoiceFiles.getViewUrl(fileKey);
          window.open(url, '_blank');
          return true;
      } catch (error) {
          console.error('[LOAMS] Error opening file:', error);
          throw error;
      }
  },
  
  // Format file size for display
  formatFileSize: (bytes) => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
},





// Add to loamsApi object in api.js

contractDetails: {
    // Get all contract details
    getAll: async () => {
        console.log('[LOAMS] Fetching all contract details');
        try {
            const response = await pythonApi.get('/api/contract-details');
            return response;
        } catch (error) {
            console.error('[LOAMS] Get contract details error:', error);
            throw error;
        }
    },

    // Create new contract
    create: async (contractData) => {
        console.log('[LOAMS] Creating contract detail:', contractData);
        try {
            const response = await pythonApi.post('/api/contract-details', contractData);
            return response;
        } catch (error) {
            console.error('[LOAMS] Create contract error:', error);
            throw error;
        }
    },

    // Update existing contract
    update: async (contractId, contractData) => {
        console.log('[LOAMS] Updating contract:', contractId, contractData);
        try {
            const response = await pythonApi.put(`/api/contract-details/${contractId}`, contractData);
            return response;
        } catch (error) {
            console.error('[LOAMS] Update contract error:', error);
            throw error;
        }
    },

    // Delete contract
    delete: async (contractId) => {
        console.log('[LOAMS] Deleting contract:', contractId);
        try {
            const response = await pythonApi.delete(`/api/contract-details/${contractId}`);
            return response;
        } catch (error) {
            console.error('[LOAMS] Delete contract error:', error);
            throw error;
        }
    },

    // Search contracts
    search: async (searchTerm) => {
        console.log('[LOAMS] Searching contracts:', searchTerm);
        try {
            const response = await pythonApi.get('/api/contract-details/search', {
                params: { search: searchTerm }
            });
            return response;
        } catch (error) {
            console.error('[LOAMS] Search contracts error:', error);
            throw error;
        }


        },

        getWithRelatedData: async (poNumber) => {
        console.log('[LOAMS] Fetching contract with related data:', poNumber);
        try {
            const response = await pythonApi.get(`/api/contract-details/${poNumber}/related-data`);
            console.log('[LOAMS] Raw API response:', response);
            return response;
        } catch (error) {
            console.error('[LOAMS] Get contract with related data error:', error);
            throw error;
        }
    },

    // Get stats overview for all contracts
    getStatsOverview: async () => {
        console.log('[LOAMS] Fetching contracts stats overview');
        try {
            const response = await pythonApi.get('/api/contract-details/stats-overview');
            return response;
        } catch (error) {
            console.error('[LOAMS] Get stats overview error:', error);
            throw error;
        }
    },

    // Get detailed month analysis for a contract
    getInvoiceMonths: async (poNumber) => {
        console.log('[LOAMS] Fetching invoice months for contract:', poNumber);
        try {
            const response = await pythonApi.get(`/api/contract-details/${poNumber}/invoice-months`);
            return response;
        } catch (error) {
            console.error('[LOAMS] Get invoice months error:', error);
            throw error;
        }
    }
},
    

  

  





    // Updated certificate methods with a new name: certificateService
certificateService: {
    // Updated certificateService.list implementation
    // In api.js - Updated certificateService.list implementation
list: async () => {
        console.log('[LOAMS] Fetching all certificates with enhanced parsing');
        try {
            const response = await pythonApi.get('/api/certificates/all');
            console.log('[LOAMS] Raw response:', response);
            
            if (!response.data) {
                throw new Error('No data received from server');
            }
            
            // Transform the response with enhanced parsing
            const formattedData = {
                success: true,
                certificates: (response.data.certificates || []).map(cert => {
                    console.log('[LOAMS] Processing certificate:', cert);
                    
                    // Start with backend-parsed values
                    let clusterName = cert.clusterName || 'Unknown';
                    let agreementNo = cert.agreementNo || 'Unknown';
                    let month = cert.month || 'Unknown';
                    
                    // If backend parsing failed, try frontend parsing
                    if (clusterName === 'Unknown' || agreementNo === 'Unknown' || month === 'Unknown') {
                        console.log('[LOAMS] Backend parsing incomplete, trying frontend parsing');
                        
                        const parsed = parseAdvancedCertificateInfo(cert.key, cert.filename);
                        if (parsed) {
                            clusterName = parsed.cluster || clusterName;
                            agreementNo = parsed.agreement || agreementNo;
                            month = parsed.month || month;
                        }
                    }
                    
                    // Format the date for Dubai time (UTC+4)
                    const modifiedDate = new Date(cert.last_modified);
                    const formattedDate = modifiedDate.toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: 'Asia/Dubai'
                    });
                    
                    // Format size
                    const size = typeof cert.size === 'number' 
                        ? loamsApi.certificateService.formatFileSize(cert.size)
                        : cert.size;
                    
                    const processedCert = {
                        ...cert,
                        displayName: cert.filename || cert.displayName,
                        clusterName: clusterName,
                        agreementNo: agreementNo,
                        poNumber: agreementNo, // Keep backward compatibility
                        month: month,
                        date: modifiedDate,
                        formattedDate,
                        size
                    };
                    
                    console.log('[LOAMS] Processed certificate:', {
                        key: processedCert.key,
                        cluster: processedCert.clusterName,
                        agreement: processedCert.agreementNo,
                        month: processedCert.month
                    });
                    
                    return processedCert;
                })
            };
            
            console.log('[LOAMS] Final processed data:', formattedData.certificates.length, 'certificates');
            return { data: formattedData };
        } catch (error) {
            console.error('[LOAMS] Certificate list error:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data
            });
            throw error;
        }
    },


    listEnhanced: async () => {
        console.log('[LOAMS] 🔍 Fetching certificates from BOTH locations');
        try {
            const response = await pythonApi.get('/api/certificates/all-enhanced');
            console.log('[LOAMS] Raw enhanced response:', response);
            
            if (!response.data) {
                throw new Error('No data received from server');
            }
            
            // Transform the response with enhanced parsing
            const formattedData = {
                success: true,
                certificates: (response.data.certificates || []).map(cert => {
                    console.log('[LOAMS] Processing enhanced certificate:', cert);
                    
                    // Format the date for Dubai time (UTC+4)
                    const modifiedDate = new Date(cert.last_modified);
                    const formattedDate = modifiedDate.toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: 'Asia/Dubai'
                    });
                    
                    // Format size
                    const size = typeof cert.size === 'number' 
                        ? loamsApi.certificateService.formatFileSize(cert.size)
                        : cert.size;
                    
                    const processedCert = {
                        ...cert,
                        displayName: cert.filename || cert.displayName,
                        clusterName: cert.clusterName,
                        agreementNo: cert.agreementNo,
                        poNumber: cert.agreementNo, // Keep backward compatibility
                        month: cert.month,
                        date: modifiedDate,
                        formattedDate,
                        size,
                        // 🔥 NEW: Enhanced properties
                        certificateType: cert.certificate_type,
                        storageLocation: cert.storage_location,
                        downloadType: cert.download_type,
                        invoiceNumber: cert.invoice_number,
                        // Add visual indicators
                        isOriginal: cert.certificate_type === 'original',
                        isCCLoams: cert.certificate_type === 'cc_loams',
                        locationLabel: cert.certificate_type === 'original' ? 'Original' : 'CC-LOAMS',
                        locationBadge: cert.certificate_type === 'original' ? 'success' : 'info'
                    };
                    
                    console.log('[LOAMS] Processed enhanced certificate:', {
                        key: processedCert.key,
                        cluster: processedCert.clusterName,
                        agreement: processedCert.agreementNo,
                        month: processedCert.month,
                        type: processedCert.certificateType,
                        location: processedCert.storageLocation
                    });
                    
                    return processedCert;
                }),
                // 🔥 NEW: Enhanced breakdown statistics
                breakdown: response.data.breakdown || {
                    original: 0,
                    cc_loams: 0
                }
            };
            
            console.log('[LOAMS] Final enhanced processed data:', {
                total: formattedData.certificates.length,
                original: formattedData.breakdown.original,
                ccLoams: formattedData.breakdown.cc_loams
            });
            
            return { data: formattedData };
        } catch (error) {
            console.error('[LOAMS] Enhanced certificate list error:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data
            });
            throw error;
        }
    },

    // Enhanced preview URL method
    getPreviewUrlEnhanced: async (certificateKey) => {
        console.log('[LOAMS] 🔍 Getting enhanced preview URL for:', certificateKey);
        try {
            // Ensure the key is properly encoded
            const encodedKey = encodeURIComponent(certificateKey);
            
            const response = await pythonApi.get(`/api/certificate/preview-enhanced/${encodedKey}`, {
                timeout: 30000 // 30 second timeout
            });
            
            if (!response.data || !response.data.url) {
                throw new Error('Invalid preview URL response');
            }
            
            console.log('[LOAMS] ✅ Enhanced preview URL generated:', {
                type: response.data.certificate_type,
                key: response.data.key
            });
            
            return {
                url: response.data.url,
                certificateType: response.data.certificate_type,
                key: response.data.key
            };
        } catch (error) {
            console.error('[LOAMS] Enhanced preview URL error:', error);
            
            if (error.response?.status === 404) {
                throw new Error('Certificate not found on server');
            }
            
            throw new Error('Failed to generate preview URL: ' + error.message);
        }
    },

    // Enhanced download method with different options
    downloadCertificateEnhanced: async (certificate, downloadOptions = {}) => {
        console.log('[LOAMS] 🔽 Enhanced download for certificate:', {
            key: certificate.key,
            type: certificate.certificateType,
            location: certificate.storageLocation
        });

        try {
            // Get preview URL first
            const previewData = await loamsApi.certificateService.getPreviewUrlEnhanced(certificate.key);
            
            // Create appropriate filename based on certificate type
            let filename;
            if (certificate.certificateType === 'cc_loams') {
                // CC-LOAMS format: CC-{invoice_number}.pdf
                filename = certificate.filename || `CC-${certificate.invoiceNumber || 'Unknown'}.pdf`;
            } else {
                // Original format: cluster-agreement-month.pdf
                filename = loamsApi.certificateService.createProperFilename(certificate);
            }
            
            // Add type prefix if requested
            if (downloadOptions.addTypePrefix) {
                const prefix = certificate.certificateType === 'cc_loams' ? 'CC_' : 'ORIG_';
                filename = prefix + filename;
            }
            
            // Download the file
            const response = await fetch(previewData.url, {
                method: 'GET',
                headers: { 'Accept': 'application/pdf' }
            });
            
            if (!response.ok) {
                throw new Error(`Download failed: ${response.status} ${response.statusText}`);
            }
            
            const blob = await response.blob();
            if (blob.size === 0) {
                throw new Error('Downloaded file is empty');
            }
            
            // Create download
            const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            setTimeout(() => {
                window.URL.revokeObjectURL(blobUrl);
            }, 1000);
            
            console.log('[LOAMS] ✅ Enhanced download completed:', filename);
            return { success: true, filename };
            
        } catch (error) {
            console.error('[LOAMS] Enhanced download error:', error);
            throw error;
        }
    },

    // Batch download with type separation
    downloadCertificatesEnhanced: async (certificates, downloadOptions = {}) => {
        console.log('[LOAMS] 🔽 Enhanced batch download:', {
            total: certificates.length,
            original: certificates.filter(c => c.certificateType === 'original').length,
            ccLoams: certificates.filter(c => c.certificateType === 'cc_loams').length
        });

        try {
            if (certificates.length === 1) {
                // Single download
                return await loamsApi.certificateService.downloadCertificateEnhanced(certificates[0], downloadOptions);
            }

            // Multiple downloads - create ZIP
            const JSZip = window.JSZip || await import('jszip').then(module => module.default);
            if (!JSZip) {
                throw new Error('JSZip library not available');
            }

            const zip = new JSZip();
            
            // Create folders for different types if requested
            if (downloadOptions.separateByType) {
                zip.folder('Original_Certificates');
                zip.folder('CC_LOAMS_Certificates');
            }

            // Process each certificate
            for (const certificate of certificates) {
                try {
                    console.log(`[LOAMS] Processing certificate: ${certificate.key}`);
                    
                    // Get download URL
                    const previewData = await loamsApi.certificateService.getPreviewUrlEnhanced(certificate.key);
                    
                    // Download file content
                    const response = await fetch(previewData.url);
                    if (!response.ok) {
                        console.warn(`[LOAMS] Failed to download ${certificate.key}: ${response.status}`);
                        continue;
                    }
                    
                    const blob = await response.blob();
                    
                    // Create filename
                    let filename;
                    if (certificate.certificateType === 'cc_loams') {
                        filename = certificate.filename || `CC-${certificate.invoiceNumber || 'Unknown'}.pdf`;
                    } else {
                        filename = loamsApi.certificateService.createProperFilename(certificate);
                    }
                    
                    // Add to appropriate folder
                    if (downloadOptions.separateByType) {
                        const folderName = certificate.certificateType === 'cc_loams' 
                            ? 'CC_LOAMS_Certificates' 
                            : 'Original_Certificates';
                        zip.folder(folderName).file(filename, blob);
                    } else {
                        // Add type prefix to filename
                        const prefix = certificate.certificateType === 'cc_loams' ? 'CC_' : 'ORIG_';
                        zip.file(prefix + filename, blob);
                    }
                    
                } catch (certError) {
                    console.error(`[LOAMS] Error processing certificate ${certificate.key}:`, certError);
                    // Continue with other certificates
                }
            }

            // Generate ZIP file
            console.log('[LOAMS] Generating ZIP file...');
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            
            // Create download
            const url = window.URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            
            const currentDate = new Date().toISOString().split('T')[0];
            const zipFilename = `certificates-enhanced-${currentDate}-${certificates.length}files.zip`;
            link.download = zipFilename;
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 1000);
            
            console.log('[LOAMS] ✅ Enhanced batch download completed:', zipFilename);
            return { success: true, filename: zipFilename };

        } catch (error) {
            console.error('[LOAMS] Enhanced batch download error:', error);
            throw error;
        }
    },

    // Helper method to create proper filename
    createProperFilename: (certificate) => {
        try {
            let cluster = certificate.clusterName || 'Unknown';
            let agreement = certificate.agreementNo || 'Unknown';
            let month = certificate.month || 'Unknown';
            
            const cleanCluster = cluster.replace(/[<>:"/\\|?*\s]/g, '_');
            const cleanAgreement = agreement.replace(/[<>:"/\\|?*\s]/g, '_');
            const cleanMonth = month.replace(/[<>:"/\\|?*\s]/g, '_');
            
            const filename = `${cleanCluster}-${cleanAgreement}-${cleanMonth}.pdf`;
            return filename;
            
        } catch (error) {
            console.error('Error creating filename:', error);
            return certificate.displayName || `certificate-${Date.now()}.pdf`;
        }
    },

    // Helper function to format file size
    formatFileSize: (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    // Filter certificates by type
    filterByType: (certificates, type) => {
        if (type === 'all') return certificates;
        return certificates.filter(cert => cert.certificateType === type);
    },

    // Get certificate statistics
    getCertificateStats: (certificates) => {
        const stats = {
            total: certificates.length,
            original: certificates.filter(c => c.certificateType === 'original').length,
            ccLoams: certificates.filter(c => c.certificateType === 'cc_loams').length,
            byCluster: {},
            byMonth: {}
        };

        // Group by cluster
        certificates.forEach(cert => {
            const cluster = cert.clusterName || 'Unknown';
            if (!stats.byCluster[cluster]) {
                stats.byCluster[cluster] = { total: 0, original: 0, ccLoams: 0 };
            }
            stats.byCluster[cluster].total++;
            if (cert.certificateType === 'original') {
                stats.byCluster[cluster].original++;
            } else {
                stats.byCluster[cluster].ccLoams++;
            }
        });

        // Group by month
        certificates.forEach(cert => {
            const month = cert.month || 'Unknown';
            if (!stats.byMonth[month]) {
                stats.byMonth[month] = { total: 0, original: 0, ccLoams: 0 };
            }
            stats.byMonth[month].total++;
            if (cert.certificateType === 'original') {
                stats.byMonth[month].original++;
            } else {
                stats.byMonth[month].ccLoams++;
            }
        });

        return stats;
    },





    getPreviewUrl: async (certificateKey) => {
        console.log('[LOAMS] Getting preview URL for:', certificateKey);
        try {
            // Ensure the key is properly encoded
            const encodedKey = encodeURIComponent(certificateKey);
            
            const response = await pythonApi.get(`/api/certificate/preview/${encodedKey}`, {
                timeout: 30000 // 30 second timeout
            });
            
            if (!response.data || !response.data.url) {
                throw new Error('Invalid preview URL response');
            }
            
            console.log('[LOAMS] Preview URL generated successfully');
            return response.data.url;
        } catch (error) {
            console.error('[LOAMS] Preview URL error:', error);
            
            if (error.response?.status === 404) {
                throw new Error('Certificate not found on server');
            }
            
            throw new Error('Failed to generate preview URL: ' + error.message);
        }
    },

    deleteCertificates: async (keys) => {
        console.log('[LOAMS] Deleting certificates:', keys);
        try {
            if (!keys || !Array.isArray(keys) || keys.length === 0) {
                throw new Error('No certificates specified for deletion');
            }
            
            const response = await pythonApi.post('/api/certificates/delete', { keys });
            
            if (!response.data) {
                throw new Error('Invalid response from server');
            }
            
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to delete certificates');
            }
            
            console.log('[LOAMS] Certificates deleted successfully');
            return response.data;
        } catch (error) {
            console.error('[LOAMS] Certificate deletion error:', error);
            throw new Error(error.message || 'Failed to delete certificates');
        }
    },

    downloadCertificates: async (keys) => {
        console.log('[LOAMS] Downloading multiple certificates:', keys);
        try {
            if (!keys || !Array.isArray(keys) || keys.length === 0) {
                throw new Error('No certificates specified for download');
            }
            
            // Validate that all keys exist
            console.log('[LOAMS] Validating certificate keys...');
            
            const payload = { keys: keys };
            console.log('[LOAMS] Sending batch download request:', payload);
            
            const response = await pythonApi.post('/api/certificates/download-batch', 
                payload,
                { 
                    responseType: 'blob',
                    timeout: 300000, // 5 minutes timeout
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/zip'
                    },
                    validateStatus: function (status) {
                        return status < 500; // Accept any status less than 500
                    }
                }
            );
            
            // Check for error responses
            if (response.status >= 400) {
                let errorMessage = 'Download failed';
                
                try {
                    // Try to read error from blob
                    const text = await response.data.text();
                    const errorData = JSON.parse(text);
                    errorMessage = errorData.error || errorMessage;
                } catch (parseError) {
                    errorMessage = `Server error: ${response.status}`;
                }
                
                throw new Error(errorMessage);
            }
            
            // Verify response
            if (!response.data) {
                throw new Error('Empty response received');
            }
            
            // Check if it's actually a blob
            if (!(response.data instanceof Blob)) {
                throw new Error('Invalid response format: expected blob');
            }
            
            if (response.data.size === 0) {
                throw new Error('Downloaded file is empty');
            }
            
            const contentType = response.headers['content-type'];
            console.log('[LOAMS] Download response - Size:', response.data.size, 'Content-Type:', contentType);
            
            if (contentType && !contentType.includes('application/zip')) {
                console.warn('[LOAMS] Unexpected content type:', contentType);
            }
            
            console.log('[LOAMS] Successfully downloaded certificates ZIP');
            return response;
        } catch (error) {
            console.error('[LOAMS] Batch download error:', error);
            
            // Enhanced error handling
            if (error.code === 'ECONNABORTED') {
                throw new Error('Download request timed out. Try fewer files or check your connection.');
            }
            
            if (error.message.includes('Network Error')) {
                throw new Error('Network error during download. Please check your connection.');
            }
            
            throw error;
        }
    },

    // Helper function to format file size
    

    // Add this method to get all certificates
    getAllCertificates: async () => {
        console.log('[LOAMS] Fetching all certificates');
        try {
            const response = await pythonApi.get('/api/certificates/all');
            
            if (!response.data) {
                throw new Error('Invalid response format from server');
            }
            
            // Transform certificates to the expected format
            const formattedCertificates = (response.data.certificates || []).map(cert => {
                const parts = cert.key.split('/');
                const fileName = parts[parts.length - 1];
                const clusterName = parts[2] || '';
                const agreementNo = parts[3] || '';
                
                // Format the date
                const modifiedDate = new Date(cert.last_modified);
                const formattedDate = modifiedDate.toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
                
                return {
                    ...cert,
                    displayName: fileName,
                    clusterName,
                    agreementNo,
                    date: modifiedDate,
                    formattedDate,
                    size: typeof cert.size === 'number' ? formatFileSize(cert.size) : cert.size
                };
            });
            
            return {
                data: {
                    success: true,
                    certificates: formattedCertificates
                }
            };
        } catch (error) {
            console.error('[LOAMS] Get all certificates error:', error);
            throw new Error('Failed to load all certificates');
        }
    },

    // Method to get certificates for a specific cluster
    getClusterCertificates: async (cluster) => {
        console.log('[LOAMS] Fetching certificates for cluster:', cluster);
        try {
            const response = await pythonApi.get(`/api/certificate/list/${cluster}`);
            
            if (!response.data) {
                throw new Error('Invalid response format from server');
            }
            
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to load certificates');
            }
            
            console.log(`[LOAMS] Found ${response.data.certificates?.length || 0} certificates for cluster`);
            return response;
        } catch (error) {
            console.error('[LOAMS] Get cluster certificates error:', error);
            throw new Error(`Failed to load certificates for cluster: ${cluster}`);
        }
    }
},





    previewExcel: async (formData) => {
        console.log('[LOAMS] Starting preview request');
        try {
            const response = await pythonApi.post('/preview', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            console.log('[LOAMS] Preview successful:', response.data);
            return response;
        } catch (error) {
            console.error('[LOAMS] Preview error:', error);
            throw error;
        }
    },


    downloadStatusTemplate: async () => {
        try {
            const response = await pythonApi.get('/api/status/template', {
                responseType: 'blob'
            });
            return response;
        } catch (error) {
            console.error('[LOAMS] Template download error:', error);
            throw error;
        }
    },
    
    // Preview status Excel file
    previewStatusExcel: async (formData) => {
        try {
            const response = await pythonApi.post('/api/status/preview', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                timeout: 30000 // 30 second timeout
            });
            return response;
        } catch (error) {
            console.error('[LOAMS] Status preview error:', error);
            
            // Handle different types of errors
            if (error.response?.status === 500) {
                throw new Error('Server error processing file. Please try again.');
            } else if (error.response?.data?.error) {
                throw new Error(error.response.data.error);
            } else if (error.code === 'ECONNABORTED') {
                throw new Error('Request timed out. Please try with a smaller file.');
            } else {
                throw new Error('Failed to preview file');
            }
        }
    },
    
    // Update statuses in batch
    updateStatusBatch: async (formData) => {
        try {
            const response = await pythonApi.post('/api/status/update', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response;
        } catch (error) {
            console.error('[LOAMS] Batch status update error:', error);
            throw error;
        }
    },

    getAllInvoices: async () => {
        console.log('[LOAMS] Fetching all invoices');
        try {
            const response = await pythonApi.get('/invoices');
            console.log('[LOAMS] Fetched invoices:', response.data.length);
            return response;
        } catch (error) {
            console.error('[LOAMS] Get invoices error:', error);
            throw error;
        }
    },

    // Add this method to your loamsApi object in api.js

// Inside loamsApi object, add this method:
getAllInvoiceDetails: async () => {
    console.log('[LOAMS] Fetching all invoice details');
    try {
        // Use pythonApi instead of api to correctly access the Python service
        const response = await pythonApi.get('/api/invoice-details');
        
        if (response.data && Array.isArray(response.data)) {
            console.log('[LOAMS] Found invoice details:', response.data.length);
            
            // Process data to ensure numeric values are properly parsed
            const processedData = response.data.map(item => ({
                ...item,
                contract_value: parseFloat(item.contract_value) || 0,
                basic: parseFloat(item.basic) || 0,
                vat: parseFloat(item.vat) || 0,
                amount: parseFloat(item.amount) || 0,
                Jan_2025: parseFloat(item.Jan_2025) || 0,
                Feb_2025: parseFloat(item.Feb_2025) || 0,
                Mar_2025: parseFloat(item.Mar_2025) || 0,
                Apr_2025: parseFloat(item.Apr_2025) || 0,
                May_2025: parseFloat(item.May_2025) || 0,
                Jun_2025: parseFloat(item.Jun_2025) || 0,
                Jul_2025: parseFloat(item.Jul_2025) || 0,
                Aug_2025: parseFloat(item.Aug_2025) || 0,
                Sep_2025: parseFloat(item.Sep_2025) || 0,
                Oct_2025: parseFloat(item.Oct_2025) || 0,
                Nov_2025: parseFloat(item.Nov_2025) || 0,
                Dec_2025: parseFloat(item.Dec_2025) || 0
            }));
            
            return { data: processedData };
        } else {
            console.warn('[LOAMS] Unexpected response format:', response.data);
            throw new Error('Invalid data format received from server');
        }
    } catch (error) {
        console.error('[LOAMS] Get all invoice details error:', error);
        
        // Fall back to getting data from existing invoices
        try {
            // Get some invoice data to create mock details
            const invoicesResponse = await loamsApi.getAllInvoices();
            if (!invoicesResponse.data || !Array.isArray(invoicesResponse.data)) {
                return { data: [] };
            }
            
            console.log('[LOAMS] Fallback: creating mock data from invoices');
            
            // Create mock details from invoices
            const mockDetails = invoicesResponse.data.slice(0, 20).map(invoice => ({
                id: invoice.id || '',
                invoice_id: invoice.id || '',
                po_no: invoice.agreement_ref_no || '',
                auxillarsss: invoice.auxiliary_no || '',
                accounts_name: invoice.project || '',
                start_date: invoice.contract_start_date || '',
                end_date: invoice.contract_finish_date || '',
                contract_value: parseFloat(invoice.contract_value) || 0,
                basic: parseFloat(invoice.unit_price) || 0,
                vat: parseFloat(invoice.vat_amount) || 0,
                amount: parseFloat(invoice.total_amount) || 0,
                service_type: invoice.service_type || '',
                Jan_2025: 0,
                Feb_2025: 0,
                Mar_2025: 0,
                Apr_2025: 0,
                May_2025: 0,
                Jun_2025: 0,
                Jul_2025: 0,
                Aug_2025: 0,
                Sep_2025: 0,
                Oct_2025: 0,
                Nov_2025: 0,
                Dec_2025: 0
            }));
            
            return { data: mockDetails };
        } catch (fallbackError) {
            console.error('[LOAMS] Fallback for invoice details failed:', fallbackError);
            return { data: [] };
        }
    }
},

getInvoiceDetails: async (poNumber) => {
    console.log('[LOAMS] Fetching invoice details for PO:', poNumber);
    try {
        // Use pythonApi instead of api to correctly access the Python service
        const response = await pythonApi.get(`/api/invoice-details/${poNumber}`);
        
        if (response.data && Array.isArray(response.data)) {
            console.log('[LOAMS] Found invoice details');
            
            // Process data to ensure numeric values are properly parsed
            const processedData = response.data.map(item => ({
                ...item,
                contract_value: parseFloat(item.contract_value) || 0,
                basic: parseFloat(item.basic) || 0,
                vat: parseFloat(item.vat) || 0,
                amount: parseFloat(item.amount) || 0,
                Jan_2025: parseFloat(item.Jan_2025) || 0,
                Feb_2025: parseFloat(item.Feb_2025) || 0,
                Mar_2025: parseFloat(item.Mar_2025) || 0,
                Apr_2025: parseFloat(item.Apr_2025) || 0,
                May_2025: parseFloat(item.May_2025) || 0,
                Jun_2025: parseFloat(item.Jun_2025) || 0,
                Jul_2025: parseFloat(item.Jul_2025) || 0,
                Aug_2025: parseFloat(item.Aug_2025) || 0,
                Sep_2025: parseFloat(item.Sep_2025) || 0,
                Oct_2025: parseFloat(item.Oct_2025) || 0,
                Nov_2025: parseFloat(item.Nov_2025) || 0,
                Dec_2025: parseFloat(item.Dec_2025) || 0
            }));
            
            return { data: processedData };
        } else {
            throw new Error('No invoice details found or invalid format');
        }
    } catch (error) {
        console.error('[LOAMS] Get invoice details error:', error);
        
        // Fallback - get invoice data and transform it
        try {
            const invoiceResponse = await loamsApi.searchInvoicesForEdit(poNumber);
            
            if (!invoiceResponse.data || !Array.isArray(invoiceResponse.data) || invoiceResponse.data.length === 0) {
                throw new Error('No invoices found for this PO number');
            }
            
            const firstInvoice = invoiceResponse.data[0];
            
            // Create a fallback structure matching pts_invoice_details schema
            const fallbackData = [{
                id: null,
                invoice_id: null,
                po_no: poNumber,
                auxillarsss: firstInvoice.auxiliary_no || '',
                accounts_name: firstInvoice.project || '',
                start_date: firstInvoice.contract_start_date || '',
                end_date: firstInvoice.contract_finish_date || '',
                contract_value: parseFloat(firstInvoice.contract_value) || 0,
                basic: parseFloat(firstInvoice.unit_price) || 0,
                vat: parseFloat(firstInvoice.vat_amount) || 0,
                amount: parseFloat(firstInvoice.total_amount) || 0,
                service_type: firstInvoice.service_type || '',
                Jan_2025: 0,
                Feb_2025: 0,
                Mar_2025: 0,
                Apr_2025: 0,
                May_2025: 0,
                Jun_2025: 0,
                Jul_2025: 0,
                Aug_2025: 0,
                Sep_2025: 0,
                Oct_2025: 0,
                Nov_2025: 0,
                Dec_2025: 0
            }];
            
            return { data: fallbackData };
        } catch (fallbackError) {
            console.error('[LOAMS] Fallback error:', fallbackError);
            return { data: [] };
        }
    }
},

exportInvoiceDetailsToExcel: async (filteredData) => {
    console.log('[LOAMS] Starting Excel export');
    try {
      // Verify we have data to export
      if (!filteredData || !Array.isArray(filteredData) || filteredData.length === 0) {
        throw new Error('No data available to export');
      }
      
      console.log(`[LOAMS] Exporting ${filteredData.length} records`);
      
      // Direct approach using fetch API for more control
      const response = await fetch('/api/invoice-details/export-v2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(filteredData),
      });
      
      if (!response.ok) {
        throw new Error(`Server responded with status ${response.status}`);
      }
      
      const blob = await response.blob();
      console.log(`[LOAMS] Response blob size: ${blob.size} bytes`);
      
      if (blob.size <= 100) {
        throw new Error(`Received file is too small (${blob.size} bytes)`);
      }
      
      // Get filename from content-disposition header if available
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'invoice_details.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="([^"]+)"/);
        if (match) filename = match[1];
      }
      
      // Download the file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      link.remove();
      
      return { success: true, fileName: filename };
    } catch (error) {
      console.error('[LOAMS] Export error:', error);
      throw error;
    }
  },














  



    addInvoiceNote: async (noteData) => {
        console.log('[LOAMS] Adding invoice note');
        try {
            const response = await pythonApi.post('/invoice/notes', noteData);
            console.log('[LOAMS] Note added successfully');
            return response;
        } catch (error) {
            console.error('[LOAMS] Add note error:', error);
            throw error;
        }
    },

    getInvoiceNotes: async (invoiceId) => {
        console.log('[LOAMS] Fetching notes for invoice:', invoiceId);
        try {
            const response = await pythonApi.get(`/invoice/notes/${invoiceId}`);
            console.log('[LOAMS] Notes fetched successfully');
            return response;
        } catch (error) {
            console.error('[LOAMS] Get notes error:', error);
            throw error;
        }
    },

    getMonthlyReports: async () => {
        console.log('[LOAMS] Fetching monthly reports');
        try {
            const response = await pythonApi.get('/reports/monthly');
            console.log('[LOAMS] Reports fetched successfully');
            return response;
        } catch (error) {
            console.error('[LOAMS] Get reports error:', error);
            throw error;
        }
    },



    // Updated exportMonthlyReport function for the loamsApi object
exportMonthlyReport: async (year, month) => {
    console.log('[LOAMS] Exporting monthly report:', { year, month });
    try {
      // Format month with leading zero
      const formattedMonth = String(month).padStart(2, '0');
      
      // First attempt to use the backend export endpoint
      try {
        const response = await pythonApi.get('/api/reports/monthly/export', {
          params: { 
            year,
            month: formattedMonth
          },
          responseType: 'blob',
          timeout: 60000, // 60 second timeout for large report generation
        });
        
        // Get month name for filename
        const date = new Date(year, month-1, 1);
        const monthName = date.toLocaleString('default', { month: 'long' });
        
        // Create download
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Monthly_Report_${year}_${monthName}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log('[LOAMS] Monthly report exported successfully via API');
        return { success: true };
      } catch (apiError) {
        console.warn('[LOAMS] API export failed, falling back to client-side export:', apiError);
        // If the API export fails, fall back to client-side export
        return await loamsApi.exportMonthlyReportClientSide(year, month);
      }
    } catch (error) {
      console.error('[LOAMS] Report export error:', error);
      throw new Error('Failed to export monthly report: ' + (error.message || 'Unknown error'));
    }
  },
  
  // New client-side fallback export function
  exportMonthlyReportClientSide: async (year, month) => {
    console.log('[LOAMS] Starting client-side export fallback');
    try {
      // Get the report data first
      const reportData = await loamsApi.getMonthlyReportData(year, month);
      if (!reportData) {
        throw new Error('No report data available');
      }
      
      // Get month name
      const date = new Date(year, month-1, 1);
      const monthName = date.toLocaleString('default', { month: 'long' });
      
      // Create a new jsPDF instance (client-side PDF generation)
      const { jsPDF } = window.jspdf || await import('jspdf');
      const doc = new jsPDF();
      
      // Add company header
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('PHOENICIAN TECHNICAL SERVICES LLC', 105, 20, { align: 'center' });
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Central Plaza, Office 399, Dubai Investment Park 1', 105, 28, { align: 'center' });
      doc.text('+971 4 8882 9464 B4', 105, 34, { align: 'center' });
      
      // Add report title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Monthly Report per Project', 105, 45, { align: 'center' });
      doc.setFontSize(12);
      doc.text(`${monthName} ${year}`, 105, 52, { align: 'center' });
      
      // Add summary section
      doc.setFontSize(12);
      doc.text('Summary', 20, 65);
      
      // Create summary table
      const summaryData = [
        ['', 'Count', 'Amount (AED)'],
        ['Active Projects', reportData.summary.activeProjects || 0, formatCurrency(reportData.summary.activeAmount || 0)],
        ['Completion Certificates', reportData.summary.completionCertificates || 0, formatCurrency(reportData.summary.certificateAmount || 0)],
        ['Tax Invoice Issued', reportData.summary.invoicesIssued || 0, formatCurrency(reportData.summary.invoiceAmount || 0)],
        ['Payment Received', reportData.summary.paymentsReceived || 0, formatCurrency(reportData.summary.paymentAmount || 0)]
      ];
      
      doc.autoTable({
        startY: 70,
        head: [summaryData[0]],
        body: summaryData.slice(1),
        styles: { fontSize: 10 },
        headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0] },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 40, halign: 'center' },
          2: { cellWidth: 50, halign: 'right' }
        }
      });
      
      // Add details section
      doc.text('Details', 20, doc.autoTable.previous.finalY + 15);
      
      // Create details table
      const detailsHeaders = [['Cluster', 'PO No.', 'Invoice No', 'CC No', 'Status', 'Amount']];
      const detailsRows = reportData.details.map(item => [
        item.cluster,
        item.poNumber,
        item.invoiceNumber,
        item.ccNumber,
        item.status,
        formatCurrency(item.amount)
      ]);
      
      doc.autoTable({
        startY: doc.autoTable.previous.finalY + 20,
        head: detailsHeaders,
        body: detailsRows,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0] },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 30 },
          2: { cellWidth: 30 },
          3: { cellWidth: 20 },
          4: { cellWidth: 30 },
          5: { cellWidth: 30, halign: 'right' }
        },
        didDrawPage: (data) => {
          // Add header to each new page
          if (data.pageNumber > 1) {
            doc.setFontSize(8);
            doc.text(`Monthly Report - ${monthName} ${year}`, 105, 15, { align: 'center' });
          }
        }
      });
      
      // Save the PDF
      doc.save(`Monthly_Report_${year}_${monthName}.pdf`);
      console.log('[LOAMS] Client-side PDF export completed');
      
      return { success: true };
    } catch (error) {
      console.error('[LOAMS] Client-side export error:', error);
      
      // Attempt to generate Excel if PDF generation fails
      try {
        console.log('[LOAMS] Falling back to Excel export');
        
        // Get report data
        const reportData = await loamsApi.getMonthlyReportData(year, month);
        if (!reportData) {
          throw new Error('No report data available');
        }
        
        // Get month name
        const date = new Date(year, month-1, 1);
        const monthName = date.toLocaleString('default', { month: 'long' });
        
        // Import XLSX dynamically if needed
        const XLSX = window.XLSX || await import('xlsx').then(module => module.default);
        if (!XLSX) {
          throw new Error('XLSX library not available');
        }
        
        // Create workbook
        const wb = XLSX.utils.book_new();
        
        // Create summary sheet data
        const summaryData = [
          ['PHOENICIAN TECHNICAL SERVICES LLC'],
          ['Central Plaza, Office 399, Dubai Investment Park 1'],
          ['+971 4 8882 9464 B4'],
          [''],
          ['Monthly Report per Project'],
          [`${monthName} ${year}`],
          [''],
          ['', 'Count', 'Amount (AED)'],
          ['Active Projects', reportData.summary.activeProjects || 0, reportData.summary.activeAmount || 0],
          ['Completion Certificates', reportData.summary.completionCertificates || 0, reportData.summary.certificateAmount || 0],
          ['Tax Invoice Issued', reportData.summary.invoicesIssued || 0, reportData.summary.invoiceAmount || 0],
          ['Payment Received', reportData.summary.paymentsReceived || 0, reportData.summary.paymentAmount || 0]
        ];
        
        // Create details sheet data
        const detailsData = [
          ['Cluster', 'PO No.', 'Invoice No', 'CC No', 'Status', 'Amount', 'Bank']
        ];
        
        reportData.details.forEach(item => {
          detailsData.push([
            item.cluster,
            item.poNumber,
            item.invoiceNumber,
            item.ccNumber,
            item.status,
            item.amount,
            item.bank || ''
          ]);
        });
        
        // Add sheets to workbook
        const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');
        
        const detailsSheet = XLSX.utils.aoa_to_sheet(detailsData);
        XLSX.utils.book_append_sheet(wb, detailsSheet, 'Details');
        
        // Set column widths
        summarySheet['!cols'] = [{ wch: 30 }, { wch: 15 }, { wch: 20 }];
        detailsSheet['!cols'] = [
          { wch: 30 }, { wch: 20 }, { wch: 15 }, 
          { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 15 }
        ];
        
        // Generate Excel file
        XLSX.writeFile(wb, `Monthly_Report_${year}_${monthName}.xlsx`);
        
        return { success: true };
      } catch (excelError) {
        console.error('[LOAMS] Excel fallback export error:', excelError);
        throw new Error('Failed to export report in any format');
      }
    }
  },
  



    checkExistingInvoices: async (filenames) => {
        try {
            const response = await pythonApi.post('/api/invoices/check-existing', {
                filenames
            });
            return response.data;
        } catch (error) {
            console.error('[LOAMS] Check existing invoices error:', error);
            throw error;
        }
    },

    uploadInvoicesToS3: async (files, onProgress) => {
        try {
            const formData = new FormData();
            files.forEach(file => {
                formData.append('files', file.blob, file.filename);
            });

            const response = await pythonApi.post('/api/invoices/upload-to-s3', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    if (onProgress) {
                        onProgress(percentCompleted);
                    }
                }
            });

            return response.data;
        } catch (error) {
            console.error('[LOAMS] Upload to S3 error:', error);
            throw error;
        }
    },

    generateInvoices: async (formData, onProgress) => {
        console.log('[LOAMS] Starting invoice generation');
        try {
            console.log('[LOAMS] Generate FormData:', {
                hasFile: formData.has('file'),
                fileName: formData.get('file')?.name,
                fileSize: formData.get('file')?.size
            });
    
            const response = await pythonApi.post('/generate', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Accept': '*/*'
                },
                responseType: 'blob',
                timeout: 600000, // 10 minutes timeout for large files
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    console.log(`Upload Progress: ${percentCompleted}%`);
                    if (onProgress) {
                        onProgress(percentCompleted);
                    }
                }
            });
    
            // Get S3 upload results from headers
            const s3Results = JSON.parse(response.headers['x-s3-upload-results']);
            console.log('[LOAMS] S3 Upload Results:', s3Results);
    
            return {
                data: response.data,
                headers: response.headers,
                s3Results
            };
    
        } catch (error) {
            if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
                console.error('[LOAMS] Network error:', error);
                throw new Error('Network error occurred. Please try again or check your connection.');
            }
            console.error('[LOAMS] Generation error:', error);
            throw error;
        }
    },

    searchInvoices: async (searchTerm) => {
        console.log('[LOAMS] Searching invoices:', searchTerm);
        try {
            // If number only is entered, add LOAMS- prefix
            const formattedSearch = searchTerm.includes('LOAMS-') ? 
                searchTerm : `LOAMS-${searchTerm}`;
                
            // Add proper error handling
            const response = await pythonApi.get('/api/invoices/search', {
                params: { search: formattedSearch },
                validateStatus: function (status) {
                    return status < 500; // Accept any status less than 500
                }
            });
    
            if (response.data.error) {
                throw new Error(response.data.error);
            }
    
            console.log('[LOAMS] Search results:', response.data);
            return response;
        } catch (error) {
            console.error('[LOAMS] Search error:', error);
            throw error;
        }
    },










    






    // Add these functions to your loamsApi object in api.js

    searchInvoicesForEdit: async (poNumber) => {
        console.log('[LOAMS] Starting edit search with PO:', poNumber);
        try {
            const response = await pythonApi.get('/api/invoices/edit/search', {
                params: { po_number: poNumber },
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });
    
            if (response.data && Array.isArray(response.data)) {
                console.log('[LOAMS] Found', response.data.length, 'invoices');
            } else {
                console.warn('[LOAMS] Unexpected response format:', response.data);
            }
    
            return response;
        } catch (error) {
            console.error('[LOAMS] Edit search error:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data
            });
            throw error;
        }
    },
    
    // Update invoices for a PO number
    updateInvoicesForEdit: async (poNumber, updateData) => {
        console.log('[LOAMS] 🔍 Starting update for PO:', poNumber);
        console.log('[LOAMS] 🔍 Update data:', JSON.stringify(updateData, null, 2));
        
        try {
            // Validate required data
            if (!poNumber || !updateData) {
                throw new Error('Missing required data for update');
            }
    
            // Log the payload being sent to the backend
            const payload = {
                po_number: poNumber,
                update_data: updateData
            };
            console.log('[LOAMS] 🔍 Request payload:', JSON.stringify(payload, null, 2));
    
            // Make the API call
            const startTime = new Date();
            console.log(`[LOAMS] 🔍 Making request to /api/invoices/edit/update at ${startTime.toISOString()}`);
            
            const response = await pythonApi.put('/api/invoices/edit/update', {
                po_number: poNumber,
                update_data: updateData
            }, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });
    
            const endTime = new Date();
            const duration = endTime - startTime;
            console.log(`[LOAMS] 🔍 Response received after ${duration}ms:`, response.data);
    
            if (response.data.error) {
                console.error('[LOAMS] ❌ Error in response:', response.data.error);
                throw new Error(response.data.error);
            }
    
            return response;
        } catch (error) {
            console.error('[LOAMS] ❌ Update error:', error.message);
            console.error('[LOAMS] ❌ Error details:', error.response?.data || 'No response data');
            throw error;
        }
    },

    // Keep only this version and remove the other one
updateInvoiceStatus: async (invoiceId, statusData) => {
    console.log('[LOAMS] Updating invoice status:', { invoiceId, statusData });
    try {
        // Add validation
        if (!invoiceId) {
            throw new Error('Invoice ID is required');
        }

        if (!statusData) {
            throw new Error('Status data is required');
        }

        // Add data logging
        console.log('[LOAMS] Request payload:', {
            url: `/api/invoices/${invoiceId}/status`,
            method: 'PUT',
            data: statusData
        });

        const response = await pythonApi.put(
            `/api/invoices/${invoiceId}/status`,
            {
                ...statusData,
                bank: statusData.bank || null,
                // Ensure all required fields are present
                damac_status: statusData.damac_status || null,
                oracle_status: statusData.oracle_status || null,
                payment_status: statusData.payment_status || null,
                // Include all reference numbers
                damac_ref_no: statusData.damac_ref_no || null,
                oracle_ref_no: statusData.oracle_ref_no || null,
                payment_ref_no: statusData.payment_ref_no || null,
                // Include all dates
                damac_date: statusData.damac_date || null,
                oracle_date: statusData.oracle_date || null,
                payment_date: statusData.payment_date || null,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                timeout: 30000 // 30 second timeout
            }
        );

        // Log response
        console.log('[LOAMS] Status update response:', response.data);

        // Validate response
        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response;
    } catch (error) {
        console.error('[LOAMS] Update status error:', {
            error: error.message,
            status: error.response?.status,
            data: error.response?.data
        });

        // Enhanced error handling
        if (error.response?.status === 404) {
            throw new Error('Invoice not found');
        } else if (error.response?.status === 400) {
            throw new Error(error.response.data.error || 'Invalid status data');
        } else if (error.code === 'ECONNABORTED') {
            throw new Error('Request timed out. Please try again.');
        }

        throw error;
    }
},





    updateAuxiliaryNumber: async (invoiceId, data) => {
        console.log('[LOAMS] Updating auxiliary number:', { invoiceId, data });
        try {
            const response = await pythonApi.put(
                `/api/invoices/${invoiceId}/auxiliary`,
                data
            );
            console.log('[LOAMS] Auxiliary update successful');
            return response;
        } catch (error) {
            console.error('[LOAMS] Update auxiliary error:', error);
            throw error;
        }
    },

    saveAgreementAuxiliary: async (data) => {
        console.log('[LOAMS] Saving agreement auxiliary:', data);
        try {
            const response = await pythonApi.post('/api/agreement/auxiliary', data);
            console.log('[LOAMS] Auxiliary saved successfully');
            return response;
        } catch (error) {
            console.error('[LOAMS] Save auxiliary error:', error);
            throw error;
        }
    },

    

    // In loamsApi.js
getDashboardStats: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await pythonApi.get(`/api/dashboard/stats${queryString ? `?${queryString}` : ''}`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        timeout: 30000
      });
  
      if (!response.data) {
        throw new Error('No data received from server');
      }
  
      if (response.data.error) {
        throw new Error(response.data.error);
      }
  
      return response;
  
    } catch (error) {
      console.error('[LOAMS] Dashboard stats error:', error);
      throw error;
    }
  },


  // Monthly Summary API endpoint
getMonthlySummary: async (params = {}) => {
    console.log('[LOAMS] Fetching monthly summary with params:', params);
    try {
        // Build query string with proper parameter handling
        const queryParams = new URLSearchParams();
        
        if (params.year) {
            queryParams.append('year', params.year);
        }
        
        if (params.service_type && params.service_type !== 'all') {
            queryParams.append('service_type', params.service_type);
        }

        // Make the API request with proper configuration
        const response = await pythonApi.get(
            `/api/monthly-summary${queryParams.toString() ? `?${queryParams.toString()}` : ''}`, 
            {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                withCredentials: true,
                timeout: 30000,
                validateStatus: (status) => status < 500 // Accept any status less than 500
            }
        );

        // Validate response
        if (!response.data) {
            throw new Error('No data received from server');
        }

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        // Transform and validate monthly data
        if (response.data.success && response.data.data) {
            const transformedData = {
                monthly: (response.data.data.monthly || []).map(item => ({
                    year: parseInt(item.year) || 0,
                    month: item.month?.trim() || '',
                    invoice_count: parseInt(item.invoice_count) || 0,
                    invoiced_amt: parseFloat(item.invoiced_amt) || 0,
                    oracle_approved_amt: parseFloat(item.oracle_approved_amt) || 0,
                    paid_amt: parseFloat(item.paid_amt) || 0,
                    receivable_amt: parseFloat(item.receivable_amt) || 0,
                    landscaping_amt: parseFloat(item.landscaping_amt) || 0,
                    swimming_amt: parseFloat(item.swimming_amt) || 0,
                    // Calculate payment percentage for visualization
                    payment_percentage: item.oracle_approved_amt > 0 
                        ? (item.paid_amt / item.oracle_approved_amt * 100).toFixed(2) 
                        : 0
                })),
                totals: {
                    invoice_count: parseInt(response.data.data.totals?.invoice_count) || 0,
                    invoiced_amt: parseFloat(response.data.data.totals?.invoiced_amt) || 0,
                    oracle_approved_amt: parseFloat(response.data.data.totals?.oracle_approved_amt) || 0,
                    paid_amt: parseFloat(response.data.data.totals?.paid_amt) || 0,
                    receivable_amt: parseFloat(response.data.data.totals?.receivable_amt) || 0,
                    landscaping_amt: parseFloat(response.data.data.totals?.landscaping_amt) || 0,
                    swimming_amt: parseFloat(response.data.data.totals?.swimming_amt) || 0
                }
            };

            // Sort monthly data by date
            transformedData.monthly.sort((a, b) => {
                const dateA = new Date(`${a.year}-${a.month}-01`);
                const dateB = new Date(`${b.year}-${b.month}-01`);
                return dateA - dateB;
            });

            // Return transformed data with success status
            return {
                ...response,
                data: {
                    success: true,
                    data: transformedData
                }
            };
        }

        return response;

    } catch (error) {
        console.error('[LOAMS] Monthly summary error:', error);
        
        // Handle specific error cases
        if (error.response?.status === 404) {
            throw new Error('Monthly summary service not available');
        }
        
        if (error.code === 'ECONNABORTED') {
            throw new Error('Request timed out. Please try again.');
        }
        
        if (error.response?.data?.error) {
            throw new Error(error.response.data.error);
        }

        if (error.response?.status === 400) {
            throw new Error('Invalid parameters provided');
        }
        
        // Generic error handling
        throw new Error(error.message || 'Failed to fetch monthly summary data');
    }
},

    getContractDateRange: async () => {
        try {
            console.log('[LOAMS] Fetching contract date range');
            
            const response = await pythonApi.get('/api/contract-date-range', {
                headers: {
                    'Accept': 'application/json'
                }
            });

            console.log('[LOAMS] Contract date range response:', response.data);

            if (!response.data) {
                throw new Error('No date range received');
            }

            return response.data;

        } catch (error) {
            console.error('[LOAMS] Contract date range error:', error);
            throw new Error('Failed to fetch contract date range');
        }
    },

    exportDashboardData: async (filters = {}) => {
        try {
            console.log('[LOAMS] Exporting dashboard data with filters:', filters);
            
            const params = new URLSearchParams();
            if (filters.service_type) params.append('service_type', filters.service_type);
            if (filters.start_date) params.append('start_date', filters.start_date);
            if (filters.end_date) params.append('end_date', filters.end_date);

            const response = await pythonApi.get(`/api/dashboard/export?${params.toString()}`, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                responseType: 'blob'
            });

            // Create download
            const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `dashboard_report_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return { success: true };

        } catch (error) {
            console.error('[LOAMS] Dashboard export error:', error);
            throw new Error('Failed to export dashboard data');
        }
    },

    // Helper function to abort pending requests
    abortController: null,

    cancelPendingRequests: () => {
        if (loamsApi.abortController) {
            loamsApi.abortController.abort();
            loamsApi.abortController = null;
        }
    },

    // Initialize abort controller before making requests
    initAbortController: () => {
        loamsApi.cancelPendingRequests();
        loamsApi.abortController = new AbortController();
        return loamsApi.abortController.signal;
    },



    files: {
        upload: async (file, category = 'documents', subfolder = null, config = {}) => {
          console.log('[LOAMS] Uploading file:', {
            fileName: file.name,
            category,
            subfolder,
            size: file.size
          });
    
          try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('category', category);
            if (subfolder) {
              formData.append('subfolder', subfolder);
            }
    
            const response = await pythonApi.post('/api/files/upload', formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              },
              timeout: 300000, // 5 minutes for large files
              ...config
            });
    
            console.log('[LOAMS] File upload successful:', response.data);
            return response;
          } catch (error) {
            console.error('[LOAMS] File upload failed:', error);
            // Enhance error message based on the error type
            if (error.code === 'ECONNABORTED') {
              throw new Error('Upload timed out. Please try again with a smaller file or check your connection.');
            }
            if (error.response?.status === 413) {
              throw new Error('File is too large. Maximum size allowed is 50MB.');
            }
            throw error;
          }
        },
    
        list: async (params = {}) => {
          console.log('[LOAMS] Listing files with params:', params);
          try {
            const response = await pythonApi.get('/api/files/list', {
              params,
              timeout: 30000 // 30 seconds timeout
            });
    
            // Transform the response to ensure consistent structure
            const files = response.data.files?.map(file => ({
              id: file.id,
              name: file.name,
              size: file.size,
              modified: file.modified,
              category: file.category,
              mime_type: file.mime_type,
              path: file.path
            })) || [];
    
            return {
              data: {
                files,
                total: files.length
              }
            };
          } catch (error) {
            console.error('[LOAMS] List files failed:', error);
            throw error;
          }
        },
    
        download: async (fileId, config = {}) => {
          console.log('[LOAMS] Downloading file:', fileId);
          try {
            const response = await pythonApi.get(`/api/files/download/${fileId}`, {
              responseType: 'blob',
              timeout: 300000, // 5 minutes for large files
              ...config
            });
    
            // If not requesting raw blob, create download
            if (!config.responseType || config.responseType !== 'blob') {
              const blob = new Blob([response.data], {
                type: response.headers['content-type']
              });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = response.headers['content-disposition']?.split('filename=')[1] || 'download';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            }
    
            return response;
          } catch (error) {
            console.error('[LOAMS] Download failed:', error);
            if (error.code === 'ECONNABORTED') {
              throw new Error('Download timed out. Please try again later.');
            }
            throw error;
          }
        },
    
        delete: async (fileId) => {
          if (!fileId) {
            throw new Error('File ID is required for deletion');
          }
    
          console.log('[LOAMS] Deleting file:', fileId);
          try {
            const response = await pythonApi.delete(`/api/files/delete/${fileId}`);
            console.log('[LOAMS] File deleted successfully');
            return response;
          } catch (error) {
            console.error('[LOAMS] Delete failed:', error);
            // Enhance error messages based on response
            if (error.response?.status === 404) {
              throw new Error('File not found or already deleted');
            }
            if (error.response?.status === 403) {
              throw new Error('You do not have permission to delete this file');
            }
            throw error;
          }
        },
    
        getStorageInfo: async () => {
          console.log('[LOAMS] Getting storage info');
          try {
            const response = await pythonApi.get('/api/files/storage-info');
            console.log('[LOAMS] Storage info retrieved:', response.data);
            return response;
          } catch (error) {
            console.error('[LOAMS] Get storage info failed:', error);
            throw error;
          }
        },
    
        createBackup: async (options = {}) => {
          console.log('[LOAMS] Creating backup with options:', options);
          try {
            const response = await pythonApi.post('/api/files/backup', options, {
              responseType: 'blob',
              timeout: 600000 // 10 minutes for large backups
            });
    
            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `backup_${new Date().toISOString().split('T')[0]}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
    
            return { success: true };
          } catch (error) {
            console.error('[LOAMS] Backup failed:', error);
            throw error;
          }
        }
      },
    


    





      getMonthlyReportData: async (year, month) => {
        console.log('[LOAMS] Fetching monthly report data:', { year, month });
        try {
            // Format month with leading zero
            const formattedMonth = String(month).padStart(2, '0');
            
            const response = await pythonApi.get('/api/report/monthly', {
                params: { 
                    year,
                    month: formattedMonth
                },
                headers: {
                    'Accept': 'application/json'
                },
                timeout: 30000  // 30 second timeout
            });
    
            if (!response.data) {
                throw new Error('No data received from server');
            }
    
            console.log('[LOAMS] Received report data:', {
                projects: response.data.summary?.activeProjects || 0,
                invoices: response.data.summary?.invoicesIssued || 0,
                details: response.data.details?.length || 0
            });
    
            return response.data;
    
        } catch (error) {
            console.error('[LOAMS] Monthly report error:', error);
            throw error;
        }
    },
      
















    exportData: async (columns, filters = {}) => {
        try {
            const params = new URLSearchParams();
            
            // Add column selections
            if (columns?.invoice) {
                columns.invoice.forEach(col => params.append('invoice[]', col));
            }
            if (columns?.status) {
                columns.status.forEach(col => params.append('status[]', col));
            }
            
            // Handle serial number range - strip any non-numeric characters
            if (filters.startSerial) {
                const startNum = filters.startSerial.replace(/[^0-9]/g, '');
                if (startNum) {
                    params.append('start_serial', startNum);
                }
            }
            if (filters.endSerial) {
                const endNum = filters.endSerial.replace(/[^0-9]/g, '');
                if (endNum) {
                    params.append('end_serial', endNum);
                }
            }
            
            // Handle service type
            if (filters.serviceType && filters.serviceType !== 'all') {
                params.append('service_type', filters.serviceType);
            }

            // Handle months with validation
            if (filters.months?.length > 0) {
                filters.months.forEach(month => {
                    // Validate month format (YYYY-MM)
                    if (/^\d{4}-\d{2}$/.test(month)) {
                        params.append('months[]', month);
                    }
                });
            }

            console.log('[LOAMS] Export request params:', params.toString());

            const response = await pythonApi.get('/api/export', {
                params: params,
                paramsSerializer: {
                    indexes: null // Required for array parameters
                },
                timeout: 30000 // 30 seconds timeout for potentially large exports
            });

            // Log success
            console.log('[LOAMS] Export successful:', {
                records: response.data?.length || 0,
                filters: filters
            });

            return response;
        } catch (error) {
            // Enhanced error logging
            console.error('[LOAMS] Export error:', {
                error: error.message,
                status: error.response?.status,
                data: error.response?.data,
                filters: filters
            });

            // Rethrow with more specific message if available
            if (error.response?.data?.details) {
                throw new Error(error.response.data.details);
            }
            throw error;
        }
    },




    // In api.js - Update the generateSingleInvoice function
generateSingleInvoice: async (formData) => {
    console.log('[LOAMS] Generating single invoice');
    try {
        // Log what we're sending
        for (let pair of formData.entries()) {
            console.log('[LOAMS] FormData contains:', pair[0], 
                pair[1] instanceof File ? `File: ${pair[1].name} (${pair[1].size} bytes)` : pair[1]);
        }

        const response = await pythonApi.post('/generate', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Accept': '*/*'
            },
            responseType: 'blob',   
            timeout: 60000 // 1 minute timeout
        });

        // ✅ FIXED: Check content type and handle accordingly
        const contentType = response.headers['content-type'];
        console.log('[LOAMS] Response content type:', contentType);
        
        // Verify we got a valid response
        if (!contentType) {
            throw new Error('No content type in response');
        }

        // ✅ UPDATED: Accept both PDF and ZIP responses
        if (contentType.includes('application/pdf')) {
            console.log('[LOAMS] Single PDF invoice generation successful');
        } else if (contentType.includes('application/zip')) {
            console.log('[LOAMS] ZIP invoice generation successful');
        } else {
            console.warn('[LOAMS] Unexpected content type:', contentType);
            // Still proceed as it might be valid
        }

        return response;

    } catch (error) {
        console.error('[LOAMS] Single invoice generation error:', error);
        if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
            throw new Error('Network error occurred. Please try again or check your connection.');
        }
        throw error;
    }
},




















    // Add method to get available serial number range
    getSerialRange: async () => {
        try {
            const response = await pythonApi.get('/api/invoice-range');
            return response.data;
        } catch (error) {
            console.error('[LOAMS] Failed to get serial range:', error);
            throw error;
        }
    },


    // Update these methods in the loamsApi object in api.js

// Get all unique clusters
getClusters: async () => {
    console.log('[LOAMS] Fetching clusters');
    try {
        const response = await pythonApi.get('/api/file/clusters');  // Updated endpoint
        console.log('[LOAMS] Found clusters:', response.data.length);
        return response;
    } catch (error) {
        console.error('[LOAMS] Get clusters error:', error);
        throw error;
    }
},

// Get agreements for a specific cluster
getClusterAgreements: async (cluster) => {
    console.log('[LOAMS] Fetching agreements for cluster:', cluster);
    try {
        const response = await pythonApi.get(`/api/file/clusters/${cluster}/agreements`);  // Updated endpoint
        console.log('[LOAMS] Found agreements:', response.data.length);
        return response;
    } catch (error) {
        console.error('[LOAMS] Get agreements error:', error);
        throw error;
    }
},









getYearlyReportAllData: async () => {
  console.log('[LOAMS] Fetching yearly report data for all years');
  try {
    const response = await pythonApi.get('/api/report/yearly/all', {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      timeout: 60000
    });
    
    // The response is already in the right format, so just return it directly
    return response.data;
  } catch (error) {
    console.error('[LOAMS] All years report error:', error);
    throw error;
  }
},

exportYearlyReportAllYears: async () => {
  console.log('[LOAMS] Exporting yearly report for all years');
  try {
    const response = await pythonApi.get('/api/report/yearly/export/all', {
      responseType: 'blob',
      timeout: 120000 // 2 minute timeout for Excel generation of all years
    });
    
    // Create download
    const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Yearly_Report_All_Years.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    console.error('[LOAMS] All years report export error:', error);
    throw new Error('Failed to export all years report');
  }
},

exportYearlyPdfAllYears: async (yearlyData) => {
  console.log('[LOAMS] Exporting yearly PDF report for all years');
  try {
    // Import necessary libraries
    const jspdfModule = await import('jspdf');
    const jsPDF = jspdfModule.default;
    const autotableModule = await import('jspdf-autotable');
    const autoTable = autotableModule.default;
    
    // Create new PDF document in landscape orientation with A3 format
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a3' // Use A3 for more space to fit all columns properly
    });
    
    // Add company header and title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('PHOENICIAN TECHNICAL SERVICES LLC', doc.internal.pageSize.width / 2, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Central Plaza, Office 399, Dubai Investment Park 1', doc.internal.pageSize.width / 2, 28, { align: 'center' });
    doc.text('+971 4 8882 9464 B4', doc.internal.pageSize.width / 2, 34, { align: 'center' });
    
    // Add report title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Yearly Summary Report - All Years', doc.internal.pageSize.width / 2, 45, { align: 'center' });
    
    // Create summary table with data across all years
    if (yearlyData && yearlyData.yearly_data && yearlyData.yearly_data.length > 0) {
      // Add year-by-year summary
      const yearlySummaryHeaders = [
        [
          { content: 'Year', styles: { halign: 'left', fontStyle: 'bold' } },
          { content: 'Active Projects (AED)', styles: { halign: 'right', fontStyle: 'bold' } },
          { content: 'Completion Certificates (AED)', styles: { halign: 'right', fontStyle: 'bold' } },
          { content: 'Tax Invoice Issued (AED)', styles: { halign: 'right', fontStyle: 'bold' } },
          { content: 'Payment Received (AED)', styles: { halign: 'right', fontStyle: 'bold' } },
          { content: 'Balance Receivable (AED)', styles: { halign: 'right', fontStyle: 'bold' } }
        ]
      ];
      
      // Calculate totals across all years
      let totalActiveAmount = 0;
      let totalCcAmount = 0;
      let totalInvoiceAmount = 0;
      let totalPaidAmount = 0;
      let totalReceivableAmount = 0;
      
      // Prepare yearly summary data
      const yearlySummaryData = yearlyData.yearly_data.map(yearData => {
        const year = yearData.year;
        const activeAmount = yearData.summary.activeProjects.amount || 0;
        const ccAmount = yearData.summary.completionCertificates.amount || 0;
        const invoiceAmount = yearData.summary.invoiceProcessed?.amount || 0;
        const paidAmount = yearData.summary.paymentsReceived.amount || 0;
        const receivableAmount = (invoiceAmount - paidAmount) || 0;
        
        // Update totals
        totalActiveAmount += activeAmount;
        totalCcAmount += ccAmount;
        totalInvoiceAmount += invoiceAmount;
        totalPaidAmount += paidAmount;
        totalReceivableAmount += receivableAmount;
        
        return [
          { content: year.toString(), styles: { halign: 'left' } },
          { content: formatCurrency(activeAmount), styles: { halign: 'right' } },
          { content: formatCurrency(ccAmount), styles: { halign: 'right' } },
          { content: formatCurrency(invoiceAmount), styles: { halign: 'right' } },
          { content: formatCurrency(paidAmount), styles: { halign: 'right' } },
          { content: formatCurrency(receivableAmount), styles: { halign: 'right', textColor: receivableAmount > 0 ? [174, 39, 50] : [0, 0, 0] } }
        ];
      });
      
      // Add total row
      yearlySummaryData.push([
        { content: 'Total', styles: { halign: 'left', fontStyle: 'bold' } },
        { content: formatCurrency(totalActiveAmount), styles: { halign: 'right', fontStyle: 'bold' } },
        { content: formatCurrency(totalCcAmount), styles: { halign: 'right', fontStyle: 'bold' } },
        { content: formatCurrency(totalInvoiceAmount), styles: { halign: 'right', fontStyle: 'bold' } },
        { content: formatCurrency(totalPaidAmount), styles: { halign: 'right', fontStyle: 'bold' } },
        { content: formatCurrency(totalReceivableAmount), styles: { halign: 'right', fontStyle: 'bold', textColor: [174, 39, 50] } }
      ]);
      
      // Add yearly summary table
      autoTable(doc, {
        startY: 60,
        head: yearlySummaryHeaders,
        body: yearlySummaryData,
        theme: 'grid',
        styles: { 
          fontSize: 10,
          cellPadding: 3,
          lineColor: [200, 200, 200],
          lineWidth: 0.1
        },
        headStyles: { 
          fillColor: [162, 115, 46], 
          textColor: [255, 255, 255],
          fontSize: 10
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        }
      });
      
      // Add detailed year section for each year
      let currentY = doc.autoTable.previous.finalY + 15;
      
      // For each year, create a detailed breakout
      for (const yearData of yearlyData.yearly_data) {
        // Add new page if not enough space
        if (currentY > doc.internal.pageSize.height - 100) {
          doc.addPage();
          currentY = 20;
        }
        
        // Add year header
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`Year ${yearData.year} Monthly Breakdown`, 20, currentY);
        currentY += 10;
        
        // Create monthly amount data for this year
        const monthlyAmountHeaders = [
          [
            { content: 'Month', styles: { halign: 'left', fontStyle: 'bold' } },
            { content: 'Active Projects', styles: { halign: 'right', fontStyle: 'bold' } },
            { content: 'Comp. Certificates', styles: { halign: 'right', fontStyle: 'bold' } },
            { content: 'Processed', styles: { halign: 'right', fontStyle: 'bold' } },
            { content: 'Payment Received', styles: { halign: 'right', fontStyle: 'bold' } },
            { content: 'Balance Receivable', styles: { halign: 'right', fontStyle: 'bold' } }
          ]
        ];
        
        // Prepare the monthly data
        const monthlyAmountData = yearData.monthly_details.map(month => {
          // Calculate Balance Receivable as Invoice Processed - Payment Received
          const balanceReceivable = (month.processed_amount || 0) - (month.paid_amount || 0);
          
          return [
            { content: month.month.trim(), styles: { halign: 'left' } },
            { content: formatCurrency(month.active_projects_amount), styles: { halign: 'right' } },
            { content: formatCurrency(month.cc_amount), styles: { halign: 'right' } },
            { content: formatCurrency(month.processed_amount), styles: { halign: 'right' } },
            { content: formatCurrency(month.paid_amount), styles: { halign: 'right' } },
            { content: formatCurrency(balanceReceivable), styles: { halign: 'right', textColor: balanceReceivable > 0 ? [174, 39, 50] : [0, 0, 0] } }
          ];
        });
        
        // Add monthly amount table for this year
        autoTable(doc, {
          startY: currentY,
          head: monthlyAmountHeaders,
          body: monthlyAmountData,
          theme: 'grid',
          styles: { 
            fontSize: 8,
            cellPadding: 2,
            lineColor: [200, 200, 200],
            lineWidth: 0.1
          },
          headStyles: { 
            fillColor: [162, 115, 46], 
            textColor: [255, 255, 255],
            fontSize: 8
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245]
          }
        });
        
        // Update current Y position for next year
        currentY = doc.autoTable.previous.finalY + 20;
      }
    } else {
      // No data available message
      doc.setFontSize(12);
      doc.text('No yearly data available.', 20, 70);
    }
    
    // Add download info at bottom of page
    const now = new Date();
    // Format date according to Dubai time (UTC+4)
    const dubaiTime = new Date(now.getTime() + (4 * 60 * 60 * 1000)); // UTC+4 for Dubai
    const dateStr = dubaiTime.toLocaleString('en-AE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Dubai'
    });
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text(`Downloaded by - ${getUserName()} | ${dateStr}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10, { align: 'right' });
    
    // Save the PDF
    doc.save(`Yearly_Report_All_Years.pdf`);
    
    return { success: true };
  } catch (error) {
    console.error('[LOAMS] All years PDF export error:', error);
    throw error;
  }
},




// Get invoices for a specific agreement
getAgreementInvoices: async (agreementRef) => {
    console.log('[LOAMS] Fetching invoices for agreement:', agreementRef);
    try {
        const response = await pythonApi.get(`/api/file/agreements/${agreementRef}/invoices`);  // Updated endpoint
        console.log('[LOAMS] Found invoices:', response.data.length);
        return response;
    } catch (error) {
        console.error('[LOAMS] Get invoices error:', error);
        throw error;
    }
},






getFileClusters: async () => {
    console.log('[LOAMS] Fetching file clusters');
    try {
        const response = await pythonApi.get('/api/file/clusters');
        console.log('[LOAMS] Found file clusters:', response.data.length);
        return response;
    } catch (error) {
        console.error('[LOAMS] Get file clusters error:', error);
        throw error;
    }
  },
  
  getFileClusterAgreements: async (cluster) => {
    console.log('[LOAMS] Fetching file agreements for cluster:', cluster);
    try {
        const response = await pythonApi.get(`/api/file/clusters/${cluster}/agreements`);
        console.log('[LOAMS] Found file agreements:', response.data.length);
        return response;
    } catch (error) {
        console.error('[LOAMS] Get file agreements error:', error);
        throw error;
    }
  },




  

  uploadWithInvoiceCreation: async (formData) => {
    console.log('[LOAMS] Enhanced upload with invoice creation');
    
    // Log FormData contents for debugging
    for (let pair of formData.entries()) {
        console.log('[LOAMS] FormData contains:', pair[0], 
            pair[1] instanceof File ? `File: ${pair[1].name} (${pair[1].size} bytes)` : pair[1]);
    }
    
    try {
        // Use enhanced upload endpoint
        const response = await pythonApi.post('/api/certificate/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            timeout: 60000,
        });
        
        console.log('[LOAMS] Enhanced upload response:', {
            status: response.status,
            success: response.data?.success,
            certificate: response.data?.certificate,
            invoice: response.data?.invoice,
            validation: response.data?.validation
        });
        
        return response;
    } catch (error) {
        console.error('[LOAMS] Enhanced upload error:', {
            message: error.message,
            status: error.response?.status,
            data: error.response?.data
        });
        
        // Enhanced error handling for frontend
        if (error.response?.data?.sequential_validation_failed) {
            const enhancedError = new Error(error.response.data.error || 'Sequential validation failed');
            enhancedError.response = error.response;
            enhancedError.sequential_validation_failed = true;
            enhancedError.missing_month = error.response.data.missing_month;
            throw enhancedError;
        }
        
        if (error.response?.data?.certificate_exists) {
            const enhancedError = new Error('Certificate already exists for this month');
            enhancedError.response = error.response;
            enhancedError.certificate_exists = true;
            throw enhancedError;
        }
        
        throw error;
    }
},

checkCertificate: async (cluster, agreementRef, month) => {
    console.log('[LOAMS] Checking certificate:', { cluster, agreementRef, month });
    try {
        const response = await pythonApi.get('/api/certificate/check', {
            params: {
                cluster,
                agreement_ref: agreementRef,
                month
            }
        });
        return response.data;
    } catch (error) {
        console.error('[LOAMS] Certificate check failed:', error);
        throw error;
    }
},






// Monthly Project Report methods
getMonthlyProjectReport: async (year, month) => {
    console.log('[LOAMS] Fetching monthly project report:', { year, month });
    try {
      // Format month with leading zero
      const formattedMonth = String(month).padStart(2, '0');
      
      const response = await pythonApi.get('/api/reports/monthly', {
        params: { 
          year,
          month: formattedMonth
        },
        headers: {
          'Accept': 'application/json'
        },
        timeout: 30000, // 30 second timeout for report generation
        retry: 2,       // Allow 2 retries
        retryDelay: 1000 // Wait 1 second between retries
      });
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      
      console.log('[LOAMS] Successfully fetched monthly report data:', {
        projects: response.data?.summary?.activeProjects || 0,
        invoices: response.data?.summary?.invoicesIssued || 0, 
        details: response.data?.details?.length || 0
      });
      
      return response.data;
    } catch (error) {
      console.error('[LOAMS] Monthly project report error:', error);
      
      // Enhanced error details
      if (error.response?.status === 404) {
        throw new Error('Report service not available');
      }
      
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      
      if (error.code === 'ECONNABORTED') {
        throw new Error('Report generation timed out. The selected period may contain too much data.');
      }
      
      throw new Error(error.message || 'Failed to load report data');
    }
  },
  
  exportMonthlyProjectReport: async (year, month) => {
    console.log('[LOAMS] Exporting monthly project report:', { year, month });
    try {
      // Format month with leading zero
      const formattedMonth = String(month).padStart(2, '0');
      const monthName = new Date(year, month-1, 1).toLocaleString('default', { month: 'long' });
      
      // First get the report data
      const reportData = await loamsApi.getMonthlyProjectReport(year, formattedMonth);
      
      // Import XLSX dynamically if needed
      const XLSX = window.XLSX || await import('xlsx').then(module => module.default);
      if (!XLSX) {
        throw new Error('XLSX library not available');
      }
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Create summary sheet
      const summaryData = [
        ['PHOENICIAN TECHNICAL SERVICES LLC'],
        ['Central Plaza, Office 399, Dubai Investment Park 1'],
        ['+971 4 8882 9464 B4'],
        [''],
        ['Monthly Report per Project'],
        [`Year: ${year}`],
        [`Month: ${monthName}`],
        [''],
        ['', 'Count', 'Amount'],
        ['Active Projects', reportData.summary.activeProjects, reportData.summary.activeAmount.toFixed(2)],
        ['Completion Certificates', reportData.summary.completionCertificates, reportData.summary.certificateAmount.toFixed(2)],
        ['Tax Invoice Issued', reportData.summary.invoicesIssued, reportData.summary.invoiceAmount.toFixed(2)],
        ['Payment Received', reportData.summary.paymentsReceived, reportData.summary.paymentAmount.toFixed(2)],
        ['']
      ];
      
      // Create details sheet data
      const detailsData = [
        ['Cluster', 'PO No.', 'Invoice No', 'CC No', 'Status', 'Amount', 'Payment Bank']
      ];
      
      reportData.details.forEach(item => {
        detailsData.push([
          item.cluster,
          item.poNumber,
          item.invoiceNumber,
          item.ccNumber,
          item.status,
          item.amount.toFixed(2),
          item.bank || ''
        ]);
      });
      
      // Add sheets to workbook
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');
      
      const detailsSheet = XLSX.utils.aoa_to_sheet(detailsData);
      XLSX.utils.book_append_sheet(wb, detailsSheet, 'Details');
      
      // Style sheets
      summarySheet['!cols'] = [{ wch: 25 }, { wch: 10 }, { wch: 15 }];
      detailsSheet['!cols'] = [
        { wch: 25 }, { wch: 20 }, { wch: 15 }, 
        { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 15 }
      ];
      
      // Generate file and trigger download
      XLSX.writeFile(wb, `Monthly_Report_${year}_${monthName}.xlsx`);
      
      console.log('[LOAMS] Successfully exported monthly report to Excel');
      return { success: true };
    } catch (error) {
      console.error('[LOAMS] Monthly report export error:', error);
      throw new Error(`Failed to export report: ${error.message}`);
    }
  },






  getYearlyReportData: async (year) => {
    console.log('[LOAMS] Fetching yearly report data for', year);
    try {
      const response = await pythonApi.get('/api/report/yearly', {
        params: { year },
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        timeout: 30000 // 30 second timeout for report generation
      });
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      console.log('[LOAMS] Successfully received yearly report data');
      return response;
    } catch (error) {
      console.error('[LOAMS] Yearly report error:', error);
      
      if (error.response?.status === 404) {
        throw new Error('Report service not available');
      }
      
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      
      if (error.code === 'ECONNABORTED') {
        throw new Error('Report generation timed out. The selected year may contain too much data.');
      }
      
      throw new Error(error.message || 'Failed to load report data');
    }
  },
  
  exportYearlyReport: async (year) => {
    console.log('[LOAMS] Exporting yearly report for year:', year);
    try {
      const response = await pythonApi.get('/api/report/yearly/export', {
        params: { year },
        responseType: 'blob',
        timeout: 60000 // 1 minute timeout for Excel generation
      });
      
      // Create download
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Yearly_Report_${year}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('[LOAMS] Successfully exported yearly report');
      return { success: true };
    } catch (error) {
      console.error('[LOAMS] Yearly report export error:', error);
      
      if (error.response?.status === 404) {
        throw new Error('Export service not available');
      }
      
      if (error.code === 'ECONNABORTED') {
        throw new Error('Export timed out. The report may be too large.');
      }
      
      throw new Error('Failed to export yearly report');
    }
  },


  // Add these functions to your loamsApi object in api.js

// Get list of projects for dropdown
getProjectsList: async (searchTerm = '') => {
    console.log('[LOAMS] Fetching projects list with search:', searchTerm);
    try {
      const response = await pythonApi.get('/api/projects', {
        params: { search: searchTerm },
        headers: {
          'Accept': 'application/json'
        },
        timeout: 30000 // 30 second timeout
      });
  
      if (!response.data) {
        throw new Error('No data received from server');
      }
  
      console.log('[LOAMS] Found projects:', response.data.length);
      return response.data;
    } catch (error) {
      console.error('[LOAMS] Projects list error:', error);
      throw new Error(error.message || 'Failed to load projects list');
    }
  },
  
  // Get project report data
  getProjectReportData: async (poNumber, projectName) => {
    console.log('[LOAMS] Fetching project report data:', { poNumber, projectName });
    try {
      // Build query parameters
      const params = {};
      if (poNumber) params.po_number = poNumber;
      if (projectName) params.project_name = projectName;
      
      const response = await pythonApi.get('/api/report/project', {
        params,
        headers: {
          'Accept': 'application/json'
        },
        timeout: 60000 // 60 second timeout for detailed project data
      });
  
      if (!response.data) {
        throw new Error('No data received from server');
      }
  
      console.log('[LOAMS] Project report data received:', {
        project: response.data.project?.name,
        months: response.data.monthly_details?.length
      });
  
      return response.data;
    } catch (error) {
      console.error('[LOAMS] Project report error:', error);
      
      // Enhanced error handling
      if (error.response?.status === 404) {
        throw new Error('Project not found. Please check the PO number or project name.');
      }
      
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      
      throw new Error(error.message || 'Failed to load project data');
    }
  },
  
  // Export project report to Excel
  exportProjectReport: async (poNumber) => {
    console.log('[LOAMS] Exporting project report for PO:', poNumber);
    try {
      if (!poNumber) {
        throw new Error('PO number is required for export');
      }
      
      const response = await pythonApi.get('/api/report/project/export', {
        params: { po_number: poNumber },
        responseType: 'blob',
        timeout: 30000 // 30 second timeout for Excel generation
      });
      
      // Create download
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Project_Report_${poNumber.replace(/[\/\\:*?"<>|]/g, '_')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error('[LOAMS] Project report export error:', error);
      throw new Error('Failed to export project report: ' + (error.message || 'Unknown error'));
    }
  },



  // ADD this new method to your loamsApi object:
checkSequentialEligibility: async (cluster, agreementRef, month) => {
    const [year, monthNum] = month.split('-');
    const monthInt = parseInt(monthNum);
    
    // Jan-Apr always allowed
    if (monthInt <= 4) {
        return {
            eligible: true,
            reason: `Month ${month} is in the early period (Jan-Apr)`,
            requires_previous: false
        };
    }
    
    // May+ requires previous month
    const prevMonth = monthInt === 1 ? 12 : monthInt - 1;
    const prevYear = monthInt === 1 ? parseInt(year) - 1 : parseInt(year);
    const previousMonth = `${prevYear}-${prevMonth.toString().padStart(2, '0')}`;
    
    const existenceCheck = await loamsApi.certificates.checkExists(cluster, agreementRef, previousMonth);
    
    if (existenceCheck.exists) {
        return {
            eligible: true,
            reason: `Previous month ${previousMonth} certificate exists`,
            requires_previous: true,
            previous_month: previousMonth
        };
    } else {
        return {
            eligible: false,
            reason: `Cannot upload ${month}. Previous month (${previousMonth}) is missing.`,
            requires_previous: true,
            missing_month: previousMonth
        };
    }
},





  














  certificate: {
    upload: async (formData) => {
        console.log('[LOAMS] Uploading certificate');
        try {
            const response = await pythonApi.post('/api/certificate/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                timeout: 60000
            });
            return response;
        } catch (error) {
            console.error('[LOAMS] Certificate upload failed:', error);
            throw error;
        }
    },

    list: async (cluster, agreementRef) => {
        console.log('[LOAMS] Fetching certificates for', { cluster, agreementRef });
        try {
            const response = await pythonApi.get('/api/certificate/list', {
                params: {
                    cluster,
                    agreement_ref: agreementRef
                }
            });
            console.log('[LOAMS] Found certificates:', response.data?.certificates?.length || 0);
            return response;
        } catch (error) {
            console.error('[LOAMS] Certificate list error:', error);
            throw error;
        }
    },

    getPreviewUrl: async (certificateKey) => {
        console.log('[LOAMS] Getting preview URL for:', certificateKey);
        try {
            const encodedKey = encodeURIComponent(certificateKey);
            const response = await pythonApi.get(`/api/certificate/preview/${encodedKey}`);
            return response.data.url;
        } catch (error) {
            console.error('[LOAMS] Preview URL error:', error);
            throw error;
        }
    },

    checkExists: async (cluster, agreementRef, month) => {
        console.log('[LOAMS] Checking certificate existence:', { cluster, agreementRef, month });
        try {
            const response = await pythonApi.get('/api/certificate/list', {
                params: {
                    cluster,
                    agreement_ref: agreementRef
                }
            });
            
            const certificates = response.data?.certificates || [];
            const exists = certificates.some(cert => cert.month === month);
            
            return {
                exists,
                certificate: exists ? 
                    certificates.find(cert => cert.month === month) : 
                    null
            };
        } catch (error) {
            console.error('[LOAMS] Check existence error:', error);
            throw error;
        }
    }
},








// In api.js, update the documents object
documents: {
    getDocument: async (identifier, type) => {
        console.log('[LOAMS] Fetching document:', { identifier, type });
        try {
            // Construct the path based on document type
            let path;
            switch(type) {
                case 'Service Agreement':
                    path = `documents/PO/${identifier}.pdf`;
                    break;
                case 'Completion Certificate':
                    path = `documents/CC-LOAMS/CC-${identifier}.pdf`; // Use CC- prefix
                    break;
                default: // Invoice
                    path = `documents/INVOICES/${identifier}.pdf`;
            }
            
            console.log('[LOAMS] Fetching with path:', path);
            
            const response = await pythonApi.get('/api/documents/view', {
                params: { path }
            });
            return response;
        } catch (error) {
            console.error('[LOAMS] Get document error:', error);
            throw error;
        }
    },




    downloadDocument: async (url) => {
        console.log('[LOAMS] Downloading document');
        try {
            const response = await pythonApi.get(url, {
                responseType: 'blob'
            });

            // Create download
            const blob = new Blob([response.data], { 
                type: response.headers['content-type'] 
            });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            
            // Get filename from Content-Disposition header or use default
            const contentDisposition = response.headers['content-disposition'];
            const fileName = contentDisposition ? 
                contentDisposition.split('filename=')[1].replace(/"/g, '') : 
                'document.pdf';
            
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
            
            return { success: true };
        } catch (error) {
            console.error('[LOAMS] Document download failed:', error);
            throw error;
        }
    }
},


getAgreementDetails: async (cluster, agreementRef) => {
  console.log('[LOAMS] Fetching agreement details:', { cluster, agreementRef });
  try {
    const response = await pythonApi.get('/api/agreement-details', {
      params: { cluster, agreementRef },
      headers: {
        'Accept': 'application/json'
      },
      timeout: 30000 // 30 second timeout
    });
    
    console.log('[LOAMS] Agreement details response:', response.data);
    return response.data;
  } catch (error) {
    console.error('[LOAMS] Error fetching agreement details:', error);
    
    // Handle specific error cases
    if (error.response?.status === 404) {
      console.warn(`[LOAMS] No agreement found for ${cluster}/${agreementRef}`);
      return { success: false, error: 'Agreement not found' };
    }
    
    throw error;
  }
},

getServiceType: async (cluster, agreementRef) => {
  console.log('[LOAMS] Fetching service type for:', { cluster, agreementRef });
  try {
    const response = await loamsApi.getAgreementDetails(cluster, agreementRef);
    
    if (response.success && response.data) {
      // Return formatted service type if available, otherwise original service type
      return response.data.formatted_service_type || response.data.service_type || '';
    }
    
    // Client-side fallback formatting if server didn't return a formatted type
    if (response.data?.service_type) {
      return formatServiceType(response.data.service_type);
    }
    
    return '';
  } catch (error) {
    console.error('[LOAMS] Error fetching service type:', error);
    // Return empty string on error (for graceful fallback)
    return '';
  }
},


getAllMonthsReport: async () => {
    console.log('[LOAMS] Fetching all months report');
    try {
        const response = await pythonApi.get('/api/certificates/report/all-months', {
            headers: {
                'Accept': 'application/json'
            },
            timeout: 60000 // Extended timeout for processing all months
        });
        
        if (!response.data) {
            throw new Error('No data received from server');
        }
        
        if (!response.data.success) {
            throw new Error(response.data.error || 'Failed to load all months report');
        }
        
        console.log('[LOAMS] All months report loaded successfully:', {
            totalAgreements: response.data.statistics?.total_agreements || 0,
            totalCertificates: response.data.statistics?.total_certificates || 0,
            monthsIncluded: response.data.monthly_breakdown?.length || 0
        });
        
        return response.data;
    } catch (error) {
        console.error('[LOAMS] All months report error:', error);
        
        // If the backend doesn't support all months endpoint, fall back to client-side aggregation
        if (error.response?.status === 404) {
            console.log('[LOAMS] Backend does not support all months endpoint, using client-side aggregation');
            return await loamsApi.certificates.getAllMonthsReportFallback();
        }
        
        throw error;
    }
},

// Fallback method for all months report (client-side aggregation)
getAllMonthsReportFallback: async () => {
    console.log('[LOAMS] Using fallback method for all months report');
    try {
        // Get all available months from certificates
        const certificatesResponse = await loamsApi.certificateService.list();
        
        if (!certificatesResponse.data?.success || !certificatesResponse.data.certificates) {
            throw new Error('Failed to get certificates list');
        }
        
        // Extract unique months
        const uniqueMonths = [...new Set(certificatesResponse.data.certificates.map(cert => {
            const fileName = cert.displayName;
            const match = fileName.match(/(\d{4}-\d{2})\.pdf$/i);
            return match ? match[1] : null;
        }))].filter(Boolean);
        
        console.log('[LOAMS] Found months for aggregation:', uniqueMonths);
        
        // Fetch reports for each month
        const monthlyReports = [];
        for (const month of uniqueMonths) {
            try {
                console.log(`[LOAMS] Fetching report for month: ${month}`);
                const monthReport = await loamsApi.certificates.getReport(month);
                if (monthReport.success) {
                    monthlyReports.push({
                        month,
                        data: monthReport
                    });
                }
            } catch (monthError) {
                console.warn(`[LOAMS] Failed to fetch report for month ${month}:`, monthError);
                // Continue with other months even if one fails
            }
        }
        
        if (monthlyReports.length === 0) {
            throw new Error('No monthly reports could be loaded');
        }
        
        // Aggregate all the monthly data
        const allAgreements = [];
        const monthlyBreakdown = [];
        let totalAgreements = 0;
        let totalWithCertificates = 0;
        let totalCertificates = 0;
        const uniqueProjects = new Set();
        
        monthlyReports.forEach(({ month, data }) => {
            // Add month info to each agreement
            data.agreements.forEach(agreement => {
                allAgreements.push({
                    ...agreement,
                    month: month,
                    monthLabel: formatMonthLabel(month)
                });
            });
            
            // Collect statistics
            totalAgreements += data.statistics.total_agreements || 0;
            totalWithCertificates += data.statistics.total_with_certificates || 0;
            totalCertificates += data.statistics.total_certificates || 0;
            
            // Track unique projects
            data.agreements.forEach(agreement => {
                uniqueProjects.add(agreement.project);
            });
            
            // Monthly breakdown
            monthlyBreakdown.push({
                month: formatMonthLabel(month),
                total: data.statistics.total_agreements || 0,
                uploaded: data.statistics.total_with_certificates || 0,
                percentage: data.statistics.completion_rate || 0
            });
        });
        
        const completionRate = totalAgreements > 0 ? 
            Math.round((totalWithCertificates / totalAgreements) * 100) : 0;
        
        console.log('[LOAMS] All months aggregation complete:', {
            totalAgreements,
            totalWithCertificates,
            totalCertificates,
            completionRate,
            uniqueProjects: uniqueProjects.size
        });
        
        return {
            success: true,
            agreements: allAgreements,
            statistics: {
                total_agreements: totalAgreements,
                total_with_certificates: totalWithCertificates,
                total_certificates: totalCertificates,
                completion_rate: completionRate,
                unique_projects: uniqueProjects.size
            },
            monthly_breakdown: monthlyBreakdown.sort((a, b) => a.month.localeCompare(b.month)),
            method: 'client_aggregation' // Flag to indicate this was client-side aggregated
        };
        
    } catch (error) {
        console.error('[LOAMS] Fallback all months report error:', error);
        throw new Error(`Failed to generate all months report: ${error.message}`);
    }
},

// Helper function to format month labels
formatMonthLabel: (monthStr) => {
    try {
        const [year, month] = monthStr.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1);
        return `${date.toLocaleString('default', { month: 'short' })} ${year}`;
    } catch (error) {
        console.error('[LOAMS] Error formatting month label:', error);
        return monthStr; // Return original if formatting fails
    }
},















    // Update this function in the loamsApi object (in api.js)
// Update this function in the loamsApi object (in api.js)
updateInvoice: async (invoiceId, invoiceData) => {
  console.log('[LOAMS] Updating invoice:', { invoiceId, invoiceData });
  try {
      // Add validation for required fields
      if (!invoiceId) {
          throw new Error('Invoice ID is required');
      }

      // Log the update request
      console.log('[LOAMS] Update request payload:', {
          id: invoiceId,
          payeeDetails: {
              payee: invoiceData.payee,
              address: invoiceData.address,
              trn: invoiceData.trn
          },
          otherDetails: {
              agreement_ref_no: invoiceData.agreement_ref_no,
              project: invoiceData.project,
              // ... other fields
          }
      });

      const response = await pythonApi.put(
          `/api/invoices/${invoiceId}`,
          invoiceData,
          {
              headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
              },
              timeout: 30000  // 30 second timeout
          }
      );

      // Handle errors in response
      if (response.data.error) {
          throw new Error(response.data.error);
      }

      // Log success details
      console.log('[LOAMS] Invoice update successful:', {
          status: 'success',
          updatedInvoiceId: invoiceId,
          response: response.data
      });

      return response;
  } catch (error) {
      console.error('[LOAMS] Update invoice error:', {
          error: error.message,
          invoiceId,
          status: error.response?.status
      });

      // Enhanced error handling
      if (error.response?.status === 404) {
          throw new Error('Invoice not found');
      } else if (error.response?.status === 400) {
          throw new Error(error.response.data.error || 'Invalid invoice data');
      } else if (error.code === 'ECONNABORTED') {
          throw new Error('Request timed out. Please try again.');
      }

      throw error;
  }
},



    // In api.js
generateSoa: async (params = {}) => {
    console.log('[LOAMS] Generating SOA with params:', params);
    try {
        const queryParams = new URLSearchParams();
        
        // Handle "All Clusters" case
        if (params.cluster && params.cluster !== 'All Clusters') {
            queryParams.append('cluster', params.cluster);
        }
        
        // Add service type if not 'all'
        if (params.serviceType && params.serviceType !== 'all') {
            queryParams.append('service_type', params.serviceType);
        }

        const response = await pythonApi.get(
            `/api/soa/generate${queryParams.toString() ? `?${queryParams}` : ''}`,
            {
                headers: {
                    'Accept': 'application/json'
                },
                timeout: 30000
            }
        );

        if (!response.data) {
            throw new Error('No data received from server');
        }

        // Transform response if needed
        if (Array.isArray(response.data)) {
            return {
                data: {
                    success: true,
                    data: {
                        rows: response.data,
                        totals: response.data.reduce((acc, row) => {
                            acc.invoiced_amount = (acc.invoiced_amount || 0) + parseFloat(row.invoiced_amount || 0);
                            acc.payment_received = (acc.payment_received || 0) + parseFloat(row.payment_received || 0);
                            acc.balance_forwarded = (acc.balance_forwarded || 0) + parseFloat(row.balance_forwarded || 0);
                            acc.outstanding_balance = (acc.outstanding_balance || 0) + parseFloat(row.outstanding_balance || 0);
                            return acc;
                        }, {}),
                        total_count: response.data.length
                    }
                }
            };
        }

        return response;

    } catch (error) {
        console.error('[LOAMS] SOA generation error:', error);
        throw error;
    }
},

downloadSoa: async (data, fileName = 'statement_of_account.pdf') => {
    try {
        const response = await pythonApi.post('/api/soa/download', data, {
            responseType: 'blob',
            timeout: 60000 // Increased timeout for large PDFs
        });

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return { success: true };

    } catch (error) {
        console.error('[LOAMS] SOA download error:', error);
        throw error;
    }
},



login: async (credentials) => {
    try {
        // Regular login
        const response = await api.post('/api/auth/login', credentials);
        
        if (!response.data || !response.data.token) {
            throw new Error('Invalid response format from server');
        }

        const { token, user } = response.data;
        
        // Get avatar URL
        const avatarResponse = null

        // Store authentication data
        const finalToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        localStorage.setItem('token', finalToken);
        localStorage.setItem('user', JSON.stringify({
            ...user,
            displayName: user.username?.split('@')[0] || 'User',
            avatarUrl: avatarResponse.data.avatarUrl
        }));
        localStorage.setItem('isAuthenticated', 'true');
        
        // Set role-based access flags
        const role = user.role.toLowerCase();
        localStorage.setItem('hasAccountsAccess', 
            ['super_admin', 'admin', 'accounts'].includes(role) ? 'true' : 'false'
        );
        localStorage.setItem('hasLoamsAccess', 
            ['super_admin', 'admin', 'accounts', 'engineer', 'loams_viewer'].includes(role) ? 'true' : 'false'
        );
        localStorage.setItem('hasBoqAccess', 
            ['super_admin', 'admin', 'boq_admin'].includes(role) ? 'true' : 'false'
        );
        localStorage.setItem('isEngineer', role === 'engineer' ? 'true' : 'false');
        localStorage.setItem('isSuperAdmin', role === 'super_admin' ? 'true' : 'false');
        localStorage.setItem('isLoamsViewer', role === 'loams_viewer' ? 'true' : 'false');
        
        return response;
    } catch (error) {
        console.error('[AUTH] Login failed:', error);
        localStorage.clear();
        throw error;
    }
},


  


    









// get health of the system 

getHealth: async () => {
    try {
        console.log('[LOAMS] Checking service health');
        const response = await pythonApi.get('/health', {
            timeout: 60000,  // 60 second timeout
            retry: 2,        // Allow 2 retries
            retryDelay: 1000 // Wait 1 second between retries
        });

        if (!response.data) {
            throw new Error('No response data received');
        }

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        console.log('[LOAMS] Health check successful:', response.data);
        return response;
    } catch (error) {
        console.error('[LOAMS] Health check failed:', error);
        
        // Provide more specific error messages
        if (error.code === 'ECONNABORTED') {
            throw new Error('Service is starting up or not responding. Please try again in a moment.');
        } else if (error.response?.status === 500) {
            throw new Error('Service is experiencing internal issues. Please try again later.');
        }
        
        throw error;
    }
}
};

// Auth API endpoints
// Update just the authApi section in your api.js
export const authApi = {
    login: async (credentials) => {
        try {
            const response = await api.post('/api/auth/login', credentials);
            console.log('Login response:', response);
            
            if (!response.data || !response.data.token) {
                throw new Error('Invalid response format from server');
            }

            const { token, user } = response.data;
            
            // Store authentication data
            const finalToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
            localStorage.setItem('token', finalToken);
            localStorage.setItem('user', JSON.stringify({
                ...user,
                displayName: user.username?.split('@')[0] || 'User'
            }));
            localStorage.setItem('isAuthenticated', 'true');
            
            // Set role-based access flags
            const role = user.role.toLowerCase();
            localStorage.setItem('hasAccountsAccess', 
                ['super_admin', 'admin', 'accounts','loams_only'].includes(role) ? 'true' : 'false'
            );
            localStorage.setItem('hasLoamsAccess', 
                ['super_admin', 'admin', 'accounts', 'engineer', 'loams_viewer','loams_only'].includes(role) ? 'true' : 'false'
            );
            localStorage.setItem('hasBoqAccess', 
                ['super_admin', 'admin', 'boq_admin'].includes(role) ? 'true' : 'false'
            );
            localStorage.setItem('isEngineer', role === 'engineer' ? 'true' : 'false');
            localStorage.setItem('isSuperAdmin', role === 'super_admin' ? 'true' : 'false');
            localStorage.setItem('isLoamsViewer', role === 'loams_viewer' ? 'true' : 'false');
            
            return response;
        } catch (error) {
            console.error('[AUTH] Login failed:', error);
            localStorage.clear();
            throw error;
        }
    },
    
    logout: async () => {
        try {
            await api.post('/api/auth/logout');
        } finally {
            localStorage.clear();
            window.location.href = '/login';
        }
    }
};

// Client API endpoints
export const clientApi = {
    getClients: async () => {
        try {
            console.log('[CLIENT] Getting clients');
            const response = await api.get('/api/clients');
            console.log('[CLIENT] Got clients:', response.data.length);
            return response;
        } catch (error) {
            console.error('[CLIENT] Get clients error:', error);
            throw error;
        }
    },
    getClient: async (id) => {
        try {
            const response = await api.get(`/api/clients/${id}`);
            return response;
        } catch (error) {
            console.error('[CLIENT] Get client error:', error);
            throw error;
        }
    },
    createClient: (clientData) => api.post('/api/clients', clientData),
    updateClient: (id, clientData) => api.put(`/api/clients/${id}`, clientData),
    deleteClient: (id) => api.delete(`/api/clients/${id}`)
};

// BOQ API endpoints
export const boqApi = {
    getProjects: () => api.get('/api/boq/projects'),
    getProject: (id) => api.get(`/api/boq/projects/${id}`),
    createProject: (projectData) => api.post('/api/boq/projects', projectData),
    updateProject: (id, projectData) => api.put(`/api/boq/projects/${id}`, projectData),
    deleteProject: (id) => api.delete(`/api/boq/projects/${id}`),
    
    getProjectBills: (projectId) => api.get(`/api/boq/projects/${projectId}/bills`),
    createProjectBill: (projectId, billData) => api.post(`/api/boq/projects/${projectId}/bills`, billData),
    updateProjectBill: (billId, billData) => api.put(`/api/boq/bills/${billId}`, billData),
    deleteProjectBill: (billId) => api.delete(`/api/boq/bills/${billId}`)
};






// OCR API endpoints for DAMAC reference extraction
export const ocrApi = {
    // Get all documents from Portal Extract folder
    listDocuments: async (filters = {}) => {
      console.log('[OCR] Fetching documents with filters:', filters);
      try {
        // Build query parameters
        const queryParams = new URLSearchParams();
        
        if (filters.damacRef) {
          queryParams.append('damac_ref', filters.damacRef);
        }
        
        if (filters.project) {
          queryParams.append('project', filters.project);
        }
        
        const response = await pythonApi.get(`/api/ocr/documents${queryParams.toString() ? `?${queryParams}` : ''}`, {
          headers: {
            'Accept': 'application/json'
          },
          timeout: 30000 // 30-second timeout
        });
  
        if (!response.data) {
          throw new Error('Invalid response format from server');
        }
  
        console.log('[OCR] Found documents:', response.data.documents?.length || 0);
        return response.data;
      } catch (error) {
        console.error('[OCR] Error fetching documents:', error);
        throw error;
      }
    },
  
    // Process a single document to extract DAMAC reference
    processDocument: async (s3Key, options = {}) => {
      console.log('[OCR] Processing document:', s3Key);
      try {
        const response = await pythonApi.post('/api/ocr/process', { 
          s3_key: s3Key,
          project: options.project
        });
  
        return response.data;
      } catch (error) {
        console.error('[OCR] Error processing document:', error);
        throw error;
      }
    },
  
    // Process all unprocessed documents
    processAllDocuments: async () => {
      console.log('[OCR] Processing all documents');
      try {
        const response = await pythonApi.post('/api/ocr/process-all', null, {
          timeout: 60000 // 1-minute timeout for batch processing
        });
        return response.data;
      } catch (error) {
        console.error('[OCR] Error processing all documents:', error);
        throw error;
      }
    },
  
    // Get preview URL for document
    getPreviewUrl: async (s3Key) => {
      console.log('[OCR] Getting preview URL for:', s3Key);
      try {
        const encodedKey = encodeURIComponent(s3Key);
        const response = await pythonApi.get(`/api/ocr/preview/${encodedKey}`);
        
        if (!response.data?.url) {
          throw new Error('No preview URL received');
        }
  
        return response.data.url;
      } catch (error) {
        console.error('[OCR] Error getting preview URL:', error);
        throw error;
      }
    },
  
    // Download document
    downloadDocument: async (s3Key) => {
      console.log('[OCR] Downloading document:', s3Key);
      try {
        const encodedKey = encodeURIComponent(s3Key);
        const response = await pythonApi.get(`/api/ocr/download/${encodedKey}`, {
          responseType: 'blob'
        });
        
        // Extract filename from S3 key
        const filename = s3Key.split('/').pop() || 'document.pdf';
        
        // Create download
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true };
      } catch (error) {
        console.error('[OCR] Error downloading document:', error);
        throw error;
      }
    },
  
    // Upload new documents
    uploadDocuments: async (files, options = {}) => {
      console.log('[OCR] Uploading documents:', files.length);
      try {
        const formData = new FormData();
        
        // Add files to form data
        Array.from(files).forEach(file => {
          formData.append('files', file);
        });
        
        // Add options
        if (options.processImmediately !== undefined) {
          formData.append('process_immediately', options.processImmediately.toString());
        }
        
        if (options.project) {
          formData.append('project', options.project);
        }
        
        if (options.prefix) {
          formData.append('prefix', options.prefix);
        }
  
        const response = await pythonApi.post('/api/ocr/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 300000, // 5-minute timeout for large uploads
          onUploadProgress: options.onProgress
        });
  
        return response.data;
      } catch (error) {
        console.error('[OCR] Error uploading documents:', error);
        throw error;
      }
    },


    

    

  
    // Get processing status
    getProcessingStatus: async () => {
      try {
        const response = await pythonApi.get('/api/ocr/status', {
          timeout: 10000 // 10-second timeout
        });
        return response.data;
      } catch (error) {
        console.error('[OCR] Error getting processing status:', error);
        return { 
          success: false,
          total_documents: 0,
          processed_documents: 0,
          remaining_documents: 0
        };
      }
    },
  
    // Search for documents by DAMAC reference
    searchByDamacRef: async (damacRef) => {
      try {
        const response = await pythonApi.get('/api/ocr/search', {
          params: { damac_ref: damacRef }
        });
        return response.data;
      } catch (error) {
        console.error('[OCR] Error searching by DAMAC reference:', error);
        throw error;
      }
    },
  
    // Process multiple documents
    processBatch: async (s3Keys) => {
      console.log('[OCR] Processing batch of documents:', s3Keys.length);
      try {
        const results = [];
        
        for (const s3Key of s3Keys) {
          try {
            const response = await pythonApi.post('/api/ocr/process', { s3_key: s3Key });
            results.push({
              s3_key: s3Key,
              success: response.data?.success || false,
              damac_ref: response.data?.damac_ref || null,
              error: response.data?.error || null
            });
          } catch (error) {
            results.push({
              s3_key: s3Key,
              success: false,
              error: error.message || 'Processing failed'
            });
          }
        }
        
        return {
          success: true,
          results,
          successful: results.filter(r => r.success).length,
          failed: results.filter(r => !r.success).length
        };
      } catch (error) {
        console.error('[OCR] Error in batch processing:', error);
        throw error;
      }
    }
  };

  



  export const uploadWithInvoiceCreation = async (formData) => {
    try {
      console.log('[LOAMS] Enhanced upload with invoice creation');
      
      // Log FormData contents
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(`[LOAMS] FormData contains: ${key} File: ${value.name} (${value.size} bytes)`);
        } else {
          console.log(`[LOAMS] FormData contains: ${key} ${value}`);
        }
      }
      
      const response = await api.post('/api/certificate/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000 // 2 minutes for GPT-4 validation
      });
      
      console.log('[LOAMS] Enhanced upload response:', response.data);
      return response.data;
      
    } catch (error) {
      console.log('[LOAMS] Enhanced upload error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      // 🔥 Handle validation failure specifically
      if (error.response?.data?.validation_failed) {
        const details = error.response.data.validation_details || {};
        
        // Build user-friendly error message
        let userMessage = '❌ Certificate Validation Failed\n\n';
        userMessage += 'The uploaded document does not match the selected criteria:\n\n';
        
        const errors = details.errors || [];
        if (errors.length > 0) {
          errors.forEach((err, index) => {
            userMessage += `${index + 1}. ${err}\n`;
          });
        } else {
          // Build from matches
          if (!details.cluster_match) {
            userMessage += `• Cluster name does not match\n`;
            userMessage += `  Expected: ${formData.get('cluster')}\n`;
            userMessage += `  Found: ${details.extracted_values?.cluster || 'Unknown'}\n\n`;
          }
          if (!details.po_match) {
            userMessage += `• PO/Agreement number does not match\n`;
            userMessage += `  Expected: ${formData.get('agreement_ref')}\n`;
            userMessage += `  Found: ${details.extracted_values?.po || 'Unknown'}\n\n`;
          }
          if (!details.month_match) {
            userMessage += `• Month does not match\n`;
            userMessage += `  Expected: ${formData.get('month')}\n`;
            userMessage += `  Found: ${details.extracted_values?.month || 'Unknown'}\n\n`;
          }
        }
        
        userMessage += '\nPlease upload the correct certificate for the selected cluster, PO, and month.';
        
        // Throw with user-friendly message
        const validationError = new Error(userMessage);
        validationError.isValidationError = true;
        validationError.validationDetails = details;
        validationError.response = error.response;
        throw validationError;
      }
      
      // Handle other errors
      throw error;
    }
  };




  


  
// Add this to your api.js file

// PIN API functions
export const pinApi = {
    // Check if user has a PIN set up
    checkPin: async () => {
      try {
        const response = await pythonApi.get('/api/pin/check', {
          headers: {
            'Authorization': localStorage.getItem('token')
          }
        });
        return response;
      } catch (error) {
        console.error('[API] Error checking PIN:', error);
        throw error;
      }
    },
  
    // Set up a new PIN
    setupPin: async (pin) => {
      try {
        const response = await pythonApi.post('/api/pin/setup', 
          { pin },
          {
            headers: {
              'Authorization': localStorage.getItem('token'),
              'Content-Type': 'application/json'
            }
          }
        );
        return response;
      } catch (error) {
        console.error('[API] Error setting up PIN:', error);
        throw error;
      }
    },
  
    // Verify a PIN
    verifyPin: async (pin) => {
      try {
        const response = await pythonApi.post('/api/pin/verify', 
          { pin },
          {
            headers: {
              'Authorization': localStorage.getItem('token'),
              'Content-Type': 'application/json'
            }
          }
        );
        return response;
      } catch (error) {
        console.error('[API] Error verifying PIN:', error);
        throw error;
      }
    },
  
    // Change existing PIN
    changePin: async (currentPin, newPin) => {
      try {
        const response = await pythonApi.post('/api/pin/change', 
          { currentPin, newPin },
          {
            headers: {
              'Authorization': localStorage.getItem('token'),
              'Content-Type': 'application/json'
            }
          }
        );
        return response;
      } catch (error) {
        console.error('[API] Error changing PIN:', error)
        throw error;
      }
    }
  };

  


  



export { pythonApi, loamsEngineerApi };
export default api;